<template>
  <div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import En from '@/common/classes/En'
import UniData from '@/common/classes/UniData'
import { useActions } from '@/common/composables/useActions'
import {
  type ElementState,
  type OpenUrl,
  PageDataKey,
  PageDataSlotKey,
  PageRegistryFormIdKey,
  PageWindowInstanceIdKey
} from '@/common/composables/usePageData'
import { useScriptAutoHandler } from '@/common/composables/useScript'
import { mergePageFieldMaps as mergeFieldMaps, overlayAuthoritativeFieldKeys } from '@/common/utils/mergePageFieldMaps'
import {
  isUniDataTableEntry,
  normalizeUniDataTableEmptyFlag,
} from '@/common/utils/normalizeReloadTableEmptyFlag'
import { stripServerEventOverlayFields } from '@/common/utils/stripServerEventOverlayFields'
import { buildReloadFormRequestFields } from '@/common/utils/reloadFormRequestFields'
import { applyReloadPanelFieldsToChangedData, normalizeReloadPanelTableField } from '@/common/utils/applyReloadPanelFieldsToChangedData'
import {
  buildReloadPanelRequestFields,
} from '@/common/utils/buildReloadPanelRequestFields'
import {
  expandReloadPanelFieldKeys,
  scopeFieldMapToKeys,
  scopeReloadPanelResultFields,
} from '@/common/utils/reloadPanelResultScope'
import {
  buildSelectedPrimaryKeyUniData,
  buildSelectedPrimaryKeyWsResponse,
  resolveElementType,
  resolvePrimaryKeyColumnNames,
} from '@/common/utils/elementTableSwingMethods'
import {
  consumePendingOverlayVisibilityFromRecord,
  overlayPendingIdKey,
  setPendingOverlayVisibilityRecord
} from '@/common/utils/overlayPendingVisibility'
import {
  collectShownOverlayPageIds,
  findRegisteredElementPageId,
  isFormOverlayPageId,
  normalizeOriginOverlayPageId,
  overlayXmlIdFromPageId,
  resolveOverlayRefillTargets,
  shouldSkipUnscopedOverlayFieldCopy,
  shouldSyncPageFieldToElementPage,
  toFormOverlayPageId,
} from '@/common/utils/formOverlayPageScope'
import { overlayLayoutDuplicatedFieldNames } from '@/common/utils/validateFormElementNames'
import {
  collectLiveFieldOverrides,
  mergeLiveFieldOverridesIntoTypedFormData
} from '@/common/utils/liveFormFieldOverrides'
import {
  applySynthActionTableLeafCollect,
  resolveFormTableSelectionsForSynthCollect,
} from '@/common/utils/applyPutFormDataSynthCollect'
import { flushPendingEditableTableEditsFromRegistry } from '@/common/utils/editableTablePendingCommit'
import { retryEditableTableOnChangeIfNeeded } from '@/common/utils/editableTableOnChangeRetry'
import { setFormErrorForSlot } from '@/common/utils/formErrorStore'
import { isElementScopedFieldKey } from '@/common/utils/elementScopedUniData'
import { applyListboxWireTypesFromListboxFields } from '@/common/utils/comboBoxSwingFields'
import { normalizeElementCollectedDataForExport, applyDateRangeToClientDataFieldsFromRegistry, buildDateRangeWireFieldsFromUiValue } from '@/common/utils/elementGetDataExport'
import {
  pickPutFullDataScopedPageFields,
  prunePutFullDataCollectedFields,
  pruneTilesPutFullDataFields,
} from '@/common/utils/elementPutFullDataExport'
import {
  mergePageDataFieldSnapshot,
  resolveJsResultChangedDataFieldMap,
  sanitizeClientDataFieldMap,
} from '@/common/utils/resolveClientDataActualFields'
import {
  collectLayoutFieldNamesForPanels,
  collectPagePanelIds,
  filterLayoutScopedFieldsToPanels,
  resolveFormLevelClientFields,
  resolveLayoutScopedClientFields,
} from '@/common/utils/layoutScopedClientFields'
import {
  findRegisteredPanelChild,
  isIdentificatorElementType,
  readPanelGetDataElementHint,
  shouldIncludeElementInPanelGetData,
  collectFormIdentificatorGetDataFields,
  applyFormIdentificatorAndListboxGetDataToFields,
} from '@/common/utils/panelGetDataCollect'
import { mergeExchangeStatesForPageUpdate, normalizeExchangeStates } from '@/common/utils/normalizeExchangeStates'
import { mergePutDataCollectedFields } from '@/common/utils/mergePutDataCollectedFields'
import { applyOpenSuggesterFromStates } from '@/common/utils/applyOpenSuggesterFromStates'
import { resolveOpenSuggesterRegistryEntry } from '@/common/utils/resolveOpenSuggesterRegistryEntry'
import { stripLeakedFormRows } from '@/common/utils/coalesceEmptyUniDataTableField'
import {
  applyFileAttachmentsToClientDataFieldsFromRegistry,
  collectFileAttachmentFieldNamesFromForm,
  collectFileAttachmentFieldsFromFormsPanels,
  emptyFileAttachmentsUniDataTable,
  fileItemsToUniDataTable,
  isFileAttachmentFieldInForm,
  pickPreferredFileAttachmentsField,
  pruneAttachmentElementRpcFields,
  uniDataTableToFileItems,
} from '@/common/utils/fileAttachmentsUniData'
import { resolvePositiveElementId } from '@/common/utils/attachmentNativeFileRegistry'
import { AttachmentUploadError } from '@/common/utils/attachmentUpload'
import { resolveFileAttachmentsPutFullDataElement } from '@/services/action/collectFileAttachmentsPutFullData'
import {
  clearAttachmentPutFullDataWireTable,
  stashAttachmentPutFullDataWireTable,
} from '@/common/utils/attachmentPutFullDataWireBuffer'
import {
  collectFormActionsFromPageData,
  fileAttachmentsPutFullDataShouldThrowPending,
  fileAttachmentsTableHasPendingUploadWithoutServerCode,
  parseFileAttachmentsElementMeta,
  shouldUseInlineAttachmentGetData,
} from '@/common/utils/attachmentRpcPolicy'
import {
  type AttachmentStoreMutationCause,
  markAttachmentStoreMutationCause
} from '@/common/utils/attachmentStoreMutationCause'
import { fieldEntryHasPresentableValue, fieldEntryLooksEmptyIncoming } from '@/common/utils/changedDataFieldMerge'
import { isTextLikeFormElementType } from '@/common/utils/liveFormFieldOverrides'
import {
  pickPresentableTextGetData,
  readFormsElementSlotField,
} from '@/common/utils/textLikeSlotGetData'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import {
  collectRegisteredElementsByName,
  resolveRegisteredElementByName,
} from '@/common/utils/resolveRegisteredElementByName'
import { bindClientImportElementArgs } from '@/common/utils/resolveClientImportElementArg'
import {
  combineRootOnLoadScript,
  readRootFormFunctions,
  resolveWebClientRootOnLoadScriptAfterReload,
  isSameSchemaStackReload,
  buildRootOnLoadRunTriggerFromPage,
  reserveRootOnLoadGlobally,
} from '@/common/utils/formRootOnLoad'
import {
  rememberDeferredActionVisibility,
  rememberDeferredPanelVisibility,
  takeDeferredActionVisibility,
  takeDeferredPanelVisibility,
} from '@/common/utils/deferredFormVisibility'
import {
  createPanelChildMembershipIndex,
  panelIdsEqual,
  rememberPanelChildMembership,
  syncPanelChildrenShellVisibility,
} from '@/common/utils/panelChildShellMembership'
import {
  replacePanelVisibilityRegistryList,
  removePanelVisibilityEntries,
  upsertPanelVisibilityEntries,
} from '@/common/utils/panelVisibilityRegistry'
import {
  buildDialogCloseConfigForStore,
  flushDialogClosePendingFieldOnChanges,
  inferDialogCloseSourceTableFieldsFromScript,
  resolveDialogCloseSourceFieldOverlay
} from '@/common/utils/dialogCloseParentMergeScope'
import {
  buildDialogClosedNReturnCloseOverrides,
  resolveOnCloseDialogWebClientExec,
  shouldExecuteElementOnChangeOnClient,
} from '@/services/action/actionScriptRuntime'
import { shouldDropStaleDialogOpenAfterClose } from '@/common/utils/dialogPageSlot'
import {
  isolateAllSharedTableFieldValues,
  isolateSharedTableFieldAliases,
  normalizeDialogPageUniDataFieldAliases
} from '@/common/utils/cloneTableValue'
import {
  resolveDialogCloseParentContext,
  resolveParentSlotAtDialogOpen
} from '@/common/utils/resolveDialogCloseParentStoreKey'
import { getPageIdentityAliases, resolvePageIdentity } from '@/common/utils/resolvePageIdentity'
import { pageSlotElementRefillMatch } from '@/common/utils/pageSlotElementRefillMatch'
import { buildPageDataCommitSignature } from '@/common/utils/pageDataCommitSignature'
import { t } from '@/common/utils/translations'
import {
  findPageStoreEntryForForm,
  resolvePagesDataStoreKey,
  resolveResponseSchemaFormId,
  resolveStateUpdateStoreKey
} from '@/common/utils/resolvePageStoreEntry'
import { resolveFormsStorageFormIdForContext as resolveFormsStorageFormIdForContextUtil } from '@/common/utils/resolveFormsStorageFormId'
import { resolveRegisteredElementForReload as resolveRegisteredElementForReloadFromPool } from '@/common/utils/resolveRegisteredElementForReload'
import { resolveInvokeEventSelectedData } from '@/common/utils/resolveInvokeEventSelectedData'
import { pickElementEventScript, pickElementEventScriptNode } from '@/common/utils/scriptNodeText'
import { getWebClientExecFromScriptNode } from '@/common/utils/webClientExec'
/** WUI-385: резолв en.setVisible по platform id / field name (паритет Swing JSContext). */
import {
  findRegisteredElementByElementId,
  findRegisteredElementByName,
  findRegisteredElementsByName,
  normalizeRegistryLevel,
  parseNumericId,
  resolveVisibilityTarget
} from '@/common/utils/resolveRegistryVisibility'
import { findLayoutPageTabByVisibilityName } from '@/common/utils/pageTabMeta'
import { resolveNameBasedElementVisibilityTarget } from '@/common/utils/resolveNameBasedElementVisibility'
import { omitLeakedTableColumnFieldsFromElementSlot } from '@/common/utils/omitTableColumnFieldsFromElementSlot'
import { flattenFormStorageMap } from '@/common/utils/flattenFormStorageMap'
import {
  collectTilesCloneWidgetFieldNames,
  listPanelFormsStorageKeys,
  mergeFormsPanelBucketsForGetForm,
  overlayTilesCloneWidgetFieldsFromRow,
  overlayTilesRowDataOnFormFields,
  pruneTilesCloneFormsBuckets,
  readTilesRowScopeFromCtx,
  resolveTilesPanelFormsStorageKey,
  resolvePanelInstanceKeyToFormsBucket,
  shouldSkipTilesCloneScalarRefill,
  stripTilesRowFieldPrefix,
  type SwingTilesRowScopeLike,
} from '@/common/utils/tilesRowPanelData'
import {
  overlayTilesInlineAfterLoadRowFields,
} from '@/common/utils/tilesInlineAfterLoad'
import {
  isTilesRowScopedRegistryName,
  listPersistentScopedKeysBeyondRowCount,
  omitPersistentScopedKeys,
  type TilesRowActionVisibilityMap,
} from '@/common/utils/tilesSetInlineVisibility'
import {
  collectUniDataTableFieldNamesFromReloadFields,
  collectUniDataTableRowCountsFromReloadFields,
  isUniDataTableFieldShape,
} from '@/common/utils/omitEmptyUniDataTableFieldsFromReloadPayload'
import { mergeEventListenersForPageUpdate } from '@/common/utils/eventListeners'
import { applyIncomingExchangeStates, commitExchangeResult } from '@/services/action/commitExchangeResult'
import {
  MAIN_FRAME_SLOT_ID,
  buildMainFramePageDataFromActionResponse,
} from '@/services/mainFrame/mainFrameMount'
import { assignPageFieldToRegistrySlot, resolvePageFieldEntry } from '@/common/utils/pageFieldRegistryWrite'
import { editableTableRegistryRicherThanPage } from '@/common/utils/editableTableLiveCollect'
import { resolveTreeTableFieldNameFromPageFields } from '@/common/utils/treeTableSearch'
import { shouldApplyPageFocusGuardForReload } from '@/common/utils/reloadElementPageFocusGuard'
import { TypeSerializer } from '@/common/utils/typeSerializer.ts'
import { normalizeFormElementType } from '@/common/utils/formElementType'
import { parsePageFieldToDate } from '@/common/utils/parsePageFieldToDate'
import { IKey } from '@/common/constants/ikey'
import { setType, setTypes } from '@/common/utils/typedValueSerializer'
import {
  toMultiSelectTypedValue,
  tryPromoteStringWrappedUniDataTable
} from '@/common/utils/normalizeMultiselectTableValue'
import {
  parseComboBoxIdFieldPairsFromElement,
} from '@/common/utils/comboBoxSwingFields'
import { eventActionNameCandidates } from '@/components/platform/events/event-action-name-candidates'
import { isActionXmlVerifyEnabled } from '@/common/utils/actionXmlVerify'
import { verifyElements, verifyFormElements, resolveActionLayoutVerifyScope } from '@/common/utils/verifyHelper'
import type { ActionHooks, FormContext } from '@/components/platform/types'
import { coreService, createSafeClientData } from '@/services/core/core.service'
import apiService from '@/services/api.service'
import { getActionDialogClosedScript } from '@/services/action/action.service'
import { confirmPlatformAction } from '@/services/action/confirmPlatformAction'
import { localPropertiesService } from '@/services/local-properties/local-properties.service'
import { createClientImportRuntime } from '@/services/script/client-import-runtime'
import { getActiveExecuteScriptCurrentData } from '@/services/script/script.service'
import type { JsRequest } from '@/services/websocket/types'
import { useAppStore } from '@/stores/app'
import { useConfigStore } from '@/stores/config'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import { useHotKeys } from '@/common/composables/useHotKeys'
import { usePageReloadGuard } from '@/common/composables/usePageReloadGuard'
import { skipNextPageLeaveConfirm } from '@/common/utils/pageLeaveGuardBridge'
import {
  hasOpenPlatformForms,
  resolveOpenTabIdsForReloadGuard,
  shouldConfirmPageReload,
} from '@/common/utils/pageReloadGuard'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, provide, ref, toRaw, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import { initOfficeEventRouter } from '@/services/office/officeEventRouter'
import { initFormEventRouter } from '@/services/form-events/formEventRouter'
import {
  createListenersApi,
  eventRestrictions,
  serverEventRegistry
} from '@/services/server-events/server-events.service'
import {
  applyDispatchedVisibilities,
  normalizeDispatchedVisibilities,
  type SetVisibleByLevelOptions,
} from '@/services/action/applyDispatchedVisibilities'
import { createOnServerEventUserShim } from '@/services/server-events/onServerEventUserShim'
import { normalizeOnServerEventClientScript } from '@/common/utils/normalizeOnServerEventClientScript'
import {
  collectOnServerEventScriptEntries,
  resolvePanelIdByJsName,
} from '@/common/utils/collectOnServerEventScripts'
import { useUserStore } from '@/stores/user'
import { createDetachedSignature, createHash } from 'crypto-pro'
import { useFileSelectModal } from '@/stores/useFileSelectModal'
import { useSignModal } from '@/stores/useSignModal'
import { profilePayload, startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { isFormPageBootstrapActive } from '@/common/utils/formPageBootstrap'
import { formHasRenderableLayout } from '@/services/parse/parse.service'
import {
  isTreeTableElementType,
  parseTreeTableRuntimeColumnsFromPage,
  rebuildTreeTableGetDataForWs
} from '@/common/utils/treeTableGetData'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import {
  getJsRequestSessionId,
  normalizePlatformSessionId,
  runWithJsRequestSessionId,
  withAttachmentUploadSessionOverride
} from '@/common/utils/jsRequestSessionContext'
import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import { isPlatformCoreNullError } from '@/common/utils/isPlatformCoreNullError'
import { resolveReloadPanelSessionId } from '@/common/utils/resolveReloadPanelSessionId'
import { resolveReloadFormStackContext } from '@/common/utils/resolveReloadFormStackContext'
import { createPlatformActionParams } from '@/common/utils/requestSanitizer'
import { dispatchSameWindowReplace } from '@/common/utils/createWindowSlotId'
import { isPlatformTestApiEnabled } from '@/common/utils/platformTestApiEnabled'
import { installPlatformTestApi, uninstallPlatformTestApi } from '@/common/utils/installPlatformTestApi'
import {
  buildClientElementVisibilityPatches,
  buildClientPageVisibilityPatches,
  persistVisibilityStatePatchesToPageStore,
  recordVisibilityStatePatch,
  recordSetEditableByLevelPatch,
  recordSetEditableNamePatch,
  resolveVisibilityPersistSlotId,
  visibilityPatchesFromDispatched,
  type VisibilityStatePatch,
} from '@/common/utils/visibilityStatePatches'
import {
  applyRegisteredElementEditableFromStates,
  applyRegisteredElementVisibilityFromStates,
  replayStoredPageVisibilityStates,
} from '@/common/utils/replayStoredPageVisibilityStates'
import {
  buildShellCleanupFormIdSet,
  clearFormShellVisibilityRuntime,
  collectShellVisibilityCleanupTargets,
  forgetIndexedShellElementIds,
  panelsVisibilityRegistryAfterShellCleanup,
  pruneRecordByElementIds,
  pruneRecordByKeys,
  pruneVisibilityHooksByElementIds,
  registerFormShellVisibilityCleanup,
  rememberShellElementIdForSlot,
  type ShellVisibilityRegistryEntry,
} from '@/common/utils/formShellVisibilityCleanup'

import {
  EnKey,
  FormProviderKey,
  GetFormKey,
  GetPageDataKey,
  OnActionKey,
  OnElementKey,
  OpenUrls,
  RegisterActionKey,
  RegisterElementKey,
  RegisterFormKey,
  RegisterOverlayKey,
  RegisterPanelKey,
  ReloadElementKey,
  RunActionByNameKey,
  RunActionKey,
  RunCodeKey,
  RunEventKey,
  SetValueKey,
  SkipNextPageLeaveConfirmKey,
  UnRegisterActionKey,
  UnRegisterElementKey,
  UnRegisterFormKey,
  UnRegisterOverlayKey,
  UnRegisterPanelKey
} from './injection-keys'
import { normalizePageTabId, useFormStates } from '@/common/composables'
import UniDataTable from '@/common/classes/UniDataTable.ts'
import { assignClientImportWindowGlobals } from '@/services/script/runtime-window-globals'
import {
  throwInvalidJsRequestPayload,
  throwRuntimeJsRequestError,
  throwUnknownJsRequestHandler
} from '@/common/exceptions'
import { createBaseUniDataResponse, ensureUniDataWireFields } from '@/common/utils/uniDataWsResponse'

// Helper function to recursively filter out complex objects
const filterComplexObjects = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle primitive types
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => filterComplexObjects(item));
  }

  // Handle objects
  if (typeof obj === 'object') {
    const complexIndicators = [
      'rows', 'columns', 'fields', 'attributes', 'empty',
      'elementConditions', 'reloadActions', 'params', 'doActionId', 'openPage',
      '_attributes', '_text', '_typeId', '_id', '_versionId', '_actual', '_deleted',
      'ProcessInstances', 'exception', 'clientData', 'formError', 'closedOutdistances'
    ];

    // Check if object has any complex indicators
    for (const indicator of complexIndicators) {
      if (indicator in obj) {
        return null; // Skip this complex object
      }
    }

    if ('type' in obj && 'value' in obj) {
      const objType = obj.type;
      const objValue = obj.value;

      // Allow simple type/value structures
      if (typeof objType === 'string' && typeof objValue !== 'object') {
        // {type: "String", value: "text"}
        return obj;
      } else if (objValue && typeof objValue === 'object') {
        // Check if the value is complex
        if (objValue.rows || objValue.columns || objValue.fields || objValue.attributes) {
          return null; // Skip complex value
        }
        return obj;
      }
      return obj;
    }

    // Check for nested complex structures
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === 'object') {
        // If any nested value is complex, skip the entire object
        if (Array.isArray(value) && value.length > 0) {
          // Check if array contains complex objects
          const hasComplexItems = value.some(item =>
              item && typeof item === 'object' && (
                'fields' in item || 'attributes' in item || 'rows' in item
              )
          );
          if (hasComplexItems) {
            return null;
          }
        } else if (typeof value === 'object') {
          // Check if nested object is complex
          if ('fields' in value || 'attributes' in value || 'rows' in value) {
            return null;
          }
        }
      }
    }

    // If we get here, it's a simple object - recursively filter its properties
    const filtered: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const filteredValue = filterComplexObjects(value);
      if (filteredValue !== null) {
        filtered[key] = filteredValue;
      }
    }
    return filtered;
  }

  return obj;
};

const getElementKey = (formId: string | number, panelId: string | number, elementId: string | number, name: string) =>
  `${formId}::${panelId}::${elementId}::${name}`;

/**
 * Гарантирует наличие bucket в `forms` для конкретного элемента.
 *
 * Для обычных панелей bucket = `panelId`.
 * Для строк Tiles bucket = `panelId~<tableField>__row<index>`, чтобы данные строк не смешивались.
 *
 * @returns фактический ключ bucket панели в `forms[storageFormId]`
 */
const ensureFormsElementBucket = (
  storageFormId: string,
  panelId: string | number,
  elementId: string | number,
  ctx?: unknown,
): string => {
  if (!forms.value[storageFormId]) {
    forms.value[storageFormId] = {}
  }
  const bucket = resolveTilesPanelFormsStorageKey(panelId, readTilesRowScopeFromCtx(ctx))
  if (!forms.value[storageFormId][bucket]) {
    forms.value[storageFormId][bucket] = {}
  }
  if (!forms.value[storageFormId][bucket][elementId]) {
    forms.value[storageFormId][bucket][elementId] = {}
  }
  return bucket
}

const isMultiSelectElement = (element: any) =>
  normalizeFormElementType(element) === 'MULTISELECT'

// Create minimal clientData with only primitive values
const createMinimalClientData = (data: any): any => {
  const minimalData: any = {};

  if (!data || typeof data !== 'object') {
    return minimalData;
  }

  for (const [key, value] of Object.entries(data)) {
    // Skip any field that contains complex data structures
    if (key.includes('__listbox') || key.includes('__action') || key.includes('__page') ||
      key === 'speedLimits' || key === 'closedOutdistances' || key === 'fields' ||
      key === 'ProcessInstances' || key === 'exception' || key === 'clientData') {
      continue; // Skip these complex fields entirely
    }

    // Skip complex objects
    if (value && typeof value === 'object') {
      // {type: "String", value: "text"}
      if ('type' in value && 'value' in value && typeof value.type === 'string') {
        const fieldValue = value.value;

        // Only include primitive values
        if (fieldValue === null || fieldValue === undefined) {
          minimalData[key] = fieldValue;
        } else if (typeof fieldValue === 'string' || typeof fieldValue === 'number' || typeof fieldValue === 'boolean') {
          minimalData[key] = fieldValue;
        }
        // Skip any complex field values
      }
      // Skip all other complex objects
    } else if (value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      // Include primitive values directly
      minimalData[key] = value;
    }
  }

  return minimalData;
};

interface ElementRegistration {
  name: string;
  registryName?: string;
  formId: string;
  panelId: string;
  elementId: string;
  element: any;
  type?: string;
  value?: any;
  hooks?: any;
  ctx?: any;
  doNotSerialize?: boolean;
}

interface FormsRegistry {
  [formId: string]: {
    [panelId: string]: {
      [elementId: string]: any;
    };
  };
}

interface ElementsRegistry {
  [name: string]: any;
}

interface ActionsRegistry {
  [id: string]: any;
}

function fieldEntryEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const aHasValue = Object.prototype.hasOwnProperty.call(a, 'value');
  const bHasValue = Object.prototype.hasOwnProperty.call(b, 'value');
  if (!aHasValue || !bHasValue) return false;
  if (a.type !== b.type) return false;
  const aValue = a.value;
  const bValue = b.value;
  if (aValue === bValue) return true;
  if (
    aValue &&
    bValue &&
    typeof aValue === 'object' &&
    typeof bValue === 'object' &&
    Array.isArray(aValue.rows) &&
    Array.isArray(bValue.rows)
  ) {
    if (aValue.rows === bValue.rows) return true;
  }
  return stableFieldEntrySignature(a) === stableFieldEntrySignature(b);
}

function stableFieldEntrySignature(value: unknown): string {
  const seen = new WeakSet<object>();
  const normalize = (input: unknown): unknown => {
    if (input == null || typeof input !== 'object') {
      if (typeof input === 'function') return '[function]';
      return input;
    }
    if (seen.has(input as object)) return '[Circular]';
    seen.add(input as object);
    if (Array.isArray(input)) {
      return input.map(normalize);
    }
    const record = input as Record<string, unknown>;
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalize(record[key]);
        return acc;
      }, {});
  };
  try {
    return JSON.stringify(normalize(value));
  } catch {
    return String(value);
  }
}

function shallowRecordEqual(a?: Record<string, any>, b?: Record<string, any>): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => fieldEntryEqual(a[key], b[key]));
}

function scopedFieldMergeIsNoop(
  oldFields: Record<string, any> | undefined,
  mergedFields: Record<string, any>,
  incomingFields: Record<string, any>,
  omitKeys?: string[],
): boolean {
  const oldMap = oldFields || {}
  const changedKeys = new Set<string>()
  for (const key of Object.keys(incomingFields || {})) {
    changedKeys.add(key)
  }
  for (const key of omitKeys || []) {
    const normalized = String(key ?? '').trim()
    if (normalized) changedKeys.add(normalized)
  }
  if (changedKeys.size === 0) return true

  for (const key of changedKeys) {
    const oldHasKey = Object.prototype.hasOwnProperty.call(oldMap, key)
    const newHasKey = Object.prototype.hasOwnProperty.call(mergedFields, key)
    if (oldHasKey !== newHasKey) return false
    if (!fieldEntryEqual(oldMap[key], mergedFields[key])) return false
  }
  return true
}

// Stores
const appStore = useAppStore()
const documentTabsStore = useDocumentTabsStore()
const userStore = useUserStore()
const { currentPlatform } = storeToRefs(appStore);
const configStore = useConfigStore();
const route = useRoute();
const router = useRouter();

// State
const forms = ref<FormsRegistry>({});

/** Хранилище выбора таблиц с чекбоксами: ключ = formId\tpanelId\telementId, значение = { fieldName, rows } */
const tableSelectionByKey = ref<Map<string, { fieldName: string; rows: any[] }>>(new Map());

function tableSelectionKey(formId: string | number, panelId: string | number | undefined, elementId: string | number | undefined): string {
  return `${formId ?? ''}\t${panelId ?? ''}\t${elementId ?? ''}`;
}

function registerTableSelection(
  formId: string | number,
  panelId: string | number | undefined,
  elementId: string | number | undefined,
  fieldName: string,
  selectedRows: any[]
) {
  const key = tableSelectionKey(formId, panelId, elementId);
  const next = new Map(tableSelectionByKey.value);
  next.set(key, { fieldName, rows: selectedRows ?? [] });
  tableSelectionByKey.value = next;
}

function getTableSelection(
  formId: string | number | undefined,
  panelId: string | number | undefined,
  elementId: string | number | undefined
): { fieldName: string; rows: any[] } | null {
  if (formId == null || elementId == null) {
    return null;
  }
  const key = tableSelectionKey(formId, panelId, elementId);
  let result = tableSelectionByKey.value.get(key) ?? null;
  if (!result) {
    const formKey = String(formId ?? '')
    const elementKey = String(elementId ?? '')
    for (const [storedKey, storedValue] of tableSelectionByKey.value.entries()) {
      const [storedFormId, , storedElementId] = storedKey.split('\t')
      if (storedFormId === formKey && storedElementId === elementKey) {
        result = storedValue
        break
      }
    }
  }
  return result;
}

function getTableSelections(
  formId: string | number | undefined,
  panelId?: string | number | undefined
): Array<{ fieldName: string; rows: any[] }> {
  if (formId == null) {
    return [];
  }

  const formKey = String(formId ?? '');
  const panelKey = panelId == null ? null : String(panelId);
  const result: Array<{ fieldName: string; rows: any[] }> = [];

  for (const [storedKey, storedValue] of tableSelectionByKey.value.entries()) {
    const [storedFormId, storedPanelId] = storedKey.split('\t');
    if (storedFormId !== formKey) continue;
    if (panelKey !== null && storedPanelId !== panelKey) continue;
    result.push(storedValue);
  }

  return result;
}

type ElementTableSwingMethod = 'getSelectedPrimaryKey' | 'getPrimaryKeyColumns' | 'getType'

function resolveRegisteredElementForTableSwing(
  entity: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const name = String(entity.fieldName ?? entity.name ?? '').trim()
  if (name && elements.value[name]) {
    return elements.value[name] as Record<string, unknown>
  }
  const elementId = entity.elementId
  if (elementId != null) {
    const found = Object.values(elements.value).find(
      (e: any) =>
        Number(e?.elementId) === Number(elementId) || Number(e?.ctx?.id) === Number(elementId),
    )
    if (found) return found as Record<string, unknown>
  }
  return entity
}

function resolveElementTableSwingMethod(
  entity: Record<string, unknown>,
  method: ElementTableSwingMethod,
): unknown {
  const registered = resolveRegisteredElementForTableSwing(entity) ?? entity
  const rawColumns = (registered as { columns?: { column?: unknown } })?.columns?.column

  switch (method) {
    case 'getType':
      return resolveElementType(
        (registered as { elementType?: unknown }).elementType ?? entity.elementType,
      )
    case 'getPrimaryKeyColumns':
      return resolvePrimaryKeyColumnNames(rawColumns)
    case 'getSelectedPrimaryKey': {
      if (!rawColumns) return new UniData({})
      const reg = registered as {
        formId?: string | number
        panelId?: string | number
        elementId?: string | number
      }
      const selection = getTableSelection(reg.formId, reg.panelId, reg.elementId)
      return buildSelectedPrimaryKeyUniData({
        rawColumns,
        selectionRow: selection?.rows?.[0] ?? null,
      })
    }
    default:
      return undefined
  }
}

const elements = ref<ElementsRegistry>({});

function scrollToElement(name: string) {
  const element = elements.value[name]
  if (element && typeof element.scrollIntoViewElement === 'function') {
    element.scrollIntoViewElement()
  }
}

/** UI shell (editable / enabled / visible) до mount — replay в registerElement. WUI-371, WUI-385. */
type DeferredElementShell = { editable?: boolean; enabled?: boolean; visible?: boolean }
/** Буфер по registry field name (`websiteUrlTable`). */
const deferredElementShellByName = ref<Record<string, DeferredElementShell>>({})
/** Буфер по platform XML id (`39115`) — для `en.setVisible("element", id, …)` до registerElement. */
const deferredElementShellByElementId = ref<Record<string, DeferredElementShell>>({})
/** Прямой hook setVisible по elementId — fallback, если resolveVisibilityTarget не нашёл запись. */
const elementVisibilityHooksByElementId = new Map<string, (visible: boolean) => void>()
/** Общая видимость shell по XML element id — все mount Element.vue читают одно значение (WUI-386). */
const elementShellVisibleByElementId = ref<Record<string, boolean>>({})
/** WUI-408: видимость scoped tiles action/element (`table__rowN__name`) — переживает remount после reload. */
const persistentScopedElementVisibility = ref<Record<string, boolean>>({})
/** WUI-408: row-local map setInlineVisibility — переживает remount MTiles при putFullData в inlineAfterLoad. */
const tilesRowActionVisibilityStore = ref<TilesRowActionVisibilityMap>({})
/** После reload/panel с заменой tiles-таблиц — MTiles перезапускает inlineAfterLoad. */
const tilesInlineAfterLoadEpoch = ref(0)
/** Имена UniDataTable из последнего reload/panel (чтобы не дёргать чужие tiles). */
const tilesInlineAfterLoadReloadTables = ref<readonly string[]>([])

/**
 * Не сбрасываем sticky map заранее: иначе rowReady=false → мигание всех кнопок.
 * Только точечный re-run inlineAfterLoad для затронутых table field names;
 * setInlineVisibility перезапишет edit/view по новому statusId.
 */
function notifyTilesInlineAfterLoadAfterTableReload(
  reloadFields: Record<string, unknown>,
): void {
  const tableFieldNames = collectUniDataTableFieldNamesFromReloadFields(reloadFields)
  if (tableFieldNames.length === 0) return
  tilesInlineAfterLoadReloadTables.value = tableFieldNames
  tilesInlineAfterLoadEpoch.value += 1
}

function pruneTilesCloneStateAfterTableReload(
  reloadFields: Record<string, unknown>,
  formId: string | number,
): void {
  const tableFieldNames = collectUniDataTableFieldNamesFromReloadFields(reloadFields)
  if (tableFieldNames.length === 0) return
  const tableSet = new Set(tableFieldNames)
  // Row-map не обнуляем: пустой map → hide всех target до script (пропадает chrome).
  // Epoch перезапишет setInlineVisibility. Persist чистим только у исчезнувших строк.
  const omitKeys = listPersistentScopedKeysBeyondRowCount(
    Object.keys(persistentScopedElementVisibility.value),
    collectUniDataTableRowCountsFromReloadFields(reloadFields),
  )
  if (omitKeys.length > 0) {
    persistentScopedElementVisibility.value = omitPersistentScopedKeys(
      persistentScopedElementVisibility.value,
      omitKeys,
    )
  }
  const storageIds = new Set(
    [resolveFormsStorageFormId(formId), String(formId ?? '').trim()].filter(Boolean),
  )
  for (const fid of storageIds) {
    const panels = forms.value[fid]
    if (!panels || typeof panels !== 'object') continue
    pruneTilesCloneFormsBuckets(panels as Record<string, unknown>, tableSet)
  }
}

function scopedDeferredNameKey(name: string, scopeFormId?: string | number | null): string {
  const base = String(name ?? '').trim()
  if (!base) return ''
  const scope = String(scopeFormId ?? '').trim()
  return scope ? `${scope}::${base}` : base
}

function rememberPersistentScopedVisibility(name: string, visible: boolean): void {
  const key = String(name ?? '').trim()
  if (!key || !isTilesRowScopedRegistryName(key)) return
  persistentScopedElementVisibility.value = {
    ...persistentScopedElementVisibility.value,
    [key]: visible,
  }
}

function applyPersistentScopedVisibility(registryKey: string): void {
  const key = String(registryKey ?? '').trim()
  if (!key) return
  const visible = persistentScopedElementVisibility.value[key]
  if (visible === undefined) return
  const target = resolveDeferredVisibilityTarget(key)
  if (target?.setVisible) {
    target.setVisible(visible)
  }
}

function getPersistedScopedVisibility(name: string): boolean | undefined {
  const key = String(name ?? '').trim()
  if (!key) return undefined
  return persistentScopedElementVisibility.value[key]
}

// Защита панелей и элементов от скрытия если FileAttachments имеют файлы
const panelsWithFiles = ref<Set<string>>(new Set())
const elementsWithFiles = ref<Set<string>>(new Set())

function setElementShellVisibleById(elementId: string | number, visible: boolean): void {
  const key = String(elementId ?? '').trim()
  if (!key) return

  // Блокировать скрытие панели или элемента если они содержат файлы
  if (!visible && (panelsWithFiles.value.has(key) || elementsWithFiles.value.has(key))) {
    return
  }

  rememberShellElementIdForSlot(getCurrentPageSlotId(), key)
  elementShellVisibleByElementId.value = {
    ...elementShellVisibleByElementId.value,
    [key]: visible,
  }
}

/**
 * Применить shell-состояние к уже зарегистрированному элементу (Label `l2#39131`, field name `l2`, id `39131`).
 * Возвращает true, если patch применён — тогда defer не нужен.
 */
function tryApplyShellToRegisteredElement(
  patch: DeferredElementShell,
  opts: { registryKey?: string; fieldName?: string; elementId?: string | number },
): boolean {
  const registryKey = String(opts.registryKey ?? '').trim()
  if (registryKey) {
    const direct = elements.value[registryKey]
    if (direct) {
      applyDeferredElementShellPatch(direct, patch)
      return true
    }
    const directAction = actions.value[registryKey]
    if (directAction) {
      applyDeferredElementShellPatch(directAction, patch)
      return true
    }
  }

  const fieldName = String(opts.fieldName ?? '').trim()
  if (fieldName) {
    const byName = findRegisteredElementByName(elements.value, fieldName)
    if (byName?.entry) {
      applyDeferredElementShellPatch(byName.entry, patch)
      return true
    }
  }

  if (opts.elementId != null && opts.elementId !== '') {
    const byId = findRegisteredElementByElementId(elements.value, opts.elementId)
    if (byId?.entry) {
      applyDeferredElementShellPatch(byId.entry, patch)
      return true
    }
  }

  return false
}

/** Отложить shell по field name (states, setEditable по имени). */
function rememberDeferredElementShell(
  name: string,
  patch: DeferredElementShell,
  scopeFormId?: string | number | null,
): void {
  const key = scopedDeferredNameKey(name, scopeFormId)
  if (!key) return
  if (tryApplyShellToRegisteredElement(patch, { registryKey: key, fieldName: key })) {
    return
  }
  deferredElementShellByName.value = {
    ...deferredElementShellByName.value,
    [key]: { ...deferredElementShellByName.value[key], ...patch },
  }
}

/** Отложить shell по XML element id (скрипты: en.setVisible("element", 39115, …)). */
function rememberDeferredElementShellById(elementId: string | number, patch: DeferredElementShell): void {
  const key = String(elementId ?? '').trim()
  if (!key) return
  if (patch.visible !== undefined) {
    setElementShellVisibleById(key, patch.visible)
  }
  if (tryApplyShellToRegisteredElement(patch, { elementId })) {
    return
  }
  deferredElementShellByElementId.value = {
    ...deferredElementShellByElementId.value,
    [key]: { ...deferredElementShellByElementId.value[key], ...patch },
  }
}

function applyDeferredElementShellPatch(
  element: { setEditable?: (v: boolean) => void; setVisible?: (v: boolean) => void; setEnable?: (v: boolean) => void },
  patch: DeferredElementShell | undefined,
): void {
  if (!patch) return
  if (patch.editable !== undefined && element.setEditable) {
    element.setEditable(patch.editable)
  }
  if (patch.enabled !== undefined && typeof (element as { setEnable?: (v: boolean) => void }).setEnable === 'function') {
    ;(element as { setEnable: (v: boolean) => void }).setEnable(patch.enabled)
  }
  if (patch.visible !== undefined && element.setVisible) {
    element.setVisible(patch.visible)
  }
}

/** Применить буфер shell (в т.ч. visible) после registerElement. */
function resolveDeferredVisibilityTarget(registryKey: string): {
  setVisible?: (visible: boolean) => void
  fieldName?: unknown
  formId?: unknown
} | null {
  const key = String(registryKey ?? '').trim()
  if (!key) return null
  const element = elements.value[key]
  if (element) return element
  const action = actions.value[key]
  if (action) return action
  return null
}

function applyDeferredElementShell(registryKey: string, elementId?: string | number): void {
  const key = String(registryKey ?? '').trim()
  if (!key) return
  const target = resolveDeferredVisibilityTarget(key)
  if (!target) return

  const targetFormId = String(target.formId ?? '').trim()
  const deferredNameKeys = new Set<string>([
    key,
    scopedDeferredNameKey(key, targetFormId),
  ])
  const fieldName = String(target.fieldName ?? '').trim()
  if (fieldName) {
    deferredNameKeys.add(fieldName)
    deferredNameKeys.add(scopedDeferredNameKey(fieldName, targetFormId))
  }

  for (const deferredKey of deferredNameKeys) {
    const byName = deferredElementShellByName.value[deferredKey]
    if (!byName) continue
    applyDeferredElementShellPatch(target, byName)
    const nextByName = { ...deferredElementShellByName.value }
    delete nextByName[deferredKey]
    deferredElementShellByName.value = nextByName
    break
  }

  const idKey = elementId != null ? String(elementId).trim() : ''
  const byId = idKey ? deferredElementShellByElementId.value[idKey] : undefined
  if (byId) {
    applyDeferredElementShellPatch(target, byId)
    const nextById = { ...deferredElementShellByElementId.value }
    delete nextById[idKey]
    deferredElementShellByElementId.value = nextById
  }
}

function applyDeferredActionVisibility(actionId: string | number): void {
  const key = String(actionId).trim()
  if (!key) return
  const visible = takeDeferredActionVisibility(actionId)
  if (visible === undefined) return
  const entry = actions.value[key] as { setVisible?: (v: boolean) => void } | undefined
  if (typeof entry?.setVisible === 'function') {
    entry.setVisible(visible)
  }
}

function applyDeferredPanelVisibility(panelId: string, setVisible: (visible: boolean) => void): void {
  const key = String(panelId).trim()
  if (!key) return
  const visible = takeDeferredPanelVisibility(key)
  if (visible === undefined) return
  setVisible(visible)
}

const elementsRegistryRevision = ref(0);
const bumpElementsRegistryRevision = () => {
  elementsRegistryRevision.value += 1;
};
const elementsByKey = ref<ElementsRegistry>({});
const overlaysRegistry = ref<Record<string, {
  name: string
  overlayId: string
  formId: string
  isDialog?: boolean
  setVisible: (visible: boolean) => void
  isVisible: () => boolean
}>>({})
const panelsVisibilityRegistry = ref<Record<string, Array<{
  formId: string
  panelId: string
  instanceKey: string
  runtimeId?: string
  setVisible: (visible: boolean) => void
}>>>({})
/** Survives Panel v-if unmount so panel setVisible can revive child shells. */
const panelChildMembershipIndex = createPanelChildMembershipIndex()
const panelVisibilityRegistryKey = (formId: string, panelId: string) => `${formId}:${panelId}`
const defaultPanelInstanceKey = 'default'
const pendingOverlayVisibility = ref<Record<string, boolean>>({});
const pendingOverlayRefills: Array<{
  fieldName: string
  fieldData: unknown
  formId?: string
  boundToShown?: boolean
}> = []
const lastShownOverlayPageIds = ref<string[]>([])

const setPendingOverlayVisibility = (
  name: string | undefined,
  overlayId: string | undefined,
  visible: boolean,
) => {
  setPendingOverlayVisibilityRecord(pendingOverlayVisibility.value, name, overlayId, visible)
}

const consumePendingOverlayVisibility = (
  name: string,
  overlayId: string,
): boolean | undefined => {
  return consumePendingOverlayVisibilityFromRecord(
    pendingOverlayVisibility.value,
    name,
    overlayId,
  )
}

const pendingOverlayRefillFieldNames = () => pendingOverlayRefills.map((item) => item.fieldName)

const overlayDuplicatedFieldNamesForForm = (formId?: string | number): Set<string> => {
  const pageData = formId != null && String(formId).trim()
    ? findPageDataByFormId(formId)
    : findPageDataByFormId(appStore.currentPageId)
  return overlayLayoutDuplicatedFieldNames(pageData?.form)
}

const overlayIsDialogForPageId = (pageId: unknown): boolean | undefined => {
  const xmlId = overlayXmlIdFromPageId(pageId)
  if (!xmlId) return undefined
  for (const overlay of Object.values(overlaysRegistry.value)) {
    if (String(overlay.overlayId ?? '') === xmlId) return overlay.isDialog === true
  }
  return undefined
}

const queuePendingOverlayRefill = (payload: {
  fieldName: string
  fieldData: unknown
  formId?: string
}) => {
  const fieldName = String(payload.fieldName ?? '').trim()
  if (!fieldName) return
  pendingOverlayRefills.push({
    fieldName,
    fieldData: payload.fieldData,
    formId: payload.formId,
  })
}

const flushPendingOverlayRefills = (shownOverlayPageIds?: Iterable<string>) => {
  if (pendingOverlayRefills.length === 0) return
  const shown = shownOverlayPageIds
    ? [...shownOverlayPageIds]
    : [...lastShownOverlayPageIds.value]
  if (shownOverlayPageIds) {
    lastShownOverlayPageIds.value = shown
  }

  const registered = Object.values(elementsByKey.value) as Array<{
    fieldName?: unknown
    pageId?: unknown
    formId?: string
    refill?: (data?: unknown) => void
  }>

  const stillPending: typeof pendingOverlayRefills = []
  for (const item of pendingOverlayRefills) {
    const candidates = registered
      .filter((el) => String(el.fieldName ?? '').trim() === item.fieldName)
      .map((el) => ({
        ...el,
        isDialog: overlayIsDialogForPageId(el.pageId),
      }))
    const targets = resolveOverlayRefillTargets(candidates, shown)
    if (shown.length === 0 && item.boundToShown) {
      stillPending.push(item)
      continue
    }
    if (shown.length > 0 && targets.length === 0) {
      stillPending.push(item)
      continue
    }
    for (const target of targets) {
      refillElementViaControls(
        item.fieldName,
        item.fieldData,
        item.formId ?? String(target.formId ?? ''),
        target,
      )
    }
  }
  pendingOverlayRefills.length = 0
  pendingOverlayRefills.push(...stillPending)
  if (pendingOverlayRefills.length === 0) {
    lastShownOverlayPageIds.value = []
  }
}

const rememberShownOverlayPageIdsFromVisibilities = (raw: unknown) => {
  const visibilities = normalizeDispatchedVisibilities(raw)
  const knownIds = Object.values(overlaysRegistry.value).map((overlay) => overlay.overlayId)
  const shown = collectShownOverlayPageIds(visibilities, knownIds)
  if (shown.length > 0) {
    lastShownOverlayPageIds.value = shown
    for (const item of pendingOverlayRefills) {
      item.boundToShown = true
    }
  }
}
const actions = ref<ActionsRegistry>({});
const tempData = ref<Record<string, any>>({});
const en = ref<En>();
const syncTimeouts = ref<Record<string | number, ReturnType<typeof setTimeout>>>({});
const serverEventScripts = new Map<string, string>()

const stableServerEventSignature = (value: unknown): string => {
  const seen = new WeakSet<object>()

  const normalize = (input: unknown): unknown => {
    if (input == null || typeof input !== 'object') {
      return input
    }
    if (seen.has(input)) {
      return '[Circular]'
    }
    seen.add(input)
    if (Array.isArray(input)) {
      return input.map(normalize)
    }
    const record = input as Record<string, unknown>
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalize(record[key])
        return acc
      }, {})
  }

  try {
    return JSON.stringify(normalize(value)) ?? ''
  } catch {
    return String(value)
  }
}

const serverEventContextSignature = computed(() =>
  [
    userStore.profileLoaded ? 'profile:loaded' : 'profile:pending',
    stableServerEventSignature(userStore.platformProfile?.userFields ?? {}),
  ].join('|'),
)

const buildServerEventScriptCacheKey = (script: string): string =>
  `${serverEventContextSignature.value}\n${script}`

const toActionData = (data?: any) => {
  const selectedData = data?.selectedData ?? data?.selected ?? data
  if (data && typeof data === 'object') {
    return { ...data, selected: selectedData }
  }
  return { selected: selectedData, data }
}

const wsService = getWebSocketService()
const unsubscribeServerEvents = wsService.onEvent((event) => {
  serverEventRegistry.handleEvent(event)
})

const normalizeOnServerEventScript = (script: string): string => {
  // Raw meta (listeners.add(user, and([eventIn, present($("…"))]), listener)) → eventIn-only.
  let normalized = normalizeOnServerEventClientScript(script)
  normalized = normalized.replace(/\blistener\s*\(/g, 'function(')
  // Важно! Вызов reloadPanel/reloadForm из клиента должен быть синхронным! await
  normalized = normalized.replace(/\ben\.reloadPanel\s*\(/g, 'await en.reloadPanel(')
  normalized = normalized.replace(/\ben\.reloadForm\s*\(\s*\)/g, 'await en.reloadForm()')
  // panel.reload(en.getData()) из client onServerEvent (форма 2771 / panel 620)
  normalized = normalized.replace(
    /\b(?:await\s+)?([A-Za-z_$][\w$]*)\.reload\s*\(/g,
    (match, name: string) => (name === 'en' ? match : `await ${name}.reload(`),
  )
  return normalized
}

const registerOnServerEvent = async (
  formId: string,
  pageData: any,
  pageSlotId?: string,
) => {
  if (!en.value) return

  const pageKey = String(pageSlotId ?? formId)
  const listenerKey = pageKey

  // Swing FormBuilder.addServerEvent: form + каждая панель отдельно.
  const entries = collectOnServerEventScriptEntries(pageData)
  const rawScript = entries.map((entry) => entry.script).join('\n')

  if (!rawScript) {
    if (serverEventScripts.has(listenerKey)) {
      serverEventRegistry.clearForm(listenerKey)
      serverEventScripts.delete(listenerKey)
    }
    return
  }

  const normalizedScript = entries
    .map((entry) => normalizeOnServerEventScript(entry.script))
    .join('\n')
  const cacheKey = buildServerEventScriptCacheKey(normalizedScript)
  if (serverEventScripts.get(listenerKey) === cacheKey) {
    return
  }

  serverEventRegistry.clearForm(listenerKey)
  serverEventScripts.set(listenerKey, cacheKey)

  const previousListeners = (window as any).listeners
  const previousRestrictions = (window as any).eventRestrictions
  const previousUser = (window as any).user

  const pageSessionId = readPlatformSessionIdFromPageEntry(pageData)
  const listenerOptions = {
    sessionId: pageSessionId,
    onAfterListener: (enInstance: any) => {
      applyDispatchedVisibilities(enInstance?.getProperties?.()?.dispatchedVisibilities, {
        formProvider: {
          setVisible: setVisibleRecursive,
          setVisibleByLevel,
          getOverlays: () => overlaysRegistry.value,
        },
        formKeys: [formId, pageKey],
      })
      rememberShownOverlayPageIdsFromVisibilities(
        enInstance?.getProperties?.()?.dispatchedVisibilities,
      )
      flushPendingOverlayRefills()
    },
  }

  try {
    ; (window as any).eventRestrictions = eventRestrictions
    ; (window as any).user = createOnServerEventUserShim(userStore.platformProfile)

    for (const entry of entries) {
      const panelId = entry.panelId
      ; (window as any).listeners = createListenersApi(
        listenerKey,
        () => {
          syncEnContextForScript({ formId, pageSlotId: pageKey, panelId })
          return en.value!
        },
        {
          ...listenerOptions,
          onBeforeListener: async () => {
            await awaitPendingElementEvent()
            syncEnContextForScript({ formId, pageSlotId: pageKey, panelId })
          },
        },
      )

      en.value!._setFormId(formId)
      en.value!._setPanelId(panelId)
      syncEnContextForScript({ formId, pageSlotId: pageKey, panelId })
      await en.value!.run(normalizeOnServerEventScript(entry.script))
    }
  } catch (error) {
    console.error('[onServerEvent] Error executing script:', error)
  } finally {
    if (previousListeners === undefined) {
      delete (window as any).listeners
    } else {
      ; (window as any).listeners = previousListeners
    }
    if (previousRestrictions === undefined) {
      delete (window as any).eventRestrictions
    } else {
      ; (window as any).eventRestrictions = previousRestrictions
    }
    if (previousUser === undefined) {
      delete (window as any).user
    } else {
      ; (window as any).user = previousUser
    }
  }
}

// Computed
const rawMenu = computed(() => configStore.rawMenu);

const flattenObj = (obj: any, level: number) => flattenFormStorageMap(obj, level)

/** Значение из поля формы */
const unwrapTypedSlotValue = (v: any): any => {
  if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v && 'type' in v) {
    return v.value
  }
  return v
}

/** DD.MM.YYYY для getData по DATERANGE */
const formatDateRangePartForGetData = (d: unknown): string => {
  if (d == null || d === '') return ''
  if (d instanceof Date) {
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}.${month}.${year}`
  }
  if (typeof d === 'string') {
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) return d
    const parsed = new Date(d)
    if (!Number.isNaN(parsed.getTime())) {
      return formatDateRangePartForGetData(parsed)
    }
    return d
  }
  return ''
}

/** MCheckbox: boolean (binary) или { columns, rows, empty } */
const isCheckboxLikeSlotModel = (plain: unknown): boolean => {
  if (plain === null || plain === undefined) return false
  if (typeof plain === 'boolean') return true
  if (typeof plain === 'object' && !Array.isArray(plain) && Array.isArray((plain as { rows?: unknown }).rows)) {
    return true
  }
  return false
}

const checkboxRowHasJacksonTypedFields = (row: unknown): boolean => {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return false
  const f = (row as { fields?: unknown }).fields
  if (!f || typeof f !== 'object' || Array.isArray(f)) return false
  const entries = Object.values(f as Record<string, unknown>)
  if (entries.length === 0) return false
  return entries.every(
    (cell) =>
      cell &&
      typeof cell === 'object' &&
      !Array.isArray(cell) &&
      typeof (cell as { type?: unknown }).type === 'string' &&
      'value' in (cell as object),
  )
}

const checkboxCellToTyped = (val: unknown, preferBoolean: boolean): { type: string; value: unknown } => {
  if (preferBoolean && typeof val === 'boolean') {
    return { type: 'Boolean', value: val }
  }
  if (val === null || val === undefined) {
    return { type: 'String', value: '' }
  }
  if (typeof val === 'boolean') {
    return { type: 'Boolean', value: val }
  }
  if (typeof val === 'number' && Number.isFinite(val)) {
    return { type: Number.isInteger(val) ? 'Long' : 'Double', value: val }
  }
  return { type: 'String', value: String(val) }
}

/** Строка MCheckbox  */
const normalizeCheckboxUniDataTableRowForWire = (row: unknown): { fields: Record<string, { type: string; value: unknown }> } => {
  if (checkboxRowHasJacksonTypedFields(row)) {
    return row as { fields: Record<string, { type: string; value: unknown }> }
  }
  const r = row as Record<string, unknown>
  if (r?.fields && typeof r.fields === 'object' && !Array.isArray(r.fields)) {
    const out: Record<string, { type: string; value: unknown }> = {}
    for (const [k, cell] of Object.entries(r.fields as Record<string, unknown>)) {
      if (
        cell &&
        typeof cell === 'object' &&
        !Array.isArray(cell) &&
        typeof (cell as { type?: unknown }).type === 'string' &&
        'value' in (cell as object)
      ) {
        out[k] = cell as { type: string; value: unknown }
      } else {
        out[k] = checkboxCellToTyped(cell, k === 'value')
      }
    }
    return { fields: out }
  }
  return {
    fields: {
      id: checkboxCellToTyped(r?.id ?? '', false),
      description: checkboxCellToTyped(r?.description ?? '', false),
      value: checkboxCellToTyped(r?.value, true),
    },
  }
}

const normalizeCheckboxUniDataTableForWire = (table: unknown): unknown => {
  if (table == null || typeof table !== 'object' || Array.isArray(table)) return table
  const t = table as { rows?: unknown[]; columns?: unknown }
  if (!Array.isArray(t.rows) || t.rows.length === 0) return table
  return {
    ...t,
    columns:
      Array.isArray(t.columns) && t.columns.length > 0
        ? t.columns
        : ['id', 'description', 'value'],
    rows: t.rows.map((row) => normalizeCheckboxUniDataTableRowForWire(row)),
  }
}

const getCheckboxItemsFromRegisteredElement = (reg: any): Array<{ id: string; label: string }> => {
  const rawItems = reg?.items?.item ?? reg?.items ?? []
  const items = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : []
  return items
    .map((item: any) => ({
      id: String(item?.id?._text || item?.value?._text || ''),
      label: String(
        item?.description?._text || item?.name?._text || item?.value?._text || item?.id?._text || '',
      ),
    }))
    .filter((item) => item.id && item.label)
}

const checkboxRowHasNonEmptyId = (row: unknown): boolean => {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return false
  const r = row as Record<string, any>
  const id =
    r?.fields?.id?.value ??
    r?.fields?.id ??
    r?.id?.value ??
    r?.id
  return id != null && String(id).trim() !== ''
}

/** Таблица чекбокса  */
const isUsableCheckboxTableModel = (plain: unknown, expectedItemCount: number): boolean => {
  if (plain == null || typeof plain !== 'object' || Array.isArray(plain)) return false
  const rows = (plain as { rows?: unknown }).rows
  if (!Array.isArray(rows) || rows.length === 0) return expectedItemCount === 0
  if (expectedItemCount > 0 && rows.length !== expectedItemCount) return false
  return rows.some((row) => checkboxRowHasNonEmptyId(row))
}

const buildDefaultCheckboxWireTableFromItems = (items: Array<{ id: string; label: string }>) => ({
  columns: ['id', 'description', 'value'],
  rows: items.map((opt) => ({
    fields: {
      id: checkboxCellToTyped(opt.id, false),
      description: checkboxCellToTyped(opt.label, false),
      value: checkboxCellToTyped(false, true),
    },
  })),
  empty: items.length === 0,
})

const parseDaterangePlainForGetData = (raw: unknown): { from: unknown; to: unknown } => {
  const plain = unwrapTypedSlotValue(raw)
  if (plain === '' || plain == null) {
    return {from: null, to: null}
  }
  if (typeof plain === 'object' && !Array.isArray(plain) && plain !== null) {
    if ('from' in plain || 'to' in plain) {
      return {
        from: (plain as { from?: unknown }).from ?? null,
        to: (plain as { to?: unknown }).to ?? null,
      }
    }
  }
  return {from: null, to: null}
}

const listboxCatalogRowLooksLike = (x: unknown): boolean => {
  if (x == null || typeof x !== 'object' || Array.isArray(x)) return false
  const r = x as Record<string, unknown>
  if (r.fields && typeof r.fields === 'object' && !Array.isArray(r.fields)) {
    const f = r.fields as Record<string, unknown>
    const pick = (k: string) => {
      const cell = f[k]
      if (cell && typeof cell === 'object' && !Array.isArray(cell) && 'value' in cell) {
        return (cell as { value: unknown }).value
      }
      return cell
    }
    const code = pick('code') ?? pick('_id')
    const title = pick('name') ?? pick('description')
    return (code != null && String(code) !== '') || (title != null && String(title) !== '')
  }
  return (
    ('code' in r || '_id' in r || 'usersHaveAccess' in r) &&
    ('name' in r || 'description' in r)
  )
}

/** Снимок списка пунктов listbox */
const isLikelyListboxFullItemsArray = (v: unknown): boolean => {
  if (!Array.isArray(v) || v.length < 2) return false
  return v.slice(0, 3).every(listboxCatalogRowLooksLike)
}

const isLikelyListboxItemsStringDump = (v: unknown): boolean => {
  if (typeof v !== 'string') return false
  const t = v.trim()
  if (t.length < 60 || !t.includes('=>')) return false
  const hasKey = t.includes("'code'") || t.includes('"code"') || t.includes('_id')
  const hasTitle = t.includes("'name'") || t.includes("'description'") || t.includes('"name"')
  return hasKey && hasTitle
}

const isLikelyListboxTablePayload = (v: unknown): boolean => {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return false
  const rows = (v as { rows?: unknown }).rows
  if (!Array.isArray(rows) || rows.length < 2) return false
  return rows.slice(0, 3).every(listboxCatalogRowLooksLike)
}

const unwrapListboxComboSelectedValueForGetData = (raw: unknown): unknown => {
  const v = unwrapTypedSlotValue(raw)
  if (v == null) return ''
  if (isLikelyListboxItemsStringDump(v) || isLikelyListboxFullItemsArray(v) || isLikelyListboxTablePayload(v)) {
    return ''
  }
  if (typeof v === 'object' && !Array.isArray(v) && v !== null && 'value' in v) {
    return unwrapListboxComboSelectedValueForGetData((v as { value: unknown }).value)
  }
  if (typeof v === 'object' && !Array.isArray(v) && v !== null) {
    const o = v as Record<string, unknown>
    if (o.code != null || o._id != null) {
      return o.code ?? o._id ?? ''
    }
  }
  return v
}

// Registration methods
const registerAction = (action: any, hooks: ActionHooks) => {
  const idKey = action?.id != null ? String(action.id) : ''
  const registryNameKey = String(action?.registryName ?? '').trim()

  if (idKey) {
    actions.value[idKey] = action;
    Object.assign(actions.value[idKey], hooks);
    applyDeferredActionVisibility(action.id)
  }

  if (registryNameKey) {
    actions.value[registryNameKey] = action;
    Object.assign(actions.value[registryNameKey], hooks);
    applyDeferredElementShell(registryNameKey)
    applyPersistentScopedVisibility(registryNameKey)
  }
  // Apply any pending states for this action when it registers
  const formId = action.formId?.toString()
  if (formId && formProvider?.applyPendingActionStates) {
    formProvider.applyPendingActionStates(action.id, action.name, formId)
  }
};

const unRegisterAction = (action: any) => {
  const idKey = action?.id != null ? String(action.id) : ''
  const registryNameKey = String(action?.registryName ?? '').trim()

  if (idKey) {
    delete actions.value[idKey];
  }
  if (registryNameKey) {
    delete actions.value[registryNameKey];
  }
};

const registerForm = (formId: string) => {
  forms.value[formId] = forms.value[formId] || {};
};

const unRegisterForm = (formId: string) => {
  if (!forms.value[formId]) return;

  // Check if form is still in breadcrumb history - if yes, don't remove elements
  // This allows forms to be cached and restored without losing element registrations
  const currentPageId = appStore.currentPageId;
  const pageData = findPageDataByFormId(formId) || appStore.pagesData[currentPageId];

  // Don't remove elements if form is still accessible (in breadcrumb history or current page)
  if (pageData && (formId === currentPageId || pageData.form)) {
    // Still remove forms registry to allow re-initialization, but keep elements
    delete forms.value[formId];
    return;
  }

  // Remove all elements of this form only if it's truly closed
  let removedAny = false;
  Object.keys(elements.value).forEach(name => {
    if (elements.value[name].formId === formId) {
      delete elements.value[name];
      removedAny = true;
    }
  });
  Object.keys(elementsByKey.value).forEach(name => {
    if (elementsByKey.value[name].formId === formId) {
      delete elementsByKey.value[name];
    }
  });
  if (removedAny) {
    bumpElementsRegistryRevision();
  }

  Object.keys(overlaysRegistry.value).forEach(name => {
    if (overlaysRegistry.value[name].formId === formId) {
      delete overlaysRegistry.value[name];
    }
  });

  delete forms.value[formId];
};

const registerPanel = ({
  formId,
  panelId,
  setVisible,
  instanceKey,
  runtimeId,
}: {
  formId: string
  panelId: string
  instanceKey?: string
  runtimeId?: string
  setVisible?: (visible: boolean) => void
}) => {
  if (!forms.value[formId]) {
    forms.value[formId] = {};
  }
  forms.value[formId][panelId] = forms.value[formId][panelId] || {};
  if (!setVisible) return

  const registryKey = panelVisibilityRegistryKey(formId, panelId)
  const ik = String(instanceKey ?? defaultPanelInstanceKey)
  const prev = panelsVisibilityRegistry.value[registryKey] ?? []
  const next = upsertPanelVisibilityEntries(prev, {
    formId,
    panelId,
    instanceKey: ik,
    runtimeId,
    setVisible,
  })
  panelsVisibilityRegistry.value = replacePanelVisibilityRegistryList(
    panelsVisibilityRegistry.value,
    registryKey,
    next,
  )
  applyDeferredPanelVisibility(panelId, setVisible)
};

/**
 * Удаляет элемент из registry/forms при unmount.
 *
 * Для кэшируемых/живых форм выполняется мягкое удаление только данных элемента,
 * для реально закрытой формы — полная очистка записи.
 */
const unRegisterPanel = ({
  formId,
  panelId,
  instanceKey,
  runtimeId,
}: {
  formId: string
  panelId: string
  instanceKey?: string
  runtimeId?: string
}) => {
  const tileBucket = resolvePanelInstanceKeyToFormsBucket(panelId, instanceKey, defaultPanelInstanceKey)
  if (tileBucket && forms.value[formId]?.[tileBucket]) {
    delete forms.value[formId][tileBucket]
  } else if (forms.value[formId]?.[panelId]) {
    delete forms.value[formId][panelId]
  }
  const registryKey = panelVisibilityRegistryKey(formId, panelId)
  const ik = String(instanceKey ?? defaultPanelInstanceKey)
  const prev = panelsVisibilityRegistry.value[registryKey] ?? []
  const next = removePanelVisibilityEntries(prev, { runtimeId, instanceKey: ik })
  panelsVisibilityRegistry.value = replacePanelVisibilityRegistryList(
    panelsVisibilityRegistry.value,
    registryKey,
    next,
  )
};

const registerOverlay = ({
  name,
  overlayId,
  formId,
  setVisible,
  isVisible,
  isDialog,
}: {
  name: string
  overlayId: string
  formId: string
  setVisible: (visible: boolean) => void
  isVisible: () => boolean
  isDialog?: boolean
}) => {
  if (!name) return
  overlaysRegistry.value[name] = { name, overlayId, formId, setVisible, isVisible, isDialog }
  const pending = consumePendingOverlayVisibility(name, overlayId)
  if (pending !== undefined) {
    setVisible(pending)
  }
}

const unregisterOverlay = ({ name, formId }: { name: string; formId: string }) => {
  const entry = overlaysRegistry.value[name]
  if (entry && entry.formId === formId) {
    delete overlaysRegistry.value[name]
  }
}

const registerElement = ({
                           name,
                           registryName,
                           formId,
                           panelId,
                           elementId,
                           element,
                           type,
                           value,
                           hooks,
                           ctx,
                           doNotSerialize = false
                         }: ElementRegistration) => {
  if (!name) return;

  const registryKey = registryName ?? name;

  // Validate required parameters
  if (!formId || !panelId || !elementId) {
    console.warn(`[registerElement] Missing required parameters: formId=${formId}, panelId=${panelId}, elementId=${elementId}`);
    return;
  }

  // Remove previous element with same registry key
  delete elements.value[registryKey];

  // Ensure element is not null/undefined
  if (!element) {
    console.warn(`[registerElement] Element is null/undefined for name: ${name}`);
    return;
  }

  elements.value[registryKey] = { ...element };
  elements.value[registryKey].panelId = panelId;
  elements.value[registryKey].formId = formId;
  elements.value[registryKey].elementId = elementId;
  rememberPanelChildMembership(panelChildMembershipIndex, panelId, elementId)
  elements.value[registryKey].pageId = (ctx as any)?.formPath?.pageId ?? (ctx as any)?.pageId;
  elements.value[registryKey].pageDataSlot = String(
    (ctx as any)?.pageDataSlot ?? (ctx as any)?.formPath?.pageDataSlot ?? '',
  ).trim();
  elements.value[registryKey].fieldName = name;
  elements.value[registryKey].registeredValue = value;
  elements.value[registryKey].ctx = ctx;
  elements.value[registryKey].doNotSerialize = doNotSerialize;
  Object.assign(elements.value[registryKey], hooks);
  // Labels use registryName `name#id` — alias by plain name for en.setVisible("element", name, …).
  if (name && name !== registryKey) {
    elements.value[name] = elements.value[registryKey]
  }
  const pageForElement = findPageDataByFormId(formId)
  const regSessionId = readPlatformSessionIdFromPageEntry(pageForElement)
  if (regSessionId) {
    elements.value[registryKey].platformSessionId = regSessionId
  }
  elements.value[registryKey].elementType = type ?? normalizeFormElementType(element);
  elements.value[registryKey].getType = () => {
    const registeredElement = elements.value[registryKey]
    return registeredElement?.elementType ?? normalizeFormElementType(registeredElement)
  }

  const regEl = elements.value[registryKey]
  const elTypeNorm = regEl.elementType
  if (elTypeNorm === 'IDENTIFICATOR' && typeof regEl.getData !== 'function') {
    regEl.getData = function (this: any) {
      const fieldName = this.fieldName || name
      const fid = this.formId ?? formId
      const pid = this.panelId ?? panelId
      const eid = this.elementId ?? elementId
      const storageFormId = resolveFormsStorageFormId(fid)
      const formsSlot = forms.value[storageFormId]?.[pid]?.[eid]?.[fieldName]
      if (formsSlot !== undefined) {
        return { [fieldName]: formsSlot }
      }
      const pageSlot = String(this.pageDataSlot ?? this.ctx?.pageDataSlot ?? '').trim()
      const pageData =
        (pageSlot ? (appStore.pagesData as Record<string, any>)[pageSlot] : undefined) ||
        findPageDataByFormId(fid) ||
        (getCurrentPageSlotId() ? appStore.pagesData[getCurrentPageSlotId()] : null)
      const pageField = pageData
        ? (mergePageDataFieldSnapshot(pageData) as Record<string, unknown>)[fieldName]
        : undefined
      if (pageField !== undefined) {
        return { [fieldName]: pageField }
      }
      return {}
    }
  }

  if (isTextLikeFormElementType(elTypeNorm)) {
    const innerGetData = typeof regEl.getData === 'function' ? regEl.getData : null
    regEl.getData = function (this: any) {
      const fieldName = this.fieldName || name
      const fid = this.formId ?? formId
      const pid = this.panelId ?? panelId
      const eid = this.elementId ?? elementId
      const pageSlot = String(this.pageDataSlot ?? this.ctx?.pageDataSlot ?? '').trim()
      const hookData = typeof innerGetData === 'function' ? innerGetData.call(this) : undefined
      const storageFormId = resolveFormsStorageFormIdForContext(fid, pageSlot)
      const bucket = resolveTilesPanelFormsStorageKey(
        pid,
        readTilesRowScopeFromCtx(this.ctx) ?? null,
      )
      const slotFromContext = readFormsElementSlotField(
        forms.value as Record<string, unknown>,
        storageFormId,
        bucket,
        eid,
        fieldName,
      )
      const slotFromFormId = readFormsElementSlotField(
        forms.value as Record<string, unknown>,
        resolveFormsStorageFormId(fid),
        String(pid ?? ''),
        eid,
        fieldName,
      )
      const formsSlot = fieldEntryLooksEmptyIncoming(slotFromContext) ? slotFromFormId : slotFromContext
      const pageData =
        (pageSlot ? (appStore.pagesData as Record<string, any>)[pageSlot] : undefined) ||
        findPageDataByFormId(fid) ||
        (getCurrentPageSlotId() ? appStore.pagesData[getCurrentPageSlotId()] : null)
      const pageField = pageData
        ? (mergePageDataFieldSnapshot(pageData) as Record<string, unknown>)[fieldName]
        : undefined
      return pickPresentableTextGetData(fieldName, hookData, formsSlot, pageField)
    }
  }

  if (
    (elTypeNorm === 'TABLE' || elTypeNorm === 'EDITABLE' || elTypeNorm === 'TREETABLE') &&
    typeof regEl.getPaginatorParams !== 'function'
  ) {
    regEl.getPaginatorParams = function (this: any) {
      const tableName: string = this.fieldName || name
      const fid = this.formId
      const pd = appStore.pagesData[fid]
      const params = (pd?.uniData?.clientData?.params || {}) as Record<string, any>
      const pageFields = (pd ? mergePageDataFieldSnapshot(pd) : {}) as Record<string, any>
      const page = Number(params[`${tableName}__page`]) || 1
      const maxOnPage = Number(params[`${tableName}__maxOnPage`]) || 50
      const sizeFromParams = params[`${tableName}__size`]
      const sizeField = pageFields[`${tableName}__size`]
      const sizeFromFields =
        sizeField && typeof sizeField === 'object' && 'value' in sizeField
          ? sizeField.value
          : sizeField
      const tableField = pageFields[tableName]
      const rows = tableField?.value?.rows ?? tableField?.rows
      const rowsCount = Array.isArray(rows) ? rows.length : 0
      const totalSize = Number(sizeFromParams ?? sizeFromFields) || rowsCount
      return {
        [`${tableName}__page`]: setType(String(page)),
        [`${tableName}__maxOnPage`]: setType(String(maxOnPage)),
        [`${tableName}__size`]: setType(String(totalSize)),
      }
    }
  }

  if (typeof elements.value[registryKey].invokeEvent !== 'function') {
    elements.value[registryKey].invokeEvent = (rawEventName: string, data?: any) => {
      if (!rawEventName) return;

      const eventName = rawEventName === 'inlineMenuShow' ? 'onInlineMenuShow' : rawEventName;
      const selectedData = resolveInvokeEventSelectedData(data);

      en.value?._setActionData(toActionData(data));

      const registeredElement = elements.value[registryKey];
      const eventScript = registeredElement?.[eventName]?._text;

      if (eventScript) {
        if (eventName === 'onChange' || eventName === 'onSelectionChanged') {
          const promise = runAction({
            data: registeredElement,
            level: 'element',
            eventName,
            formId: String(formId),
            panelId: String(panelId),
            elementId: String(elementId),
            selectedData,
            pageSlotId: registeredElement.pageDataSlot || getCurrentPageSlotId(),
          })
          trackPendingElementEvent(promise)
          return promise
        }
        const promise = runCode(eventScript, selectedData);
        trackPendingElementEvent(promise)
        return promise;
      }

      const eventAction = getEventActionForElement(formId, elementId, eventName);
      if (eventAction) {
        trackPendingElementEvent(runAction({
          data: eventAction,
          level: 'element',
          eventName,
          formId: String(formId),
          panelId: String(panelId),
          elementId: String(elementId),
          selectedData,
          pageSlotId: elements.value[registryKey].pageDataSlot || getCurrentPageSlotId(),
        }));
        return;
      }
      // Вызов из onLoad-listener (en.getElement(id).invokeEvent("onChange", ...)): нет action в layout events — запускаем скрипт элемента по formId/panelId/elementId
      if (eventName === 'onChange' || eventName === 'onSelectionChanged') {
        const inline = pickElementEventScript(registeredElement, eventName)
        const fallbackCtx = {
          level: 'element',
          eventName,
          formId: String(formId),
          panelId: String(panelId),
          elementId: String(elementId),
          data: {
            _attributes: { id: elementId },
            name: { _text: name },
            ...(inline ? { [eventName]: { _text: inline } } : {}),
          },
          selectedData,
          pageSlotId: elements.value[registryKey].pageDataSlot || getCurrentPageSlotId(),
        };
        trackPendingElementEvent(runAction(fallbackCtx));
        return;
      }
      runActionByName(eventName);
    };
  }

  const elementKey = getElementKey(formId, panelId, elementId, name);
  elementsByKey.value[elementKey] = elements.value[registryKey];
  bumpElementsRegistryRevision();

  const multiSelectElement = isMultiSelectElement(element)


  // Ensure forms structure exists (bucket per Tiles row — WUI-373)
  const formsPanelBucket = ensureFormsElementBucket(String(formId), panelId, elementId, ctx)

  if (value !== null && value !== undefined) {
    const parsedDate =
      elTypeNorm === 'DATE' || elTypeNorm === 'DATETIME'
        ? parsePageFieldToDate(value)
        : null
    const storeValue = parsedDate ?? value
    const elData = multiSelectElement ? toMultiSelectTypedValue(value) : setType(storeValue);
    if (elTypeNorm === 'FILEATTACHMENTS') {
      markAttachmentStoreMutationCause({
        fieldName: name,
        elementId,
        cause: 'register-element',
        detail: { source: 'initialValue' },
      })
    }
    forms.value[formId][formsPanelBucket][elementId] = {
      ...forms.value[formId][formsPanelBucket][elementId],
      [name]: elData,
    };
  }

  const skipSharedOverlaySeed = shouldSkipUnscopedOverlayFieldCopy(
    String(elements.value[registryKey]?.pageId ?? (ctx as any)?.formPath?.pageId ?? '').trim(),
    name,
    Object.values(elementsByKey.value) as Array<{ fieldName?: unknown; pageId?: unknown }>,
    pendingOverlayRefillFieldNames(),
    overlayDuplicatedFieldNamesForForm(formId),
  )

  // Handle case when element value was saved in en before element rendered
  if (!skipSharedOverlaySeed && en.value?.hasField(name) && !value) {
    const saved = en.value.getField(name);
    Object.assign(
      forms.value[formId][formsPanelBucket][elementId],
      { [name]: multiSelectElement ? toMultiSelectTypedValue(saved) : saved },
    );
  }

  const pageData = findPageDataByFormId(formId);
  const pageFields = pageData ? mergePageDataFieldSnapshot(pageData) : {};

  // Check for field with exact name or with suffixes
  const possibleFieldNames = multiSelectElement
    ? [name]
    : elTypeNorm === 'LISTBOX' || elTypeNorm === 'COMBOBOX'
      ? [name]
      : [
        name,
        `${name}__listbox`,
        `${name}__items`,
        `${name}__action`
      ];

  let foundInPageData = false;
  if (!skipSharedOverlaySeed) {
  for (const testFieldName of possibleFieldNames) {
    if (pageFields[testFieldName]) {
      const fieldData = pageFields[testFieldName];
      // Handle both {type, value} format and direct value
      const fieldValue = fieldData && typeof fieldData === 'object' && 'value' in fieldData
        ? fieldData.value
        : fieldData;

      if (fieldValue !== undefined && fieldValue !== null) {
        const parsedDate =
          elTypeNorm === 'DATE' || elTypeNorm === 'DATETIME'
            ? parsePageFieldToDate(fieldData)
            : null
        const storeValue = parsedDate ?? fieldValue
        const typedValue = multiSelectElement
          ? toMultiSelectTypedValue(fieldValue)
          : setType(storeValue);
        if (elTypeNorm === 'FILEATTACHMENTS') {
          markAttachmentStoreMutationCause({
            fieldName: name,
            elementId,
            cause: 'register-element',
            detail: { source: 'pageFields', fieldKey: testFieldName },
          })
        }
        Object.assign(
          forms.value[formId][formsPanelBucket][elementId],
          { [name]: typedValue },
        );
        foundInPageData = true;
        break;
      }
    }
  }
  }

  if (foundInPageData && pageData) {
    const bootstrapSlot = String((element as any)?.pageId ?? appStore.currentPageId ?? formId ?? '')
    if (!isFormPageBootstrapActive(bootstrapSlot)) {
      const realFormId = pageData?.jsResult?.form_id ||
        pageData?.status ||
        pageData?.form?.form?._attributes?.id ||
        pageData?.form?.form?._attributes?.fid ||
        pageData?.formId ||
        pageData?.$fid ||
        formId;

      if (syncTimeouts.value[realFormId]) {
        clearTimeout(syncTimeouts.value[realFormId]);
      }

      syncTimeouts.value[realFormId] = setTimeout(() => {
        delete syncTimeouts.value[realFormId];
        syncPageDataToRegistry(realFormId, formId != null ? String(formId) : undefined);
      }, 100);
    }
  }

  if (formId && formProvider?.applyPendingElementStates) {
    void formProvider.applyPendingElementStates(elementId, String(name), String(formId))
  }

  // Hooks поверх XML-элемента — явно, чтобы setVisible Label не терялся после assign invokeEvent
  const registered = elements.value[registryKey]
  if (hooks && registered) {
    if (typeof hooks.setVisible === 'function') registered.setVisible = hooks.setVisible
    if (typeof hooks.setEditable === 'function') registered.setEditable = hooks.setEditable
    if (typeof hooks.setEnable === 'function') registered.setEnable = hooks.setEnable
  }

  if (typeof hooks?.setVisible === 'function' && elementId != null && elementId !== '') {
    elementVisibilityHooksByElementId.set(String(elementId), hooks.setVisible)
  } else if (typeof registered?.setVisible === 'function' && elementId != null && elementId !== '') {
    elementVisibilityHooksByElementId.set(String(elementId), registered.setVisible.bind(registered))
  }

  applyDeferredElementShell(registryKey, elementId)
  applyPersistentScopedVisibility(registryKey)

  if (!isTilesRowScopedRegistryName(String(name))) {
    const statesRaw = pageData?.jsResult?.states
    const statesList = Array.isArray(statesRaw) ? statesRaw : []
    applyRegisteredElementVisibilityFromStates({
      states: statesList,
      elementId,
      elementName: String(name),
      setVisible: (visible) => {
        elements.value[registryKey]?.setVisible?.(visible)
      },
      setShellVisibleById: setElementShellVisibleById,
      getShellVisibleById: (id) => elementShellVisibleByElementId.value[String(id)],
    })
    applyRegisteredElementEditableFromStates({
      states: Array.isArray(statesRaw) ? statesRaw : [],
      elementId,
      elementName: String(name),
      panelId,
      setEditable: (editable) => {
        elements.value[registryKey]?.setEditable?.(editable)
      },
    })
  }
};

const unRegisterElement = ({ formId, panelId, elementId, fieldName, registryName }: any) => {
  if (!fieldName) return;

  const registryKey = registryName ?? fieldName;
  const fieldNameKey = String(fieldName).trim()

  // Get the actual formId from the element if available
  const elementKey =
    formId && panelId && elementId && fieldName
      ? getElementKey(formId, panelId, elementId, fieldName)
      : ''
  const element = elements.value[registryKey] ?? (elementKey ? elementsByKey.value[elementKey] : undefined);
  const actualFormId = element?.formId || formId;
  const elTypeForUnreg = String(
    element?.elementType ?? (typeof element?.getType === 'function' ? element.getType() : '') ?? '',
  )
    .trim()
    .toUpperCase()
  // FILEATTACHMENTS: keep putFullData hooks after panel hide (v-if unmount).
  const preserveFaRegistry = elTypeForUnreg === 'FILEATTACHMENTS'
  if (elementKey && !preserveFaRegistry) {
    delete elementsByKey.value[elementKey];
  }

  const remainingSameName = Object.values(elementsByKey.value).find((el: any) => {
    const elName = String(el?.fieldName ?? el?.name ?? '').trim()
    return elName === fieldNameKey || elName === String(registryKey).trim()
  })

  const matchesUnmountingInstance =
    !element ||
    (String(element.formId ?? '') === String(formId ?? element.formId ?? '') &&
      String(element.panelId ?? '') === String(panelId ?? element.panelId ?? '') &&
      String(element.elementId ?? '') === String(elementId ?? element.elementId ?? ''));
  if (matchesUnmountingInstance) {
    if (remainingSameName) {
      elements.value[registryKey] = remainingSameName
      if (fieldName && fieldName !== registryKey) {
        elements.value[fieldName] = remainingSameName
      }
    } else if (!preserveFaRegistry) {
      delete elements.value[registryKey];
      if (fieldName && fieldName !== registryKey && elements.value[fieldName] === element) {
        delete elements.value[fieldName]
      }
    }
    const removedElementId = element?.elementId ?? elementId
    if (!preserveFaRegistry && removedElementId != null && removedElementId !== '') {
      const remainingKeepsXmlId =
        remainingSameName != null &&
        String((remainingSameName as { elementId?: unknown }).elementId ?? '') ===
          String(removedElementId)
      if (!remainingKeepsXmlId) {
        elementVisibilityHooksByElementId.delete(String(removedElementId))
      }
    }
    bumpElementsRegistryRevision();
  }

  // Check if form is still in breadcrumb history - if yes, don't remove element
  // This allows forms to be cached and restored without losing element registrations
  const currentPageId = appStore.currentPageId;
  const pageData = appStore.pagesData[actualFormId] || appStore.pagesData[currentPageId];

  // Don't remove element if form is still accessible (in breadcrumb history or current page)
  if (pageData && (actualFormId === currentPageId || pageData.form)) {
    const elTypeNorm = String(
      element?.elementType ?? (typeof element?.getType === 'function' ? element.getType() : '') ?? '',
    )
      .trim()
      .toUpperCase()
    // Identificator / MultiSelect / FileAttachments: данные нужны после hide панели (v-if unmount).
    const preserveFormsSlot =
      elTypeNorm === 'IDENTIFICATOR' ||
      elTypeNorm === 'FILEATTACHMENTS' ||
      isMultiSelectElement(element)
    if (
      !preserveFormsSlot &&
      forms.value[actualFormId]?.[panelId]
    ) {
      delete forms.value[actualFormId][panelId][elementId];
    }
    if (fieldName && !remainingSameName) {
      delete tempData.value[fieldName];
    }
    return;
  }

  // Remove element only if form is truly closed
  if (forms.value[actualFormId]?.[panelId]) {
    delete forms.value[actualFormId][panelId][elementId];
  }
  if (fieldName && !remainingSameName) {
    delete tempData.value[fieldName];
  }
  if (element && !remainingSameName) {
    delete elements.value[fieldName];
    bumpElementsRegistryRevision();
  }
};

/**
 * Единая точка записи значений элементов формы.
 *
 * - **Обновляет локальный registry** `forms[formId][panelId][elementId]` — это источник данных для `getForm()`
 *   и основной слой, который читают элементы при рендере.
 * - **Дублирует изменения в `appStore.pagesData[*].uniData.fields.fields` (и `jsResult.changed_data`)**,
 *   чтобы пользовательский ввод не терялся при action/refill/перерисовках, когда часть логики пересобирает
 *   состояние из снапшота `pagesData`.
 *
 * `omitKeys` используется для сценариев, где одно действие обновляет несколько связанных полей
 * (например, Swing-like COMBOBOX), и некоторые ключи нужно явно удалить перед merge.
 */
const REFILL_SKIP_EMPTY_IF_SLOT_HAS_VALUE_TYPES = new Set([
  'LISTBOX',
  'COMBOBOX',
  'MULTISELECT',
  'DATERANGE',
  'TIME',
  'CHECKBOX',
  'BOOLEAN',
  'FILEATTACHMENTS',
])

function resolveRegistryFieldElementType(
  storageFormId: string,
  panelId: string | number,
  elementId: string | number,
  fieldName: string,
): string {
  const fid = String(storageFormId ?? '')
  const pid = String(panelId ?? '')
  const eid = String(elementId ?? '')
  const fname = String(fieldName ?? '').trim()
  if (!fname) return ''
  for (const el of Object.values(elements.value) as Array<{
    formId?: unknown
    panelId?: unknown
    elementId?: unknown
    fieldName?: unknown
    name?: unknown
    elementType?: string
    getType?: () => string
  }>) {
    if (!el) continue
    const regFid = String(resolveFormsStorageFormId(el.formId as string | number | undefined) ?? el.formId ?? '')
    if (regFid !== fid) continue
    if (String(el.panelId ?? '') !== pid) continue
    if (String(el.elementId ?? '') !== eid) continue
    if (String(el.fieldName ?? el.name ?? '') !== fname) continue
    return String(el.elementType ?? el.getType?.() ?? '')
      .trim()
      .toUpperCase()
  }
  return ''
}

const SCALAR_MODEL_VALUE_ELEMENT_TYPES = new Set([
  'TEXT',
  'PASSWORD',
  'TEXTAREA',
  'IDENTIFICATOR',
])

const normalizeTextFieldIncomingValue = (
  value: unknown,
): string | number | boolean | null | undefined => {
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }

  const objectValue = value as Record<string, unknown>
  const scalarCandidate = objectValue.__str ?? objectValue._text
  if (
    scalarCandidate === null ||
    scalarCandidate === undefined ||
    typeof scalarCandidate === 'string' ||
    typeof scalarCandidate === 'number' ||
    typeof scalarCandidate === 'boolean'
  ) {
    return scalarCandidate as string | number | boolean | null | undefined
  }

  return undefined
}

const setValue = ({
  value,
  formId,
  panelId,
  elementId,
  omitKeys,
  skipStore,
  tilesRowScope,
  pageDataSlot,
}: {
  value: Record<string, any>
  formId: string | number
  panelId: string | number
  elementId: string | number
  omitKeys?: string[]
  skipStore?: boolean
  tilesRowScope?: SwingTilesRowScopeLike | null
  pageDataSlot?: string
}) => {
  if (!formId || !panelId || !elementId || !value) {
    return;
  }

  try {
    const storageFormId = resolveFormsStorageFormIdForContext(formId, pageDataSlot)
    const formsPanelBucket = ensureFormsElementBucket(
      storageFormId,
      panelId,
      elementId,
      tilesRowScope ? { tilesRowScope } : null,
    )

    const curr = { ...(forms.value[storageFormId][formsPanelBucket][elementId] || {}) };
    if (Array.isArray(omitKeys) && omitKeys.length) {
      for (const key of omitKeys) {
        if (key != null && String(key).trim()) {
          delete curr[String(key)];
        }
      }
    }
    const typedValue = setTypes(value)

    for (const key of Object.keys(typedValue)) {
      const elNorm = resolveRegistryFieldElementType(storageFormId, panelId, elementId, key)
      if (!SCALAR_MODEL_VALUE_ELEMENT_TYPES.has(elNorm)) continue

      const incoming = typedValue[key]
      if (!incoming || typeof incoming !== 'object' || !('value' in incoming)) continue

      const normalizedScalar = normalizeTextFieldIncomingValue(
        (incoming as { value: unknown }).value,
      )
      if (
        normalizedScalar === undefined &&
        (incoming as { value: unknown }).value !== null &&
        (incoming as { value: unknown }).value !== undefined
      ) {
        delete typedValue[key]
        continue
      }

      if ((incoming as { value: unknown }).value !== normalizedScalar) {
        typedValue[key] = {
          ...(incoming as { type?: string; value?: unknown }),
          value: normalizedScalar,
        } as { type: string; value: unknown }
      }
    }
    for (const key of Object.keys(typedValue)) {
      const elNorm = resolveRegistryFieldElementType(storageFormId, panelId, elementId, key)
      if (!REFILL_SKIP_EMPTY_IF_SLOT_HAS_VALUE_TYPES.has(elNorm)) continue
      const incoming = typedValue[key]
      if (!fieldEntryLooksEmptyIncoming(incoming)) continue
      const existing = curr[key]
      if (!fieldEntryHasPresentableValue(existing)) continue
      delete typedValue[key]
    }
    // DateRange: параллельно UI `{from,to}` держим wire `__first`/`__second` в slot+pagesData,
    // чтобы syncPageDataToRegistry / layout-scoped currentData не откатывали фильтр (форма 2565).
    for (const key of Object.keys(typedValue)) {
      const elNorm = resolveRegistryFieldElementType(storageFormId, panelId, elementId, key)
      if (elNorm !== 'DATERANGE') continue
      const incoming = typedValue[key]
      const wire = buildDateRangeWireFieldsFromUiValue(key, incoming)
      typedValue[wire.firstKey] = setType(wire.first)
      typedValue[wire.secondKey] = setType(wire.second)
    }
    if (Object.keys(typedValue).length === 0) {
      return
    }
    for (const key of Object.keys(typedValue)) {
      const elNorm = resolveRegistryFieldElementType(storageFormId, panelId, elementId, key)
      if (elNorm !== 'FILEATTACHMENTS') continue
      markAttachmentStoreMutationCause({
        fieldName: key,
        elementId,
        cause: 'setValue',
        detail: { formId: storageFormId, panelId },
      })
    }
    forms.value[storageFormId][formsPanelBucket][elementId] = { ...curr, ...typedValue };

    const preferredSlot = String(pageDataSlot ?? '').trim()

    if (skipStore) {
      return;
    }

    // Транслирует пользовательский ввода в page/store (uniData + jsResult.changed_data)
    // для сохранения в случае actions/refills.
    // TODO: нет уверенности в правильности логики, должен ли пользовательский ввод туда? Или проблема с логике refills.
    try {
      const wantedFormId = formId
      const wantedStr = String(wantedFormId)
      const pageStoreKey = resolvePagesDataStoreKey(appStore.pagesData as Record<string, any>, {
        formId: wantedFormId,
        registryFormId: wantedStr,
        currentPageId: appStore.currentPageId,
        routeParamId: route?.params?.id != null
          ? String(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id)
          : undefined,
        // Не подменять слот элемента «текущим» — иначе reload пишет мимо pageFields таблицы.
        pageDataSlot: preferredSlot || getCurrentPageSlotId(),
        topDialogId: appStore.dialogIds?.length
          ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
          : '',
      })

      if (pageStoreKey && !isFormPageBootstrapActive(pageStoreKey)) {
        const pageData = (appStore.pagesData as Record<string, any>)[pageStoreKey]
        if (pageData && typeof pageData === 'object') {
          const allowEmptyUserCommit = {
            shouldPreserveEmptyIncoming: (key: string) => {
              const incoming = typedValue[key]
              // Reload нормализует empty:true|false — авторитетная замена таблицы.
              if (
                incoming &&
                typeof incoming === 'object' &&
                String((incoming as { type?: string }).type ?? '') === 'UniDataTable'
              ) {
                const val = (incoming as { value?: { empty?: unknown } }).value
                if (val && typeof val === 'object' && typeof val.empty === 'boolean') {
                  return false
                }
              }
              return !(key in typedValue) || !fieldEntryLooksEmptyIncoming(typedValue[key])
            },
          }
          const mergedUniFields = mergeFieldMaps(
            pageData.uniData?.fields?.fields || {},
            typedValue,
            allowEmptyUserCommit,
          )
          const mergedJsChangedFields = mergeFieldMaps(
            pageData.jsResult?.changed_data?.fields?.fields || {},
            typedValue,
            allowEmptyUserCommit,
          )
          if (
            scopedFieldMergeIsNoop(
              pageData.uniData?.fields?.fields,
              mergedUniFields,
              typedValue,
              omitKeys,
            ) &&
            scopedFieldMergeIsNoop(
              pageData.jsResult?.changed_data?.fields?.fields,
              mergedJsChangedFields,
              typedValue,
              omitKeys,
            )
          ) {
            return
          }
          appStore.setPageData(pageStoreKey, {
            ...pageData,
            uniData: {
              ...(pageData.uniData || {}),
              fields: {
                ...(pageData.uniData?.fields || {}),
                fields: mergedUniFields,
              },
            },
            jsResult: {
              ...(pageData.jsResult || {}),
              changed_data: {
                ...(pageData.jsResult?.changed_data || {}),
                fields: {
                  ...(pageData.jsResult?.changed_data?.fields || {}),
                  fields: mergedJsChangedFields,
                },
              },
            },
            // Иначе mergePageDataFieldSnapshot(uni, chain, changed) поднимает stale rows из chain.
            currentChainData: (() => {
              const chain = pageData.currentChainData
              if (!chain || typeof chain !== 'object') return chain
              const chainFields = chain.fields?.fields
              if (!chainFields || typeof chainFields !== 'object') return chain
              let touched = false
              const nextChainFields = { ...chainFields }
              for (const [key, incoming] of Object.entries(typedValue)) {
                if (
                  incoming &&
                  typeof incoming === 'object' &&
                  String((incoming as { type?: string }).type ?? '') === 'UniDataTable'
                ) {
                  const val = (incoming as { value?: { empty?: unknown } }).value
                  if (val && typeof val === 'object' && typeof val.empty === 'boolean') {
                    nextChainFields[key] = incoming
                    touched = true
                  }
                }
              }
              if (!touched) return chain
              return {
                ...chain,
                fields: {
                  ...(chain.fields || {}),
                  fields: nextChainFields,
                },
              }
            })(),
          }, undefined, undefined, 'GlobalProvider.setValue')
        }
      }
    } catch (e) {
      console.warn('[GlobalProvider.setValue] Failed to persist fields to pagesData', e)
    }
  } catch (error) {
    console.error("Error in setValue:", error);
  }
};

// Найти event action (layout position=events) для элемента по имени события
const getEventActionForElement = (formId: string | number, elementId: string | number, eventName: string): any => {
  const pageData = findPageDataByFormId(formId)
  if (!pageData?.form?.actions?.action) return null
  const actionsList = Array.isArray(pageData.form.actions.action) ? pageData.form.actions.action : [pageData.form.actions.action]
  const elId = typeof elementId === 'number' ? elementId : parseInt(String(elementId), 10)
  const nameCandidates = new Set(
    eventActionNameCandidates(eventName).map((n) => String(n).trim().toLowerCase()),
  )
  const found = actionsList.find((a: any) => {
    const level = a.layout?.level?._text
    const id = a.layout?.id?._text != null ? parseInt(String(a.layout.id._text), 10) : null
    const position = a.layout?.position?._text
    const nameRaw = a.name?._text ?? a.name
    const nameNorm = String(nameRaw ?? '').trim().toLowerCase()
    return level === 'element' && id === elId && position === 'events' && nameCandidates.has(nameNorm)
  }) || null
  return found
}

const pageEntryMatchesIdentity = (storeKey: string, pageData: any, wantedId: string | number): boolean => {
  if (!pageData || wantedId == null || wantedId === '') return false
  const wanted = String(wantedId)
  return getPageIdentityAliases(pageData as Record<string, any>, { slotId: storeKey }).includes(wanted)
}

const resolveFormsStorageFormId = (formId: string | number | undefined): string => {
  const fid = String(formId ?? '').trim()
  if (!fid) return ''

  if (forms.value[fid] != null) return fid

  const pageData = findPageDataByFormId(fid)
  const slotId = getCurrentPageSlotId()
  const aliasCandidates = new Set<string>()
  if (pageData) {
    for (const alias of getPageIdentityAliases(pageData as Record<string, any>, { slotId })) {
      if (alias) aliasCandidates.add(alias)
    }
  }
  if (slotId) aliasCandidates.add(slotId)
  const registryFormId = getCurrentRegistryFormId()
  if (registryFormId) aliasCandidates.add(registryFormId)

  for (const alias of aliasCandidates) {
    if (forms.value[alias] != null) return alias
  }
  return fid
}

/** Единый ключ forms registry для getForm/setValue (slot из FormProvider vs schema formId). */
const resolveFormsStorageFormIdForContext = (
  formId: string | number | undefined,
  pageDataSlot?: string,
): string => {
  return resolveFormsStorageFormIdForContextUtil(
    formId,
    pageDataSlot,
    (key) => forms.value[key] != null,
    (fid) => resolveFormsStorageFormId(fid) || String(fid ?? ''),
  )
}

const elementRegistryFormIdsMatch = (
  registeredFormId: string,
  contextFormId: string,
  slotId: string,
  pageData: any,
): boolean => {
  if (!registeredFormId || !contextFormId) return false
  if (registeredFormId === contextFormId) return true
  if (!pageData || !slotId) return false
  return (
    pageEntryMatchesIdentity(slotId, pageData, registeredFormId) &&
    pageEntryMatchesIdentity(slotId, pageData, contextFormId)
  )
}

// Helper function to find pageData by slot/runtime/window id
const findPageDataByFormId = (formId: string | number): any => {
  if (!formId) return null

  const formIdStr = formId.toString()
  const formIdNum = typeof formId === 'number' ? formId : parseInt(formIdStr, 10)

  // Direct page id lookup ("89_adj_123" for split pages, "89_win_sess" for NEW_WINDOW tabs)
  if ((formIdStr.includes('_adj_') || formIdStr.includes('_win_')) && appStore.pagesData[formIdStr]) {
    return appStore.pagesData[formIdStr]
  }

  const currentPageId = appStore.currentPageId

  // Prefer adjacent (split) page when it matches formId — main page stays currentPageId, adjacent form needs its own data
  const lastAdjacentId = currentPageId ? (appStore.lastAdjacentPageIdByParent as Record<string, string>)[currentPageId] : null
  if (lastAdjacentId && appStore.pagesData[lastAdjacentId]) {
    const adjData = appStore.pagesData[lastAdjacentId]
    if (pageEntryMatchesIdentity(lastAdjacentId, adjData, formId)) {
      return adjData
    }
  }

  // Then try current pageData if the form is already open
  if (currentPageId && appStore.pagesData[currentPageId]) {
    const currentPageData = appStore.pagesData[currentPageId]
    if (pageEntryMatchesIdentity(String(currentPageId), currentPageData, formId)) {
      return currentPageData
    }
  }

  // Then try direct lookup (pagesData is keyed by formId/status when opened from chain)
  if (appStore.pagesData[formIdStr]) {
    const pageData = appStore.pagesData[formIdStr]
    if (pageEntryMatchesIdentity(formIdStr, pageData, formId) || formIdStr === String(formId)) {
      return pageData
    }
  }
  if (appStore.pagesData[formIdNum]) {
    const pageData = appStore.pagesData[formIdNum]
    if (pageEntryMatchesIdentity(String(formIdNum), pageData, formId) || String(formIdNum) === String(formId)) {
      return pageData
    }
  }

  // Search through all pagesData to find one with matching formId
  for (const [key, pageData] of Object.entries(appStore.pagesData)) {
    if (!pageData) continue

    if (pageEntryMatchesIdentity(key, pageData, formId) || key === formIdStr || key === formIdNum.toString() || parseInt(key) === formIdNum) {
      return pageData
    }
  }

  // Fallback via resolvePageStoreEntry (без «любой currentPageId с данными»)
  const entry = findPageStoreEntryForForm(appStore.pagesData as Record<string, any>, {
    registryFormId: formId,
    formId,
    slotId: getCurrentPageSlotId(),
    currentPageId: appStore.currentPageId,
    topDialogId: appStore.dialogIds?.length
      ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
      : '',
  })
  if (entry) {
    return entry.page
  }

  return null
}

const writePageDataForForm = (
  registryFormId: string | number,
  updatedPageData: Record<string, unknown>,
  debugSource: string,
  options?: { originSlotId?: string; response?: Record<string, unknown> | null },
) => {
  const originSlotId = options?.originSlotId ?? getCurrentPageSlotId()
  const storeKey = resolveStateUpdateStoreKey(appStore.pagesData as Record<string, any>, {
    originSlotId,
    pageDataSlot: originSlotId,
    registryFormId,
    formId: registryFormId,
    currentPageId: appStore.currentPageId,
    topDialogId: appStore.dialogIds?.length
      ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
      : '',
    responseSchemaFormId: resolveResponseSchemaFormId(
      options?.response ?? (updatedPageData as Record<string, any>),
    ),
  })
  if (!storeKey) return
  appStore.setPageData(storeKey, updatedPageData, undefined, undefined, debugSource)
}

const syncPageDataToRegistry = (
  formId: string | number,
  pageId?: string,
  options?: { forceFieldNames?: string[]; originOverlayPageId?: string },
) => {
  const pageData = findPageDataByFormId(formId);
  if (!pageData) {
    return;
  }

  const pageFields = pageData ? mergePageDataFieldSnapshot(pageData) : {};

  if (!pageFields || Object.keys(pageFields).length === 0) {
    return;
  }

  const originOverlayPageId = normalizeOriginOverlayPageId(
    options?.originOverlayPageId,
    Object.values(elementsByKey.value).map((el) => (el as { pageId?: unknown }).pageId),
  )

  const forceFieldNames = new Set(
    (options?.forceFieldNames ?? []).map((key) => String(key ?? '').trim()).filter(Boolean),
  )

  const currentPageId = appStore.currentPageId;
  const pageIdStr = pageId?.toString();
  const formIdStr = formId.toString();

  const targetFormIds = new Set<string>();
  if (pageIdStr) targetFormIds.add(pageIdStr);
  if (formIdStr) targetFormIds.add(formIdStr);
  if (pageId) targetFormIds.add(pageId);
  const pageFormId = pageData?.jsResult?.form_id ||
    pageData?.status ||
    pageData?.form?.form?._attributes?.id ||
    pageData?.form?.form?._attributes?.fid ||
    pageData?.formId ||
    pageData?.$fid;
  if (pageFormId != null) {
    targetFormIds.add(String(pageFormId));
  }

  const pageSlotId = pageIdStr || (currentPageId != null ? String(currentPageId) : '')
  if (pageData && pageSlotId) {
    for (const alias of getPageIdentityAliases(pageData as Record<string, any>, { slotId: pageSlotId })) {
      if (alias) targetFormIds.add(alias)
    }
  }

  if (currentPageId && appStore.pagesData[currentPageId]) {
    const currentPageData = appStore.pagesData[currentPageId];
    const currentPageFormId = currentPageData?.jsResult?.form_id ||
      currentPageData?.status ||
      currentPageData?.form?.form?._attributes?.id ||
      currentPageData?.form?.form?._attributes?.fid ||
      currentPageData?.formId ||
      currentPageData?.$fid;

    if (currentPageFormId && currentPageFormId.toString() === formIdStr) {
      targetFormIds.add(currentPageId.toString());
    }
  }

  for (const [elementKey, element] of Object.entries(elementsByKey.value)) {
    const elementFormId = element.formId?.toString();

    if (!elementFormId) {
      continue;
    }

    const elementMatchesTarget =
      targetFormIds.has(elementFormId) ||
      [...targetFormIds].some((targetId) =>
        elementRegistryFormIdsMatch(elementFormId, targetId, pageSlotId, pageData),
      )
    if (!elementMatchesTarget) {
      continue;
    }

    const { panelId, elementId, formId: elementFormIdValue } = element;
    if (!panelId || !elementId) continue;
    if (
      !shouldSyncPageFieldToElementPage(
        (element as { pageId?: unknown }).pageId,
        originOverlayPageId,
        {
          fieldName: String((element as { fieldName?: unknown }).fieldName ?? '').trim()
            || String((element as { name?: unknown }).name ?? '').trim(),
          registeredElements: Object.values(elementsByKey.value) as Array<{
            fieldName?: unknown
            pageId?: unknown
          }>,
          pendingOverlayRefillFields: pendingOverlayRefillFieldNames(),
          layoutDuplicatedFieldNames: overlayDuplicatedFieldNamesForForm(elementFormIdValue || elementFormId),
        },
      )
    ) {
      continue
    }

    const registryFormId = resolveFormsStorageFormId(elementFormIdValue || elementFormId);

    if (!forms.value[registryFormId]) {
      forms.value[registryFormId] = {};
    }
    if (!forms.value[registryFormId][panelId]) {
      forms.value[registryFormId][panelId] = {};
    }
    if (!forms.value[registryFormId][panelId][elementId]) {
      forms.value[registryFormId][panelId][elementId] = {};
    }

    const baseFieldName =
      (element as any).fieldName ??
      (element as any).name ??
      (element as any).element?.name?._text ??
      (element as any).element?.name ??
      (element as any).element?._attributes?.id ??
      elementKey;

    const multiSelectElement = isMultiSelectElement(element)
    const registrySlot = forms.value[registryFormId][panelId][elementId]
    const hasLocalMultiSelectValue =
      multiSelectElement &&
      registrySlot &&
      Object.prototype.hasOwnProperty.call(registrySlot, baseFieldName)

    const hasFieldData = resolvePageFieldEntry(pageFields, baseFieldName, multiSelectElement) !== undefined
    const skipForLocalMultiSelect =
      hasLocalMultiSelectValue && !forceFieldNames.has(baseFieldName)
    const elTypeNormEarly = String(
      (element as { elementType?: string; getType?: () => string }).elementType ??
        (element as { getType?: () => string }).getType?.() ??
        '',
    )
      .trim()
      .toUpperCase()
    const pageFieldForEditable = resolvePageFieldEntry(pageFields, baseFieldName, multiSelectElement)
    const registryEditableEntry = registrySlot?.[baseFieldName]
    const skipStaleEditableOverwrite =
      elTypeNormEarly === 'EDITABLE' &&
      editableTableRegistryRicherThanPage(registryEditableEntry, pageFieldForEditable)
    if (hasFieldData && !skipForLocalMultiSelect && !skipStaleEditableOverwrite) {
      const assigned = assignPageFieldToRegistrySlot(
        forms.value[registryFormId][panelId][elementId] as Record<string, unknown>,
        baseFieldName,
        pageFields,
        { multiSelect: multiSelectElement },
      )
      if (assigned) {
        const elTypeNorm = elTypeNormEarly
        if (elTypeNorm === 'FILEATTACHMENTS') {
          markAttachmentStoreMutationCause({
            fieldName: baseFieldName,
            elementId,
            cause: 'page-field-sync',
            detail: {
              pageSlotId: pageIdStr ?? null,
              forced: forceFieldNames.has(baseFieldName),
            },
          })
        }
      }
    }
  }
};

/** Серверный is_visible панели + `elements` registry (как SwingPanel/SwingPage.isVisible при getData). */
const isPanelContextVisibleForGetData = (formId: any, panelId: any): boolean => {
  const panelIdNum = typeof panelId === 'string' ? parseInt(panelId, 10) : panelId
  const pageData = findPageDataByFormId(formId)
  const pageStates = pageData?.jsResult?.states || []
  const panelState = pageStates.find(
    (state: any) => state.element_level === 'PANEL' && state.element_id === panelIdNum,
  )
  const isPanelVisible = panelState === undefined || panelState.is_visible !== false
  let elementVisible = true
  let foundPanelElement = false
  for (const [, element] of Object.entries(elements.value)) {
    if (element.panelId === panelId && element.elementId === panelIdNum) {
      foundPanelElement = true
      if (element.isVisible !== undefined && !element.isVisible) {
        elementVisible = false
      }
      break
    }
  }
  if (foundPanelElement && !elementVisible) return false
  if (!isPanelVisible) return false
  return true
}

const refreshPageDataSnapshot = (ctx?: { formId?: number | string; pageDataSlotId?: string }) => {
  const formId = ctx?.formId ?? getCurrentRegistryFormId()
  const slotFromCtx = String(ctx?.pageDataSlotId ?? '').trim()
  const storeKey =
    slotFromCtx ||
    resolveStateUpdateStoreKey(appStore.pagesData as Record<string, any>, {
      formId: formId != null && String(formId).trim() !== '' ? String(formId) : undefined,
      currentPageId: getCurrentPageSlotId(),
    }) ||
    (formId != null && String(formId).trim() !== '' ? String(formId) : '')

  if (!storeKey) return null
  return (appStore.pagesData as Record<string, any>)[storeKey] ?? null
}

const getPageStoreField = (
  fieldName: string,
  ctx?: { formId?: number | string; pageDataSlotId?: string },
) => {
  const trimmed = String(fieldName ?? '').trim()
  if (!trimmed) return undefined

  const formId = ctx?.formId ?? getCurrentRegistryFormId()
  const slotFromCtx = String(ctx?.pageDataSlotId ?? '').trim()
  const storeKey =
    slotFromCtx ||
    resolveStateUpdateStoreKey(appStore.pagesData as Record<string, any>, {
      formId: formId != null && String(formId).trim() !== '' ? String(formId) : undefined,
      currentPageId: getCurrentPageSlotId(),
    }) ||
    (formId != null && String(formId).trim() !== '' ? String(formId) : '')

  const pageData =
    (storeKey ? (appStore.pagesData as Record<string, any>)[storeKey] : undefined) ||
    (formId != null && String(formId).trim() !== ''
      ? findPageDataByFormId(String(formId))
      : null)

  if (!pageData) return undefined
  const snapshot = mergePageDataFieldSnapshot(pageData) as Record<string, unknown>
  return snapshot[trimmed]
}

/**
 * Swing-like `getForm` для скриптов En.
 *
 * Возвращает срез данных формы/панели/элемента из локального registry `forms`
 * c учётом page slot, видимости панели и row-scope для Tiles.
 */
const getForm = ({
  formId,
  panelId,
  elementId,
  flatten = true,
  pageDataSlot,
  tilesRowScope,
}: {
  formId?: string | number
  panelId?: string | number
  elementId?: string | number
  flatten?: boolean
  pageDataSlot?: string
  tilesRowScope?: SwingTilesRowScopeLike | null
}) => {
  const preferredSlot = String(pageDataSlot ?? '').trim()
  let data: { form: any; deep: number } = { form: {}, deep: 0 };

  if (formId && panelId && elementId) {
    const storageFormId = resolveFormsStorageFormIdForContext(formId, pageDataSlot)
    if (!forms.value[storageFormId]) {
      forms.value[storageFormId] = {};
    }
    const bucket = resolveTilesPanelFormsStorageKey(panelId, tilesRowScope ?? null)
    if (!forms.value[storageFormId][bucket]) {
      forms.value[storageFormId][bucket] = {};
    }
    const formData = omitLeakedTableColumnFieldsFromElementSlot(
      (forms.value[storageFormId]?.[bucket]?.[elementId] ?? {}) as Record<string, unknown>,
    )
    if (!isPanelContextVisibleForGetData(formId, panelId)) {
      data = Object.keys(formData).length > 0 ? { form: formData, deep: 0 } : { form: {}, deep: 0 };
    } else {
      data = { form: formData, deep: 0 };
    }
  } else if (formId && panelId && !elementId) {
    const formIdStr = String(formId)
    const formIdNum = typeof formId === 'number' ? formId : parseInt(formIdStr, 10)
    const storageFormId = resolveFormsStorageFormIdForContext(formId, pageDataSlot)
    if (!forms.value[storageFormId]) {
      forms.value[storageFormId] = {};
    }
    const formPanelsResolved =
      forms.value[storageFormId] ?? forms.value[formIdStr] ?? forms.value[formIdNum] ?? forms.value[formId]

    // Check if panel is visible
    const panelIdNum = typeof panelId === 'string' ? parseInt(panelId) : panelId
    const pageData = findPageDataByFormId(formId)
    const pageStates = pageData?.jsResult?.states || []
    const panelState = pageStates.find((state: any) =>
      state.element_level === 'PANEL' && state.element_id === panelIdNum
    )
    const isPanelVisible = panelState === undefined || panelState.is_visible !== false

    // Also check if panel element exists and is visible
    let elementVisible = true
    let foundPanelElement = false
    for (const [elementName, element] of Object.entries(elements.value)) {
      if (element.panelId === panelId && element.elementId === panelIdNum) {
        foundPanelElement = true
        if (element.isVisible !== undefined && !element.isVisible) {
          elementVisible = false
        }
        break
      }
    }

    if (foundPanelElement && !elementVisible) {
      data = { form: {}, deep: 1 };
    } else if (!isPanelVisible) {
      data = { form: {}, deep: 1 };
    } else {
      let panelData: Record<string, any> = {};
      const bucketKeys = tilesRowScope
        ? [resolveTilesPanelFormsStorageKey(panelId, tilesRowScope)]
        : listPanelFormsStorageKeys(panelId, Object.keys(formPanelsResolved ?? {}))

      for (const bucketKey of bucketKeys) {
        const panelElements = formPanelsResolved?.[bucketKey] ?? {};
        for (const [slotElementId, elementData] of Object.entries(panelElements)) {
          if (!tilesRowScope) {
            const shellHidden =
              elementShellVisibleByElementId.value?.[String(slotElementId)] === false
            const registered = findRegisteredPanelChild(elements.value, {
              panelId,
              elementId: slotElementId,
              formId,
            })
            const hint = registered ? readPanelGetDataElementHint(registered) : {}
            if (shellHidden && !isIdentificatorElementType(hint.elementType)) {
              continue
            }
            if (
              registered &&
              !shouldIncludeElementInPanelGetData(hint)
            ) {
              continue
            }
            if (
              registered &&
              isIdentificatorElementType(hint.elementType) &&
              typeof (registered as { getData?: () => unknown }).getData === 'function'
            ) {
              const fromHook = (registered as { getData: () => unknown }).getData()
              if (
                fromHook &&
                typeof fromHook === 'object' &&
                !Array.isArray(fromHook) &&
                Object.keys(fromHook as Record<string, unknown>).length > 0
              ) {
                panelData = mergeFieldMaps(panelData, fromHook as Record<string, any>)
                continue
              }
            }
          }
          if (elementData && typeof elementData === 'object') {
            panelData = mergeFieldMaps(
              panelData,
              omitLeakedTableColumnFieldsFromElementSlot(elementData as Record<string, unknown>),
            )
          }
        }
      }

      if (tilesRowScope) {
        panelData = stripTilesRowFieldPrefix(panelData, tilesRowScope) as Record<string, any>
        panelData = overlayTilesCloneWidgetFieldsFromRow(
          panelData,
          tilesRowScope,
          collectTilesCloneWidgetFieldNames(
            Object.values(elements.value) as Array<{
              panelId?: unknown
              fieldName?: unknown
              name?: unknown
              ctx?: unknown
            }>,
            tilesRowScope,
            panelId,
          ),
        ) as Record<string, any>
      }

      data = { form: panelData, deep: 1 };
    }
  } else if (formId && !panelId) {
    const formIdStr = String(formId)
    const formIdNum = typeof formId === 'number' ? formId : parseInt(formIdStr, 10)
    const storageFormId = resolveFormsStorageFormIdForContext(formId, pageDataSlot)

    let formPanels =
      forms.value[storageFormId] ?? forms.value[formIdStr] ?? forms.value[formIdNum] ?? forms.value[formId]

    if (!formPanels) {
      forms.value[storageFormId] = {};
      formPanels = forms.value[storageFormId]
    }

    const pageData = preferredSlot
      ? ((appStore.pagesData as Record<string, any>)[preferredSlot] ?? findPageDataByFormId(formId))
      : findPageDataByFormId(formId)
    let formData: Record<string, any> = pageData ? resolveFormLevelClientFields(pageData) : {}

    formPanels = formPanels ?? {}
    const panelMerged = mergeFormsPanelBucketsForGetForm(
      formPanels as Record<string, Record<string, Record<string, unknown>> | unknown>,
      {
        tilesRowScope: tilesRowScope ?? null,
        isPanelVisible: (bucketKey) => isPanelContextVisibleForGetData(formId, bucketKey),
        mergeFieldMaps,
      },
    )
    formData = mergeFieldMaps(formData, panelMerged)
    // FileAttachments всегда в снимке формы — в т.ч. со скрытых панелей (Swing parity).
    const faFieldNames = collectFileAttachmentFieldNamesFromForm(pageData?.form)
    if (faFieldNames.length > 0) {
      const faFromAllPanels = collectFileAttachmentFieldsFromFormsPanels(
        formPanels as Record<string, Record<string, Record<string, unknown>> | unknown>,
        faFieldNames,
        mergeFieldMaps,
      )
      formData = mergeFieldMaps(formData, faFromAllPanels)
    }
    // Identificator skipStore / storeClientData=0 на видимых панелях — hook getData.
    // `<hidden>` и клоны Tiles не в корне (SwingFormHidden empty / Tiles → строка таблицы).
    formData = mergeFieldMaps(
      formData,
      collectFormIdentificatorGetDataFields(elements.value, {
        formId,
        formRoot: pageData?.form,
        extraMaps: [elementsByKey.value],
      }),
    )
    formData = overlayTilesRowDataOnFormFields(formData, tilesRowScope ?? null, {
      mergeFieldMaps,
    }) as Record<string, any>

    data = { form: formData, deep: 0 };
  } else {
    data = { form: forms.value, deep: 3 };
  }

  const result = flatten ? flattenObj(data.form, data.deep) : data.form;
  return result;
};

// Get data from all panels on a specific page
const getPageData = ({ formId, pageId, flatten = true }: any) => {
  if (!formId || !pageId) {
    return {}
  }

  const pageData = findPageDataByFormId(formId)
  if (!pageData?.form) {
    return {}
  }

  // Find the page in the form structure
  const form = pageData.form
  const pageIdStr = pageId.toString()
  let pagePanels: string[] = []

  // Helper function to extract panel IDs from a page
  const extractPanelsFromPage = (page: any) => {
    if (!page || page.id !== pageIdStr && page.id !== pageId) {
      return
    }

    if (page.panelRows && Array.isArray(page.panelRows)) {
      for (const row of page.panelRows) {
        if (Array.isArray(row)) {
          for (const cell of row) {
            // Cell can be a panel object with panelId or _attributes.id
            if (cell?.panelId) {
              pagePanels.push(cell.panelId)
            } else if (cell?._attributes?.id) {
              pagePanels.push(cell._attributes.id)
            } else if (cell?.id) {
              pagePanels.push(cell.id)
            }
          }
        }
      }
    }
  }

  // Search in main tabs (can be nested arrays)
  if (form.main && Array.isArray(form.main)) {
    for (const mainTab of form.main) {
      if (Array.isArray(mainTab)) {
        for (const page of mainTab) {
          extractPanelsFromPage(page)
        }
      } else if (mainTab?.id) {
        extractPanelsFromPage(mainTab)
      }
    }
  }

  // Search in header
  if (form.header && Array.isArray(form.header)) {
    for (const page of form.header) {
      extractPanelsFromPage(page)
    }
  }

  // Search in footer
  if (form.footer && Array.isArray(form.footer)) {
    for (const page of form.footer) {
      extractPanelsFromPage(page)
    }
  }

  // Remove duplicates
  pagePanels = [...new Set(pagePanels)]

  // Collect data from all panels on this page (only if visible, like SwingUI)
  let pageDataResult: Record<string, any> = {}

  // Get page states to check visibility
  const pageDataForVisibility = findPageDataByFormId(formId)
  const pageStates = pageDataForVisibility?.jsResult?.states || []
  const panelStates = new Map<number, ElementState>()
  pageStates.forEach((state: ElementState) => {
    if (state.element_level === 'PANEL') {
      panelStates.set(state.element_id, state)
    }
  })

  for (const panelId of pagePanels) {
    // Check if panel is visible (like SwingUI checks isVisible())
    const panelIdNum = typeof panelId === 'string' ? parseInt(panelId) : panelId
    const panelState = panelStates.get(panelIdNum)
    const isPanelVisible = panelState === undefined || panelState.is_visible !== false

    // Also check if panel element exists and is visible
    let elementVisible = true
    let foundPanelElement = false
    for (const [elementName, element] of Object.entries(elements.value)) {
      if (element.panelId === panelId && element.elementId === panelIdNum) {
        foundPanelElement = true
        // Check element's visibility state
        if (element.isVisible !== undefined && !element.isVisible) {
          elementVisible = false
        }
        break
      }
    }

    // Only skip data collection if panel element exists AND is explicitly hidden
    if (foundPanelElement && !elementVisible) {
      // Skip if element exists and is hidden
    } else if (!isPanelVisible) {
      // Skip if panel state explicitly says it's not visible
    } else {
      // Collect data if panel is visible (like SwingUI)
      const panelData = getForm({ formId, panelId, flatten: false })
      if (panelData && typeof panelData === 'object') {
        pageDataResult = { ...pageDataResult, ...panelData }
      }
    }
  }

  return flatten ? flattenObj(pageDataResult, 2) : pageDataResult
}

/**
 * @legacy Swing-конвейер действий (En + executeProcedure + elementAction).
 * Новые кнопки и хоткеи — через `actionService.executeAction` (`MAction`, `ElementRenderer`).
 * TODO: перевести оставшиеся вызовы `onAction` / `runAction` на `executeAction` и удалить дублирование.
 *
 * Обрабатывает выполнение действий для действий формы (кнопки, пункты меню и т.д.)
 *
 * Этот метод:
 * 1. Собирает данные формы в зависимости от уровня действия (form/panel/element/page)
 * 2. Опционально проверяет элементы формы, если включена верификация
 * 3. Настраивает контекст En с собранными данными и метаданными действия
 * 4. Выполняет действие, которое может включать:
 *    - Запуск скриптов onClick (на клиенте или сервере)
 *    - Выполнение процедур (executeProcedure)
 *    - Вызов цепочек (event.chainCalled)
 *    - Вызов doAction (elementAction)
 *    - Закрытие модальных окон/диалогов
 * 5. Применяет данные ответа обратно к форме
 *
 * Область сбора данных:
 * - Уровень формы: собирает данные со всех элементов формы
 * - Уровень панели: собирает данные со всех элементов панели
 * - Уровень элемента: собирает данные с конкретного элемента
 * - Уровень страницы: собирает данные со всех панелей на конкретной странице
 *
 * @param getContext - Функция, возвращающая контекст действия (FormContext)
 * @returns Promise, который разрешается при завершении выполнения действия
 */
const onAction = async ({ getContext }: { getContext: () => FormContext }) => {
  let responseData: Record<string, any> = {};

  const ctx = getContext();
  const { formId } = ctx;

  const confirmed = await confirmPlatformAction(ctx.data)
  if (!confirmed) return

  const pageData = findPageDataByFormId(formId) || {};

  if (formId) {
    syncPageDataToRegistry(
      formId,
      String(pageData.$fid ?? pageData.status ?? formId ?? ''),
    )
  }

  if (formId && isActionXmlVerifyEnabled(ctx.data)) {
    const layoutScope = resolveActionLayoutVerifyScope({
      level: ctx.level,
      layout: ctx.data?.layout,
      formId: ctx.formId,
      panelId: ctx.panelId,
      elementId: ctx.elementId,
      pageId: ctx.pageId,
    })
    const { valid, firstInvalidName } = verifyFormElements(elements.value, String(formId), {
      layoutScope,
      formRoot: pageData.form,
      formIdAliases: getPageIdentityAliases(pageData as Record<string, unknown>, {
        slotId: String(pageData.$fid ?? pageData.status ?? formId ?? ''),
        registryFormId: formId,
      }),
    })
    if (!valid) {
      if (firstInvalidName) {
        requestFocusOnElement(firstInvalidName)
      }
      return
    }
  }

  const getFormData = () => {
    const { level, formId, panelId, elementId, selectedData, pageId, data: actionData } = ctx;

    // Skip data collection for actions with position="events" (like SwingUI)
    if (level === 'form' && actionData?.layout?.position?._text === 'events') {
      return {};
    }

    let result: any;

    // Determine the actual level from action layout if available, otherwise use context level
    const actualLevel = actionData?.layout?.level?._text || level;
    const layoutId = actionData?.layout?.id?._text || actionData?.layout?.id;
    const targetFormId = (actualLevel === 'form' && layoutId) ? parseInt(layoutId) : formId;

    switch (actualLevel) {
      case 'form':
        // Form-level: collect data from all elements on the form
        result = getForm({ formId: targetFormId });

        // Add process instances from pageData (like SwingUI)
        if (pageData?.uniData?.ProcessInstances) {
          result.ProcessInstances = pageData.uniData.ProcessInstances;
        }
        break;
      case 'panel':
        // Swing: только getData панели (для панелей Tiles — клон через tilesRowScope в getForm).
        if (panelId) {
          result = getForm({ formId, panelId, tilesRowScope: (ctx as any).tilesRowScope }) || {}
        } else {
          result = getForm({ formId }) || {}
        }
        break
      case 'element':
        // Swing: только элемент (+ selectedData для inline), без merge всей формы.
        if (elementId && panelId) {
          result = {
            ...getForm({ formId, panelId, elementId }),
            ...selectedData,
          }
        } else {
          result = { ...selectedData }
        }
        break
      case 'page':
        // Swing: только страница, без merge всей формы.
        if (pageId) {
          result = getPageData({ formId, pageId }) || {}
        } else {
          result = getForm({ formId }) || {}
        }
        break
      default:
        // For unknown levels, fall back to form-level data collection
        console.warn(`[getFormData] Unknown level "${actualLevel}", falling back to form-level data collection`);
        result = getForm({ formId });

        // Add process instances if available
        if (pageData?.uniData?.ProcessInstances) {
          result.ProcessInstances = pageData.uniData.ProcessInstances;
        }
        break;
    }

    Object.assign(result, responseData)
    const liveOverrides = collectLiveFieldOverrides(formId, elements.value, {
      extraMaps: [elementsByKey.value],
    })
    return mergeLiveFieldOverridesIntoTypedFormData(result, liveOverrides)
  };


  en.value!._resetData();
  en.value!._addData(getFormData());
  en.value!._setActionData(ctx.data);
  en.value!._setPanelId(ctx.panelId);
  en.value!._setFormId(ctx.formId);
  en.value!._setPageData(pageData);

  const data = {
    actionItem: ctx.data,
    pageData,
    elementId: ctx.elementId,
    panelId: ctx.panelId,
    formId: ctx.formId,
    form: getFormData(),
    dialogConfig: ctx.dialogConfig,
    controls: {
      getForm,
      setValueToTempData: (value: any) => { /* TODO: Implement */ },
      runActionByName,
    },
  };

  const dlgIdForClose = String(data.formId ?? '')
  const storeDialogCfg =
    dlgIdForClose && (appStore.dialogs as Record<string, any>)?.[dlgIdForClose]?.config
      ? { ...(appStore.dialogs as Record<string, any>)[dlgIdForClose].config }
      : {}
  const mergedDialogCloseCfg = {
    ...((data.dialogConfig || {}) as Record<string, unknown>),
    ...storeDialogCfg,
  } as Record<string, unknown>

  const {
    needToExecuteProcedure,
    needToCallChain,
    needToCallDoAction,
    needToCloseModal
  } = useActions();

  const action = async () => {
    const props = en.value!.getProperties();
    props.isContextSet = false;
    props.isRepeatSet = false;

    const onClickEvent = data.actionItem.onClick?._text;
    const onChainEvent = data.actionItem['event.chainCalled']?._text;
    const onCloseRaw =
      mergedDialogCloseCfg.onCloseDialogEvent ?? (data.dialogConfig as any)?.onCloseDialogEvent;
    const onCloseEvent =
      onCloseRaw != null && String(onCloseRaw).trim() !== '' ? String(onCloseRaw).trim() : '';

    if (onClickEvent) {
      await en.value!.run(onClickEvent);
      if (props.isContextSet) return;
    }

    const runProcedureAndChain = async () => {
      const currentSessionId = pageData.sessionId ?? null;
      try {
        const response = await coreService.executeProcedure({
          type: pageData.type || 'WF',
          sessionId: currentSessionId,
          socketId: '',
          isClone: false,
          currentData: createSafeClientData({
            fields: en.value!._serialize()
          }),
          actionId: Number(data.actionItem._attributes.id),
          cloneOf: null,
        });

        if (response.fields?.fields) {
          en.value!._addResponse(response.fields.fields);
          responseData = { ...responseData, ...response.fields.fields };
        }
      } catch (error) {
        console.error('Execute procedure error:', error);
      }
      if (needToCallChain(data.actionItem)) {
        await en.value!.run(onChainEvent);
      }
    };

    if (needToExecuteProcedure(data.actionItem) && needToCallChain(data.actionItem)) {
      do {
        props.isRepeatSet = false;
        await runProcedureAndChain();
        if (props.isContextSet) return;
      } while (props.isRepeatSet && !props.isContextSet);
    } else {
      if (needToExecuteProcedure(data.actionItem)) {
        await runProcedureAndChain();
      } else if (needToCallChain(data.actionItem)) {
        await en.value!.run(onChainEvent);
      }
      if (needToCallChain(data.actionItem) && props.isContextSet) {
        return;
      }
    }

    if (needToCallDoAction(data.actionItem)) {
      data.form = { ...en.value!._serialize(), ...responseData };
      const transferRaw = data.actionItem?.transfer?._text || data.actionItem?.transfer
      const intendedDialogFormId = String(transferRaw ?? '').trim()
      const windowTypePre = data.actionItem?.window__type?._text || data.actionItem?.window__type
      const isNewDialogPre = String(windowTypePre ?? '').toUpperCase() === 'NEW_DIALOG'
      const hasFixedDialogSlot =
        isNewDialogPre &&
        intendedDialogFormId !== '' &&
        intendedDialogFormId !== '0'
      const dialogOpenEpochAtStart =
        hasFixedDialogSlot && typeof appStore.getDialogSlotEpoch === 'function'
          ? appStore.getDialogSlotEpoch(intendedDialogFormId)
          : 0
      try {
        const actionPlatform = data.actionItem.platform?._text || currentPlatform.value

        const response = await coreService.elementAction({
          form: data.form,
          formId: String(data.formId),
          pageDataSlot: getCurrentPageSlotId(),
          actionItem: data.actionItem,
          pageData: data.pageData,
          panelId: data.panelId,
          dialogConfig: data.dialogConfig,
          controls: data.controls,
          platform: actionPlatform,
          params: {
            actionId: Number(data.actionItem._attributes.id),
            clientData: createSafeClientData({
              fields: data.form
            }),
            sessionId: pageData.sessionId ?? null,
            type: actionPlatform
          }
        });

        const windowType = data.actionItem?.window__type?._text || data.actionItem?.window__type
        const windowTypeUpper = String(windowType ?? '').toUpperCase()
        const isSameWindow = windowTypeUpper === 'SAME_WINDOW'
        const isNewDialog = windowTypeUpper === 'NEW_DIALOG'
        const isMainFrame = windowTypeUpper === 'MAIN_FRAME'
        const closingModal = needToCloseModal(data.actionItem)

        if (!closingModal && isMainFrame && response?.form && appStore) {
          let mainFormId = response.form?._attributes?.id
          if (!mainFormId && response.status) {
            mainFormId = String(response.status)
          }
          const processed = await buildMainFramePageDataFromActionResponse(
            response as Record<string, unknown>,
            {
              formIdHint: mainFormId != null ? String(mainFormId) : undefined,
              platform: actionPlatform,
              appStore,
            },
          )
          appStore.setPageData(MAIN_FRAME_SLOT_ID, processed, null)
          if (router.currentRoute.value.name !== 'home') {
            await router.push({ name: 'home' })
          }
        } else if (!closingModal && isNewDialog && response?.form && appStore) {
          let dialogFormId = response.form?._attributes?.id
          if (!dialogFormId && response.status) {
            dialogFormId = String(response.status)
          }
          if (!dialogFormId) {
            dialogFormId = String(data.actionItem._attributes.id)
          }
          if (
            shouldDropStaleDialogOpenAfterClose(
              {
                dialogIds: appStore.dialogIds ?? [],
                dialogSlotEpoch: appStore.dialogSlotEpoch ?? {},
              },
              intendedDialogFormId,
              dialogFormId,
              dialogOpenEpochAtStart,
            )
          ) {
            // Dialog was closed before elementAction returned — skip stale reopen.
          } else {
          const parentSlotAtOpen = resolveParentSlotAtDialogOpen(
            appStore,
            getCurrentPageSlotId(),
            ctx.parentPageId as string | undefined,
          )
          const closeCfgFromResponse =
            response.clientDialogCloseConfig && typeof response.clientDialogCloseConfig === 'object'
              ? (response.clientDialogCloseConfig as Record<string, unknown>)
              : {}
          const onCloseRaw =
            mergedDialogCloseCfg.onCloseDialogEvent ??
            closeCfgFromResponse.onCloseDialogEvent ??
            onCloseEvent
          const dialogStoreConfig = buildDialogCloseConfigForStore(closeCfgFromResponse, {
            ...(onCloseRaw ? { onCloseDialogEvent: onCloseRaw } : {}),
            onCloseDialogWebClientExec: resolveOnCloseDialogWebClientExec({
              actionItem: data.actionItem as Record<string, unknown>,
              scriptText: typeof onCloseRaw === 'string' ? onCloseRaw : null,
              responseFlag:
                mergedDialogCloseCfg.onCloseDialogWebClientExec ??
                closeCfgFromResponse.onCloseDialogWebClientExec,
            }),
            ...buildDialogClosedNReturnCloseOverrides({
              actionItem: data.actionItem as Record<string, unknown>,
              responseCfg: closeCfgFromResponse,
              existingEvent:
                mergedDialogCloseCfg.onCloseDialogNReturnEvent ??
                closeCfgFromResponse.onCloseDialogNReturnEvent,
              existingFlag:
                mergedDialogCloseCfg.onCloseDialogNReturnWebClientExec ??
                closeCfgFromResponse.onCloseDialogNReturnWebClientExec,
            }),
            pageDataSlot: parentSlotAtOpen,
            onCloseDialogParentSlotId: parentSlotAtOpen,
          })
          appStore.addDialog({
            id: String(dialogFormId),
            sessionId: response.sessionId,
            config: dialogStoreConfig,
            data: response,
          })
          normalizeDialogPageUniDataFieldAliases(response as Record<string, unknown>)
          ;(response as Record<string, unknown>).$isDialog = true
          const { ensureWebClientOnLoadBeforeOpenForm } = await import(
            '@/services/action/webClientFormOnLoadBeforeBuild'
          )
          const preparedResponse = await ensureWebClientOnLoadBeforeOpenForm(
            response as Record<string, unknown>,
            {
              appStore,
              openedFormId: String(dialogFormId),
              parseActionId: dialogFormId,
            },
          )
          appStore.setPageData(String(dialogFormId), preparedResponse)
          }
        } else if (!closingModal && isSameWindow && response && !response.form && appStore) {
          const targetPageId =
            getCurrentPageSlotId() ||
            appStore.currentPageId ||
            (route?.params?.id as string)
          if (!targetPageId) return

          const currentPageData = appStore.pagesData[targetPageId]
          if (currentPageData) {
            const updatedPageData = {
              ...currentPageData,
              sessionId: response.sessionId || currentPageData.sessionId,
              status: response.status !== undefined ? response.status : currentPageData.status,
              form: currentPageData.form,
              jsResult: response.jsResult ? {
                ...currentPageData.jsResult,
                ...response.jsResult,
                states: mergeExchangeStatesForPageUpdate(response, currentPageData),
                changed_data: response.jsResult.changed_data || currentPageData.jsResult?.changed_data,
                event_listeners: mergeEventListenersForPageUpdate(
                  currentPageData.jsResult?.event_listeners,
                  response.jsResult.event_listeners,
                )
              } : currentPageData.jsResult,
              uniData: response.uniData || currentPageData.uniData,
              currentChainData: response.currentChainData || currentPageData.currentChainData
            }
            appStore.setPageData(targetPageId, updatedPageData, undefined, undefined, 'GlobalProvider.onAction.sameWindowPartial')
            const incomingStates = normalizeExchangeStates(response)
            if (incomingStates.length > 0) {
              await applyIncomingExchangeStates({
                appStore,
                slotId: targetPageId,
                registryFormId: resolveElementRegistryFormId(currentPageData, targetPageId),
                incomingStates,
                changedData: updatedPageData.jsResult?.changed_data,
                processStates: formStatesResult.processStates,
                setPageDataSource: 'GlobalProvider.onAction.sameWindowPartial',
              })
            }
          }
        }
      } catch (error) {
        console.error('Element action error:', error);
      }
    }

    // Allow repeated action execution (only for actions that did not use the procedure+chain loop above)
    if (props.isRepeatSet) {
      responseData = {};
      return action();
    }

    if (needToCloseModal(data.actionItem)) {
      const nReturnRaw =
        mergedDialogCloseCfg.onCloseDialogNReturnEvent ??
        (data.dialogConfig as Record<string, unknown> | undefined)?.onCloseDialogNReturnEvent;
      const onCloseNReturnEvent =
        nReturnRaw != null && String(nReturnRaw).trim() !== '' ? String(nReturnRaw).trim() : '';

      if (onCloseEvent || onCloseNReturnEvent) {
        const dialogPage = (appStore.pagesData as any)?.[dlgIdForClose];
        const dialogStoreEntry = (appStore.dialogs as Record<string, any>)?.[dlgIdForClose];
        const dialogRegistryFormId = String(
          dialogPage?.jsResult?.form_id ??
            dialogPage?.form?.form?._attributes?.id ??
            dialogPage?.formId ??
            dlgIdForClose ??
            '',
        ).trim();
        let mergedCloseFields = dialogPage
          ? mergeFieldMaps(
              dialogPage.uniData?.fields?.fields || {},
              resolveJsResultChangedDataFieldMap(dialogPage.jsResult?.changed_data),
            )
          : mergeFieldMaps(
              dialogStoreEntry?.data?.uniData?.fields?.fields || {},
              resolveJsResultChangedDataFieldMap(dialogStoreEntry?.data?.jsResult?.changed_data),
            );
        const postActionEnFields = en.value?._serialize?.() as Record<string, unknown> | undefined
        if (postActionEnFields && typeof postActionEnFields === 'object' && !Array.isArray(postActionEnFields)) {
          mergedCloseFields = mergeFieldMaps(mergedCloseFields, postActionEnFields)
        }
        mergedCloseFields = isolateAllSharedTableFieldValues(isolateSharedTableFieldAliases(mergedCloseFields));
        const dialogCloseScriptForOverlay = [onCloseEvent, onCloseNReturnEvent]
          .map((script) => (script != null ? String(script).trim() : ''))
          .find(Boolean);
        if (dialogCloseScriptForOverlay) {
          const sourceTableFields = inferDialogCloseSourceTableFieldsFromScript(dialogCloseScriptForOverlay);
          if (sourceTableFields.length) {
            const sourceOverlay = resolveDialogCloseSourceFieldOverlay({
              elements: formProvider?.getElements?.() as Record<string, unknown> | undefined,
              registryFormId: dialogRegistryFormId,
              dialogSlotId: String(dlgIdForClose ?? ''),
              fieldNames: sourceTableFields,
              getForm,
            });
            if (Object.keys(sourceOverlay).length > 0) {
              mergedCloseFields = mergeFieldMaps(mergedCloseFields, sourceOverlay);
            }
          }
        }

        const cfgForClose = { ...mergedDialogCloseCfg } as Record<string, unknown>;
        if (onCloseEvent) {
          cfgForClose.onCloseDialogEvent = onCloseEvent;
        }
        if (onCloseNReturnEvent) {
          cfgForClose.onCloseDialogNReturnEvent = onCloseNReturnEvent;
        }
        const sessionFromStore =
          dlgIdForClose &&
          (appStore.dialogs as Record<string, any>)?.[dlgIdForClose]?.sessionId != null
            ? String((appStore.dialogs as Record<string, any>)[dlgIdForClose].sessionId)
            : '';
        if (
          (!cfgForClose.onCloseDialogScriptSessionId ||
            String(cfgForClose.onCloseDialogScriptSessionId).trim() === '') &&
          pageData.sessionId != null &&
          String(pageData.sessionId).trim() !== ''
        ) {
          cfgForClose.onCloseDialogScriptSessionId = String(pageData.sessionId);
        } else if (
          (!cfgForClose.onCloseDialogScriptSessionId ||
            String(cfgForClose.onCloseDialogScriptSessionId).trim() === '') &&
          sessionFromStore
        ) {
          cfgForClose.onCloseDialogScriptSessionId = sessionFromStore;
        }

        const parentSlotClose = resolveDialogCloseParentContext(appStore, cfgForClose, {
          closingDialogId: dlgIdForClose || String(data.formId),
        }).storeKey
        const parentPageForCloseDeps = parentSlotClose
          ? (appStore.pagesData as Record<string, any>)?.[parentSlotClose]
          : undefined

        const { actionService } = await import('@/services/action/action.service');
        const closeScriptResult = await actionService.executeConfiguredDialogCloseScripts({
          mergedCloseFields,
          dialogPageSnapshot: dialogPage as Record<string, unknown> | undefined,
          cfg: cfgForClose,
          context: ctx as any,
          dependencies: {
            appStore,
            formProvider,
            openUrls: processOpenUrls,
            getElements: formProvider?.getElements,
            getForm,
            reloadElement: reloadElementViaControls,
            refillElement: refillElementViaControls,
            pageDataSlot: parentSlotClose || undefined,
            pageData: parentPageForCloseDeps ?? pageData,
          },
          dialogId: dlgIdForClose || String(data.formId),
        });
        if (closeScriptResult.pending.length > 0) {
          await flushDialogClosePendingFieldOnChanges(
            closeScriptResult.pending,
            formProvider,
            appStore,
          )
        }
        if (parentSlotClose && typeof formProvider.resyncTabSlotFields === 'function') {
          formProvider.resyncTabSlotFields(parentSlotClose, { skipSnapshotRestore: true })
        }
        en.value!._resetData();
        await appStore.removeDialog(String(data.formId), {
          router,
          routePageId: route?.params?.id != null ? String(route.params.id) : undefined,
        });
        if (
          parentSlotClose &&
          typeof formProvider.replayPageVisibilityStatesForSlot === 'function'
        ) {
          formProvider.replayPageVisibilityStatesForSlot(parentSlotClose)
        }
      } else {
        await appStore.removeDialog(String(data.formId), {
          router,
          routePageId: route?.params?.id != null ? String(route.params.id) : undefined,
        });
      }
    }
  };
  await action();
};

const tempActions = useActions()

const onElement = async (ctx: any) => {
  try {
    const formId = ctx?.formId ?? ctx?.formPath?.formId
    const panelId = ctx?.panelId ?? ctx?.formPath?.panelId
    const element = ctx?.element
    const onChangeScript = pickElementEventScript(element, 'onChange')
    if (!onChangeScript) return

    const type = (element?.type?._text || '').toUpperCase();
    if (type === 'TEXT' || type === 'PASSWORD' || type === 'TEXTAREA') return;

    // Ensure form and panel are registered before proceeding
    if (formId && !forms.value[formId]) {
      console.warn(`Form ${formId} not registered, registering now...`);
      registerForm(formId);
    }
    if (formId && panelId && !forms.value[formId]?.[panelId]) {
      console.warn(`Panel ${panelId} not registered in form ${formId}, registering now...`);
      registerPanel({ formId, panelId });
    }

    const pageDataSlot = String(
      ctx?.pageDataSlot ?? ctx?.formPath?.pageDataSlot ?? getCurrentPageSlotId() ?? '',
    ).trim()
    const contextPageData = ctx?.pageData
    const resolvedPageData = formId != null ? findPageDataByFormId(formId) : null
    const hasExplicitPageData =
      contextPageData != null &&
      typeof contextPageData === 'object' &&
      (contextPageData.form || contextPageData.uniData || contextPageData.jsResult)
    const currentPageData =
      hasExplicitPageData
        ? contextPageData
        : resolvedPageData ??
          appStore.pagesData?.[formId] ??
          appStore.pagesData?.[String(formId)] ??
          appStore.pagesData?.[Number(formId)] ??
          (pageDataSlot ? appStore.pagesData?.[pageDataSlot] : null) ??
          contextPageData

    const pageUniDataFields = currentPageData?.uniData?.fields?.fields || {}
    const pageChangedFields = resolveJsResultChangedDataFieldMap(
      currentPageData?.jsResult?.changed_data,
    )
    const panelFormData =
      getForm({ formId, panelId, pageDataSlot: pageDataSlot || undefined }) || {}

    const mergedElementData = mergeLiveFieldOverridesIntoTypedFormData(
      mergeFieldMaps(
        pageUniDataFields,
        pageChangedFields,
        panelFormData,
        ctx.elementForm || {},
        { shouldPreserveEmptyIncoming: () => true },
      ),
      collectLiveFieldOverrides(formId, elements.value, {
        extraMaps: [elementsByKey.value],
      }),
    )

    // Scope-enforcement (Swing parity): для element-event handler'а (onChange,
    // onFocusLost и т. п. на listbox/combobox/checkbox/etc) en.data должен
    // содержать только данные этого элемента (`name`, `name__*`, `name.*`).
    // Иначе при `en.reloadElement(...)` из onChange-скрипта в payload протекают
    // все поля формы (другие input'ы панели, soup из pagesData). Для cross-scope
    // данных скрипт обязан явно вызвать `en.putData(field)` / `en.putFullData(...)`.
    const elementFieldName = String(
      (element as { name?: { _text?: unknown } | unknown })?.name != null &&
        typeof (element as { name?: unknown }).name === 'object'
        ? ((element as { name: { _text?: unknown } }).name._text ?? '')
        : ((element as { name?: unknown })?.name ?? '')
    ).trim()
    const scopedElementData: Record<string, unknown> = elementFieldName
      ? Object.fromEntries(
          Object.entries(mergedElementData).filter(([k]) =>
            isElementScopedFieldKey(k, elementFieldName),
          ),
        )
      : (mergedElementData as Record<string, unknown>)

    en.value!._resetData(scopedElementData);
    en.value!._setPanelId(panelId);
    en.value!._setFormId(formId);
    en.value!._setPageData(currentPageData || {});
    if (pageDataSlot) {
      en.value!._setPageDataSlotId(pageDataSlot);
    }

    await en.value!.run(onChangeScript);

    await awaitPendingElementEvent();
    const properties = en.value?.getProperties?.();
    applyDispatchedVisibilities(properties?.dispatchedVisibilities, {
      formProvider: {
        setVisible: setVisibleRecursive,
        setVisibleByLevel,
        getOverlays: () => overlaysRegistry.value,
      },
      formKeys: [formId, pageDataSlot].filter(Boolean),
    });
    rememberShownOverlayPageIdsFromVisibilities(properties?.dispatchedVisibilities)
    flushPendingOverlayRefills()
    if (properties && 'dispatchedVisibilities' in properties) {
      delete properties.dispatchedVisibilities;
    }
  } catch (error) {
    console.error("Error in onElement:", error);
    console.error("Context:", ctx);
  }
};

const onPlatformNavigation = async (id: number) => {
  const item = rawMenu.value.find((i: any) => +i.branch_id === id);
  const cmd = item?.in_unidata;
  const platform = item?.platform;

  en.value!._resetData();
  en.value!._setFormId(undefined);
  en.value!._setPanelId(undefined);

  if (cmd) {
    await en.value!.run(cmd);
  }

  return await appStore.getPage({
    id: id.toString(),
    fields: { ...en.value!._serialize() },
    platform,
    parentPageId: null,
  });
};

const runActionByName = (name: string) => {
  const actionList = Object.values(actions.value);
  const action = actionList.find((a: any) => a.data.name?._text === name);
  if (!action) {
    return;
  }
  onAction({ getContext: () => action });
};


const verifyForm = (formId: string) => {
  return verifyFormElements(elements.value, String(formId)).valid
}

const findRegisteredActionByName = (name: string, options?: {
  formId?: string
  level?: string
  position?: string
}) => {
  const actionList = Object.values(actions.value)
  const matchesName = (action: any) => {
    const actionName = action?.data?.name?._text ?? action?.name
    return actionName === name
  }

  const matchesScope = (action: any) => {
    const layout = action?.data?.layout
    const actionLevel = layout?.level?._text
    const actionPosition = layout?.position?._text
    const actionFormId = String(action?.formId ?? layout?.id?._text ?? layout?.id ?? '')

    if (options?.level && actionLevel && actionLevel !== options.level) {
      return false
    }
    if (options?.position && actionPosition && actionPosition !== options.position) {
      return false
    }
    if (options?.formId && actionFormId && actionFormId !== options.formId) {
      return false
    }
    return true
  }

  return actionList.find((action: any) => matchesName(action) && matchesScope(action))
    ?? actionList.find((action: any) => matchesName(action))
}

const runFormEvent = (ctx: {
  formId?: string
  eventName: string
  data?: any
  verify?: boolean
}) => {
  if (!ctx?.eventName) {
    return
  }

  const formId = String(ctx.formId ?? getCurrentRegistryFormId() ?? '')
  const selectedData = ctx.data?.selectedData ?? ctx.data?.selected ?? ctx.data

  en.value?._setActionData(toActionData(ctx.data))

  const action = findRegisteredActionByName(ctx.eventName, {
    formId,
    level: 'form',
    position: 'events',
  })

  if (action) {
    onAction({
      getContext: () => ({
        ...action,
        formId,
        selectedData,
        verify: ctx.verify === true,
      }),
    })
    return
  }

  runActionByName(ctx.eventName)
}

const invokeBusEvent = (eventName: string, data?: any) => {
  if (!eventName) {
    return
  }

  serverEventRegistry.handleEvent({
    type: eventName,
    data: toActionData(data),
    timestamp: Date.now(),
  })
}

const runAction = async (ctx: any) => {
  /** @legacy делегирует в `onAction`; предпочтителен `actionService.executeAction`. */
  const hasId = ctx.data?._attributes?.id ?? ctx.data?.id ?? ctx.elementId
  if (!ctx.data || !hasId) {
    console.warn("Unable to run action! ", ctx);
    return;
  }

  if (ctx.level === 'page' && ctx.eventName === 'onClick') {
    const pageIdRaw = ctx.pageId ?? ctx.data?._attributes?.id ?? ctx.data?.id
    const pageId = parseInt(String(pageIdRaw ?? ''), 10)
    const formId = ctx.formId != null ? String(ctx.formId) : ''
    const scriptBodyRaw = ctx.data?.onClick?._text ?? ctx.data?.onClick
    const scriptBody = typeof scriptBodyRaw === 'string' ? scriptBodyRaw.trim() : ''
    if (!formId || !Number.isFinite(pageId) || !scriptBody) return

    const pageData = findPageDataByFormId(formId) || appStore.pagesData[formId]
    if (!pageData?.sessionId) {
      console.warn('No sessionId available for page onClick action execution', {
        formId,
        pageId,
        hasPageData: !!pageData,
      })
      return
    }

    const pageStoreKey = resolvePagesDataStoreKey(appStore.pagesData as Record<string, any>, {
      pageDataRef: pageData,
      pageDataSlot: getCurrentPageSlotId(),
      registryFormId: formId,
      formId,
      currentPageId: appStore.currentPageId,
      topDialogId: appStore.dialogIds?.length
        ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
        : '',
    })
    const resolvedSlot = pageStoreKey || getCurrentPageSlotId() || formId
    const slotPageData = (appStore.pagesData as Record<string, any>)[resolvedSlot] || pageData

    const { actionService } = await import('@/services/action/action.service')
    const { actionScript } = await import('@/services/action/actionScript')
    const { shouldExecuteOnClickOnClient } = await import('@/services/action/actionScriptRuntime')
    const { ScriptName } = await import('@/services/websocket/types')
    const { INITIAL_JS_RESULT_PROPERTIES } = await import('@/services/action/types')
    const { MiddleExecutionRequiredError } = await import('@/services/parse/MiddleExecutionRequiredError')

    // SwingPage.getData: only children of this page, not the whole form UniData.
    const formRoot = (slotPageData as Record<string, unknown> | undefined)?.form
    const snapshot = filterLayoutScopedFieldsToPanels(
      resolveLayoutScopedClientFields(slotPageData, formRoot),
      formRoot,
      collectPagePanelIds(formRoot, pageId),
    )
    const currentDataRef = {
      value: createSafeClientData({
        fields: {
          fields: snapshot,
          fieldAttributes: {},
          errors: {},
          attributes: {},
        },
      }),
    }
    const jsResultPropertiesRef = { value: { ...INITIAL_JS_RESULT_PROPERTIES } }

    const onClickScriptData =
      ctx.data?.onClick && typeof ctx.data.onClick === 'object'
        ? ctx.data.onClick
        : { _text: scriptBody }

    const actionContext = {
      id: String(pageId),
      formId,
      pageId: String(pageId),
      level: 'page' as const,
      data: ctx.data,
      platform: currentPlatform.value || undefined,
      onClick: scriptBody,
    }

    const dependencies = {
      appStore,
      router,
      getForm,
      getPageData,
      formStates: formStatesResult,
      formProvider,
      pageData: slotPageData,
      pageDataSlot: resolvedSlot,
      pageRegistryFormId: formId,
      pageWindowInstanceId: resolvedSlot,
      openUrls: processOpenUrls,
      reloadElement: reloadElementViaControls,
      refillElement: refillElementViaControls,
    } as any

    const onClickImportPreflight = {
      clientImportNames: Object.keys(appStore?.imports ?? {}),
      serverOnlyImportNames: appStore?.serverOnlyImportNames ?? [],
    }

    const runOnClickOnMiddle = async () => {
      await actionScript.executeMiddleCode(
        ScriptName.ON_CLICK,
        scriptBody,
        actionContext as any,
        currentDataRef,
        jsResultPropertiesRef,
        appStore,
        actionService.invokeNestedElementActionFromClientScript.bind(actionService),
        dependencies,
      )
    }

    try {
      if (shouldExecuteOnClickOnClient(actionContext as any, onClickScriptData, onClickImportPreflight)) {
        try {
          const updatedChangedData = await actionScript.executeClientCode(
            scriptBody,
            actionContext as any,
            appStore,
            currentDataRef.value,
            actionService.invokeNestedElementActionFromClientScript.bind(actionService),
            dependencies,
          )
          if (updatedChangedData) {
            currentDataRef.value = updatedChangedData
            jsResultPropertiesRef.value = actionScript.extractJsResultProperties(updatedChangedData)
            actionScript.applyChangedDataToAppStore(
              updatedChangedData,
              actionContext as any,
              appStore,
              dependencies,
            )
          }
        } catch (clientError) {
          if (clientError instanceof MiddleExecutionRequiredError) {
            await runOnClickOnMiddle()
          } else {
            throw clientError
          }
        }
      } else {
        await runOnClickOnMiddle()
      }
      actionScript.applyDispatchedVisibilitiesFromProperties(
        jsResultPropertiesRef.value.dispatched_visibilities,
        actionContext as any,
        dependencies,
      )
    } catch (error: unknown) {
      console.error('[GlobalProvider] page onClick script failed:', error)
      const { useNotification } = await import('@/common/composables/useNotification')
      useNotification().error({
        summary: t('platform.error'),
        detail: t('platform.tabActionFailed'),
        life: 6000,
      })
    }
    return
  }

  if (ctx.level === "element" && (ctx.eventName === "onChange" || ctx.eventName === "onSelectionChanged")) {
    const hasActionOnClick =
      typeof ctx?.data?.onClick?._text === 'string' && ctx.data.onClick._text.trim() !== ''
    const isEventPositionAction = String(ctx?.data?.layout?.position?._text ?? '').toLowerCase() === 'events'

    if (hasActionOnClick && isEventPositionAction) {
      await onAction({ getContext: () => ctx })
      return
    }
    // Check if onSelectionChanged should execute as a full action (open document in split)
    const isOnSelectionChangedWithAction =
      ctx.eventName === "onSelectionChanged" &&
      (ctx.data?.doActionForAdjacentWindow?._text === 'true' ||
        ctx.data?.doActionForAdjacentWindow?._text === '1') &&
      (ctx.data?.transferType?._text || ctx.data?.transfer?._text)

    // If onSelectionChanged has doActionForAdjacentWindow + transfer, execute via actionService (for split mode)
    if (isOnSelectionChangedWithAction) {
      const { actionService } = await import('@/services/action/action.service')

      // Extract selected data - it might be an array of rows
      const selectedRows = ctx.selectedData || []
      const firstSelectedRow = Array.isArray(selectedRows) ? selectedRows[0] : selectedRows

      // Use current page as parent for split - this is the list we're opening the document from
      const parentPageId = appStore.currentPageId && appStore.currentPageId !== 'home'
        ? appStore.currentPageId
        : ctx.formId?.toString()

      const actionContext = {
        id: ctx.data?._attributes?.id ?? ctx.data?.id ?? ctx.elementId ?? '0',
        name: ctx.data?.name?._text || ctx.data?.description?._text || 'onSelectionChanged',
        formId: ctx.formId?.toString(),
        elementId: ctx.elementId?.toString(),
        panelId: ctx.panelId?.toString(),
        level: 'element' as const,
        data: ctx.data,
        platform: currentPlatform.value || undefined,
        doActionForAdjacentWindow: true,
        windowType: ctx.data?.window__type?._text || 'SAME_WINDOW',
        transfer: ctx.data?.transfer?._text || '0',
        transferType: ctx.data?.transferType?._text || 'document',
        onClick: ctx.data?.onClick?._text,
        selectedData: firstSelectedRow,
        parentPageId,
      }

      await actionService.executeAction(actionContext, {
        appStore,
        router,
        actions: tempActions,
        getForm,
        getPageData,
        getTableSelection,
        getTableSelections,
        formStates: formStatesResult,
        pageData: findPageDataByFormId(ctx.formId?.toString() ?? '') ?? appStore.pagesData[parentPageId],
        pageDataSlot: String(parentPageId ?? ''),
        pageRegistryFormId: String(ctx.formId ?? ''),
        pageWindowInstanceId: String(parentPageId ?? ''),
      })
      return
    }

    // Otherwise, execute only the script
    const { scriptService } = await import('@/services/script/script.service')
    const { ScriptLevel, ScriptName } = await import('@/services/websocket/types')

    // Use elementId from formPath
    const elementId = ctx.elementId ? parseInt(ctx.elementId.toString()) : parseInt(ctx.data._attributes.id)
    const formId = ctx.formId
    const panelId = ctx.panelId
    const requestedPageSlotId = String(ctx.pageSlotId ?? '').trim()
    const pageDataRef = findPageDataByFormId(formId)
    const resolvedPageSlotId =
      resolvePagesDataStoreKey(appStore.pagesData as Record<string, any>, {
        pageDataRef: pageDataRef ?? undefined,
        pageDataSlot: requestedPageSlotId || getCurrentPageSlotId(),
        registryFormId: formId,
        formId,
        currentPageId: appStore.currentPageId,
        topDialogId: appStore.dialogIds?.length
          ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
          : '',
      }) || requestedPageSlotId || getCurrentPageSlotId() || String(formId)
    const pageData =
      (resolvedPageSlotId && (appStore.pagesData as Record<string, any>)[resolvedPageSlotId]) ||
      pageDataRef ||
      appStore.pagesData[formId]

    if (!pageData?.sessionId) {
      console.warn("No sessionId available for element onChange script execution", {
        formId,
        panelId,
        elementId,
        hasPageData: !!pageData,
        storeKeys: Object.keys(appStore.pagesData || {})
      })
      return
    }

    // Текущие данные формы: page snapshot + panel slot + live TEXT overrides (как в onElement / reload).
    const getFormDataForScript = () => {
      const pageSnapshot = mergePageDataFieldSnapshot(pageData)
      const pageChangedFields = resolveJsResultChangedDataFieldMap(pageData?.jsResult?.changed_data)
      const panelData = getForm({
        formId,
        panelId,
        flatten: false,
        pageDataSlot: resolvedPageSlotId,
      })
      const panelFormData: Record<string, any> = {}
      if (panelData && typeof panelData === 'object') {
        Object.keys(panelData).forEach(key => {
          const field = panelData[key]
          if (field && typeof field === 'object' && 'value' in field) {
            panelFormData[key] = field
          } else {
            panelFormData[key] = {
              value: field,
              type: typeof field === 'string' ? 'String' :
                typeof field === 'number' ? 'Long' :
                  typeof field === 'boolean' ? 'Boolean' : 'String'
            }
          }
        })
      }
      const mergedFields = mergeLiveFieldOverridesIntoTypedFormData(
        mergeFieldMaps(
          pageSnapshot,
          pageChangedFields,
          panelFormData,
          { shouldPreserveEmptyIncoming: () => true },
        ),
        collectLiveFieldOverrides(formId, elements.value, {
          extraMaps: [elementsByKey.value],
        }),
      )
      return mergedFields
    }

    const normalizeFieldsForScript = (fields: Record<string, any>): Record<string, any> => {
      const out: Record<string, any> = {}
      Object.keys(fields).forEach(key => {
        const entry = fields[key]
        if (!entry || typeof entry !== 'object') {
          out[key] = entry
          return
        }
        const promoted = tryPromoteStringWrappedUniDataTable(entry)
        if (promoted) {
          out[key] = promoted
          return
        }
        const type = entry.type || 'String'
        let value = entry.value
        if (type === 'String' && value != null && typeof value === 'object') {
          if (typeof value.value === 'string') value = value.value
          else if (typeof value.value === 'number' || typeof value.value === 'boolean') value = String(value.value)
          else value = ''
        }
        out[key] = { ...entry, value }
      })
      return out
    }

    let formData = getFormDataForScript()
    formData = normalizeFieldsForScript(formData)
    // Для onChange: подмешиваем значение из события (текст по Enter), т.к. оно может ещё не быть в store.
    // EDITABLE/TABLE: selectedData — контекст строки (fields + __rowNum/__column), не замена всей таблицы.
    if (ctx.eventName === 'onChange' && ctx.selectedData !== undefined && ctx.selectedData !== null && ctx.data) {
      const sd = ctx.selectedData as Record<string, unknown>
      const rowFields = sd.fields as Record<string, unknown> | undefined
      if (rowFields && typeof rowFields === 'object') {
        for (const [key, field] of Object.entries(rowFields)) {
          if (field && typeof field === 'object') {
            formData[key] = field as Record<string, unknown>
          }
        }
      } else {
        const fieldName = ctx.data?.name?._text ?? ctx.data?._attributes?.name ?? ctx.data?.name
        if (fieldName) {
          const key = typeof fieldName === 'string' ? fieldName : String(fieldName)
          const fieldPatch =
            sd != null &&
            typeof sd === 'object' &&
            !Array.isArray(sd) &&
            typeof (sd as { type?: unknown }).type === 'string' &&
            'value' in sd
              ? sd
              : setType(sd)
          formData = { ...formData, [key]: fieldPatch }
        }
      }
    }
    if (ctx.eventName === 'onSelectionChanged' && ctx.selectedData) {
      const sd = ctx.selectedData as Record<string, unknown>
      const rowFields = sd.fields as Record<string, unknown> | undefined
      if (rowFields && typeof rowFields === 'object') {
        for (const [key, field] of Object.entries(rowFields)) {
          if (field && typeof field === 'object') {
            formData[key] = field as Record<string, unknown>
          }
        }
      }
    }
    if (ctx.selectedData) {
      en.value?._setActionData(toActionData({ selectedData: ctx.selectedData }))
    }
    const clientData = createSafeClientData({
      fields: {
        fields: formData,
        fieldAttributes: {},
        errors: {},
        attributes: {}
      },
      clientData: {
        elementConditions: null,
        reloadActions: null,
        params: {},
        doActionId: 0,
        openPage: 0
      },
      formError: null
    })

    const scriptName = ctx.eventName === "onChange"
      ? ScriptName.ON_CHANGE
      : ctx.eventName === "onSelectionChanged"
        ? ScriptName.ON_SELECTION_CHANGED
        : ScriptName.ON_CHANGE // fallback

    const elementEventScriptBody =
      ctx.eventName === 'onSelectionChanged'
        ? pickElementEventScript(ctx.data, 'onSelectionChanged')
        : pickElementEventScript(ctx.data, 'onChange')

    /** Без текста скрипта на клиенте не дергаем сервер */
    if (!elementEventScriptBody) {
      return
    }

    const elementScriptEventTag =
      ctx.eventName === 'onSelectionChanged' ? 'onSelectionChanged' : 'onChange'
    const elementScriptNode = pickElementEventScriptNode(ctx.data, elementScriptEventTag)
    const runElementScriptOnClient =
      ctx.eventName === 'onChange'
        ? shouldExecuteElementOnChangeOnClient(elementScriptNode, elementEventScriptBody, {
            clientImportNames: Object.keys(appStore.imports ?? {}),
            serverOnlyImportNames: appStore.serverOnlyImportNames ?? [],
          }, readRootFormFunctions(pageData?.form))
        : getWebClientExecFromScriptNode(elementScriptNode)

    const applyElementScriptRunSideEffects = async () => {
      await awaitPendingElementEvent()
      const properties = en.value?.getProperties?.()
      applyDispatchedVisibilities(properties?.dispatchedVisibilities, {
        formProvider: {
          setVisible: setVisibleRecursive,
          setVisibleByLevel,
          getOverlays: () => overlaysRegistry.value,
        },
        formKeys: [formId, resolvedPageSlotId].filter(Boolean),
      })
      rememberShownOverlayPageIdsFromVisibilities(properties?.dispatchedVisibilities)
      flushPendingOverlayRefills()
      if (properties && 'dispatchedVisibilities' in properties) {
        delete properties.dispatchedVisibilities
      }
    }

    const runElementClientScript = async (scriptBody: string) => {
      en.value!._setFormId(formId)
      en.value!._setPanelId(panelId)
      en.value!._setPageData(pageData)
      en.value!._setPageDataSlotId(resolvedPageSlotId)
      en.value!._resetData(formData)
      if (ctx.selectedData) {
        en.value!._setActionData(toActionData({ selectedData: ctx.selectedData }))
      }
      await en.value!.run(scriptBody)
      await applyElementScriptRunSideEffects()
    }

    if (runElementScriptOnClient && en.value) {
      try {
        await runElementClientScript(elementEventScriptBody)
        return
      } catch (runErr: unknown) {
        console.error('[runAction] Client element onChange failed', runErr)
        const { useNotification } = await import('@/common/composables/useNotification')
        useNotification().error({
          summary: t('platform.error'),
          detail: t('platform.actionFailed'),
          life: 6000,
        })
        return
      }
    }

    let scriptResponse: any
    try {
      scriptResponse = await scriptService.executeScript(
        {
          sessionId: pageData.sessionId,
          currentData: clientData,
          elementId: elementId,
          level: ScriptLevel.ELEMENT,
          scriptName: scriptName,
          scriptBody: elementEventScriptBody,
        },
        en.value ?? undefined,
      )
    } catch (error: any) {
      if (error?.response?.status === 400) {
        console.warn('Element onChange script returned 400, falling back to action execution')
        return
      }
      console.error('Error executing element onChange script:', error)
      const { useNotification } = await import('@/common/composables/useNotification')
      useNotification().error({
        summary: t('platform.error'),
        detail: t('platform.actionFailed'),
        life: 6000
      })
      return
    }

    let responseData = scriptResponse?.data || scriptResponse
    const serverScriptNotFound =
      responseData &&
      typeof responseData === 'object' &&
      Number((responseData as { code?: unknown }).code) === 2001
    if (serverScriptNotFound && en.value) {
      try {
        await runElementClientScript(elementEventScriptBody)
        responseData = undefined
      } catch (runErr: unknown) {
        console.error('[runAction] Client en.run after 2001 failed', runErr)
        const { useNotification } = await import('@/common/composables/useNotification')
        useNotification().error({
          summary: t('platform.error'),
          detail: t('platform.actionFailed'),
          life: 6000,
        })
        return
      }
    } else if (scriptResponse?.client_script && en.value) {
      await applyElementScriptRunSideEffects()
    }

    const effectiveChangedData = responseData?.changed_data || responseData?.fields?.changed_data
    const effectiveStates = normalizeExchangeStates(responseData)
    const currentPageData =
      (resolvedPageSlotId && (appStore.pagesData as Record<string, any>)[resolvedPageSlotId]) ||
      findPageDataByFormId(formId) ||
      appStore.pagesData[String(formId)]

    const resolvedSlot =
      resolvePagesDataStoreKey(appStore.pagesData as Record<string, any>, {
        pageDataRef: currentPageData,
        pageDataSlot: resolvedPageSlotId || getCurrentPageSlotId(),
        registryFormId: formId,
        formId,
        currentPageId: appStore.currentPageId,
        topDialogId: appStore.dialogIds?.length
          ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
          : '',
      }) || resolvedPageSlotId || getCurrentPageSlotId() || String(formId)

    applyOpenSuggesterFromStates(effectiveStates, openSuggesterElement, formId)

    const freshPageDataBeforeCommit =
      (resolvedPageSlotId && (appStore.pagesData as Record<string, any>)[resolvedPageSlotId]) ||
      currentPageData

    await commitExchangeResult({
      appStore,
      slotId: resolvedSlot,
      registryFormId: formId,
      states: effectiveStates,
      changedData: effectiveChangedData ?? responseData?.changed_data,
      processStates: formStatesResult.processStates,
      setPageDataSource: 'GlobalProvider.runAction.elementOnChange',
      responseForStateMerge: responseData ?? undefined,
      resolveStoreKey: {
        originSlotId: getCurrentPageSlotId(),
        pageDataSlot: getCurrentPageSlotId(),
        currentPageId: appStore.currentPageId,
        topDialogId: appStore.dialogIds?.length
          ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
          : '',
        responseSchemaFormId: resolveResponseSchemaFormId(responseData),
      },
      jsResultExtras:
        effectiveStates.length > 0 || (responseData?.jsResult && typeof responseData.jsResult === 'object')
          ? {
              ...(responseData?.jsResult && typeof responseData.jsResult === 'object'
                ? responseData.jsResult
                : {}),
              changed_data:
                effectiveChangedData ??
                responseData?.jsResult?.changed_data ??
                freshPageDataBeforeCommit?.jsResult?.changed_data,
              event_listeners: mergeEventListenersForPageUpdate(
                freshPageDataBeforeCommit?.jsResult?.event_listeners,
                responseData?.jsResult?.event_listeners,
              ),
            }
          : undefined,
    })

    const scrollToPayload = responseData?.scroll_to
    if (Array.isArray(scrollToPayload) && scrollToPayload.length > 0 && scrollToPayload[0]?.name) {
      scrollToElement(scrollToPayload[0].name)
    }

    return
  }

  // Regular action execution (await — иначе runAction.resolve раньше procedure/chain/reloadNode)
  await onAction({ getContext: () => ctx })
}

// Выполнить событие?
// TODO: mn.rumynin - не уверен
let pendingElementEventPromise: Promise<unknown> | null = null

const trackPendingElementEvent = (promise: Promise<unknown> | void | undefined) => {
  if (promise && typeof (promise as Promise<unknown>).then === 'function') {
    pendingElementEventPromise = promise as Promise<unknown>
  }
}

const awaitPendingElementEvent = async () => {
  const pending = pendingElementEventPromise
  if (!pending || typeof pending.then !== 'function') return
  try {
    await pending
  } finally {
    pendingElementEventPromise = null
  }
}

const syncEnContextForScript = (ctx: {
  formId?: string | number
  panelId?: string | number
  pageSlotId?: string
}) => {
  if (!en.value) return ''
  const formId = ctx.formId != null ? String(ctx.formId) : ''
  const pageDataRef = formId ? findPageDataByFormId(formId) : null
  const pageStoreKey =
    resolvePagesDataStoreKey(appStore.pagesData as Record<string, any>, {
      pageDataRef: pageDataRef ?? undefined,
      pageDataSlot: ctx.pageSlotId || getCurrentPageSlotId(),
      registryFormId: formId,
      formId,
      currentPageId: appStore.currentPageId,
      topDialogId: appStore.dialogIds?.length
        ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
        : '',
    }) || ctx.pageSlotId || formId
  const pageData =
    (pageStoreKey && appStore.pagesData[pageStoreKey]) ||
    pageDataRef ||
    (formId ? appStore.pagesData[formId] : null) ||
    {}
  en.value._setFormId(formId)
  en.value._setPanelId(ctx.panelId)
  en.value._setPageData(pageData)
  en.value._setPageDataSlotId(pageStoreKey)
  const snapshot = mergePageDataFieldSnapshot(pageData)
  const pageChangedFields = resolveJsResultChangedDataFieldMap(pageData?.jsResult?.changed_data)
  const merged = mergeLiveFieldOverridesIntoTypedFormData(
    mergeFieldMaps(snapshot, pageChangedFields, { shouldPreserveEmptyIncoming: () => true }),
    collectLiveFieldOverrides(formId, elements.value, {
      pageDataSlot: pageStoreKey,
      extraMaps: [elementsByKey.value],
    }),
  )
  if (Object.keys(merged).length > 0) {
    en.value._resetData(merged)
  }
  return pageStoreKey
}

const runEvent = async (cmd: string, ctx: any) => {
  if (!cmd) return;
  const { formId, panelId, pageSlotId } = ctx;
  const storeKey = syncEnContextForScript({ formId, panelId, pageSlotId });

  const selectedData = ctx?.selectedData ?? ctx?.context?.selectedData ?? ctx?.context?.selected ?? null;
  if (selectedData) {
    en.value!._setActionData({ selected: selectedData });
  }

  try {
    return await en.value!.run(cmd);
  } finally {
    // Apply visibility before pending element events: refill/remount must not race
    // with panel→child re-propagate from the replay queue.
    const properties = en.value?.getProperties?.()
    const dispatched = properties?.dispatchedVisibilities
    applyDispatchedVisibilities(dispatched, {
      formProvider: {
        setVisible: setVisibleRecursive,
        setVisibleByLevel,
        getOverlays: () => overlaysRegistry.value,
      },
      formKeys: [formId, storeKey].filter(Boolean),
    })
    rememberShownOverlayPageIdsFromVisibilities(dispatched)
    flushPendingOverlayRefills()
    const persistPage = storeKey
      ? (appStore.pagesData[storeKey] as Record<string, unknown> | undefined)
      : undefined
    const visibilityPatches = visibilityPatchesFromDispatched(
      dispatched,
      persistPage?.form,
      persistPage,
      storeKey,
    )
    if (visibilityPatches.length > 0 && storeKey) {
      persistVisibilityStatePatchesToPageStore(
        appStore,
        storeKey,
        visibilityPatches,
        'GlobalProvider.runEvent.dispatchedVisibilities',
      )
    }
    if (properties && 'dispatchedVisibilities' in properties) {
      delete properties.dispatchedVisibilities
    }
    await awaitPendingElementEvent()
  }
};


/**
 * Выполняет произвольный скрипт En в заданном контексте формы/слота/панели.
 * WUI-408: для Tiles inlineAfterLoad — tilesRowScope + snapshot полей формы, затем overlay строки.
 */
const runCode = async (
  cmd: string,
  selectedData?: any,
  context?: {
    formId?: string | number
    pageDataSlot?: string
    panelId?: string | number
    tilesRowScope?: SwingTilesRowScopeLike | null
    preloadPageFields?: boolean
  },
) => {
  if (!cmd) return;

  const resolvedSelected = resolveInvokeEventSelectedData(selectedData) ?? selectedData
  const slotId = String(context?.pageDataSlot ?? getCurrentPageSlotId() ?? '').trim()
  const formIdRaw = String(context?.formId ?? getCurrentRegistryFormId() ?? '').trim()

  if (slotId) {
    en.value!._setPageDataSlotId(slotId)
    en.value!._setPageData((appStore.pagesData as Record<string, unknown>)?.[slotId] ?? {})
  }
  if (formIdRaw) {
    const numeric = parseInt(formIdRaw, 10)
    en.value!._setFormId(!Number.isNaN(numeric) && String(numeric) === formIdRaw ? numeric : formIdRaw)
  } else {
    en.value!._setFormId(undefined)
  }
  if (context?.panelId != null && String(context.panelId).trim() !== '') {
    const panelNumeric = parseInt(String(context.panelId), 10)
    en.value!._setPanelId(
      !Number.isNaN(panelNumeric) && String(panelNumeric) === String(context.panelId)
        ? panelNumeric
        : context.panelId,
    )
  } else {
    en.value!._setPanelId(undefined)
  }

  en.value!._setTilesRowScope(context?.tilesRowScope ?? null)

  // Только по явному флагу (Tiles inlineAfterLoad), чтобы не менять остальные runCode-вызовы.
  if (context?.preloadPageFields === true && slotId) {
    const pageEntry = (appStore.pagesData as Record<string, unknown>)?.[slotId]
    if (pageEntry && typeof pageEntry === 'object') {
      const snapshot = mergePageDataFieldSnapshot(pageEntry)
      if (Object.keys(snapshot).length > 0) {
        en.value!._addData(snapshot)
      }
    }
  }

  // Row fields after page snapshot so statusId строки не затирается form-level statusId.
  // `_setActionData` при tilesRowScope — только PK (action 27534). Для inlineAfterLoad
  // нужны поля строки (statusId=rawDraft), иначе 2573 показывает «Просмотр» вместо «Заполнить».
  en.value!._setActionData(toActionData({ selectedData: resolvedSelected }))
  if (context?.tilesRowScope && resolvedSelected) {
    const rowOverlay = overlayTilesInlineAfterLoadRowFields({}, resolvedSelected)
    if (Object.keys(rowOverlay).length > 0) {
      en.value!._addData(rowOverlay)
    }
  }

  // Load panel data when panelId is provided 
  // IMPORTANT: Must be AFTER _setActionData to avoid data being overwritten
  if (context?.panelId != null && formIdRaw && slotId) {
    const panelData = getForm({
      formId: formIdRaw,
      panelId: context.panelId,
      flatten: true,
      pageDataSlot: slotId,
    })

    if (panelData && typeof panelData === 'object') {
      const panelFields: Record<string, any> = {}
      for (const [key, value] of Object.entries(panelData)) {
        if (value && typeof value === 'object' && 'value' in value) {
          panelFields[key] = value
        }
      }
      if (Object.keys(panelFields).length > 0) {
        en.value!._addData(panelFields)
      }
    }
  }

  try {
    return await en.value!.run(cmd)
  } finally {
    en.value!._setTilesRowScope(null)
  }
};

const setActionData = (data?: any) => {
  en.value?._setActionData(toActionData(data))
}

type ReloadElementPath = {
  elementId: number | string
  formId: string | number
  panelId?: string | number
  sessionId?: string | number
  type?: string
}

type ReloadElementQueueEntry = {
  promise: Promise<any>
  resolve: (value: any) => void
  reject: (reason?: any) => void
  timer: ReturnType<typeof setTimeout>
  path: ReloadElementPath
  payload: any
  payloadKey: string
  inFlight: boolean
}

const reloadElementQueue = new Map<string, ReloadElementQueueEntry>()

const stableReloadPayloadKey = (value: any): string => {
  if (value == null || typeof value !== 'object') return JSON.stringify(value ?? null)
  const seen = new WeakSet<object>()
  const normalize = (input: any): any => {
    if (input == null || typeof input !== 'object') return input
    if (seen.has(input)) return '[Circular]'
    seen.add(input)
    if (Array.isArray(input)) return input.map(normalize)
    return Object.keys(input).sort().reduce((acc: Record<string, any>, key) => {
      acc[key] = normalize(input[key])
      return acc
    }, {})
  }
  try {
    return JSON.stringify(normalize(value))
  } catch {
    return ''
  }
}

const reloadElementKey = (path: ReloadElementPath) => [
  path.sessionId ?? '',
  path.formId ?? '',
  path.elementId ?? '',
].join('|')

const queuedReloadElement = (path: ReloadElementPath, payload: any) => {
  const key = reloadElementKey(path)
  const payloadKey = stableReloadPayloadKey(payload)
  // Recent-completed cache отключён: после procedure payload часто тот же, БД — нет.

  const existing = reloadElementQueue.get(key)
  if (existing) {
    if (existing.inFlight) {
      return existing.promise
    }
    existing.path = path
    existing.payload = payload
    existing.payloadKey = payloadKey
    clearTimeout(existing.timer)
    existing.timer = setTimeout(() => {
      existing.inFlight = true
      void coreService.reloadElement(existing.path as any, existing.payload)
        .then((result) => {
          existing.resolve(result)
        })
        .catch(existing.reject)
        .finally(() => {
          reloadElementQueue.delete(key)
        })
    }, 75)
    return existing.promise
  }

  let resolve!: (value: any) => void
  let reject!: (reason?: any) => void
  const promise = new Promise<any>((res, rej) => {
    resolve = res
    reject = rej
  })
  const entry: ReloadElementQueueEntry = {
    promise,
    resolve,
    reject,
    path,
    payload,
    payloadKey,
    inFlight: false,
    timer: setTimeout(() => {
      entry.inFlight = true
      void coreService.reloadElement(entry.path as any, entry.payload)
        .then((result) => {
          entry.resolve(result)
        })
        .catch(entry.reject)
        .finally(() => {
          reloadElementQueue.delete(key)
        })
    }, 75),
  }
  reloadElementQueue.set(key, entry)
  return promise
}

const resolveRegisteredElementForReload = (elementName: string, actionContext?: any) => {
  const name = String(elementName ?? '').trim()
  if (!name) return null

  const fromPrimary = resolveRegisteredElementForReloadFromPool(
    elements.value as Record<string, any>,
    name,
    actionContext,
  )
  if (fromPrimary) return fromPrimary

  const fromByKey = resolveRegisteredElementForReloadFromPool(
    elementsByKey.value as Record<string, any>,
    name,
    actionContext,
  )
  if (fromByKey) return fromByKey

  // Soft fallback: first reload-capable match by field name (ignore form/slot).
  const direct = elements.value[name]
  if (direct && typeof (direct as any).reload === 'function') return direct

  let soft: any = null
  const pools = [elements.value, elementsByKey.value]
  for (const pool of pools) {
    for (const el of Object.values(pool)) {
      if (!el || typeof el !== 'object') continue
      const field = String((el as any).fieldName ?? (el as any).name ?? '').trim()
      if (field !== name) continue
      if (typeof (el as any).reload !== 'function') continue
      if (!soft) soft = el
    }
  }
  return soft || direct || null
}

const applyReloadElementResultToPageStore = (
  elementName: string,
  result: any,
  actionContext: any,
  elementMeta?: { formId?: any; panelId?: any; elementId?: any; pageDataSlot?: string },
) => {
  if (!result?.fields) return
  const slot =
    String(actionContext?.pageDataSlot ?? actionContext?.formPath?.pageDataSlot ?? '').trim() ||
    String(elementMeta?.pageDataSlot ?? '').trim() ||
    getCurrentPageSlotId()
  const registryFormId = actionContext?.formId ?? elementMeta?.formId ?? slot
  const currentPageData =
    (slot ? appStore.pagesData[slot] : null) || findPageDataByFormId(registryFormId)
  if (!currentPageData) {
    return
  }

  const incoming = result.fields?.fields || {}
  if (Object.keys(incoming).length === 0 && !result.clientData?.params) return

  const authoritative: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(incoming)) {
    if (isUniDataTableEntry(entry)) {
      authoritative[key] = normalizeUniDataTableEmptyFlag(entry)
    }
  }

  let mergedUni = mergeFieldMaps(
    currentPageData.uniData?.fields?.fields,
    incoming,
  ) as Record<string, unknown>
  let mergedChanged = mergeFieldMaps(
    currentPageData.jsResult?.changed_data?.fields?.fields,
    incoming,
  ) as Record<string, unknown>
  if (Object.keys(authoritative).length > 0) {
    mergedUni = overlayAuthoritativeFieldKeys(mergedUni, authoritative)
    mergedChanged = overlayAuthoritativeFieldKeys(mergedChanged, authoritative)
  }

  const updatedPageData = {
    ...currentPageData,
    uniData: {
      ...currentPageData.uniData,
      clientData: {
        ...currentPageData.uniData?.clientData,
        params: {
          ...currentPageData.uniData?.clientData?.params,
          ...(result.clientData?.params || {}),
        },
      },
      fields: {
        ...currentPageData.uniData?.fields,
        fields: mergedUni,
      },
    },
    jsResult: {
      ...(currentPageData.jsResult || {}),
      changed_data: {
        ...(currentPageData.jsResult?.changed_data || {}),
        fields: {
          ...(currentPageData.jsResult?.changed_data?.fields || {}),
          fields: mergedChanged,
        },
      },
    },
    // Иначе mergePageDataFieldSnapshot поднимает stale rows из chain поверх reload.
    currentChainData: (() => {
      const chain = currentPageData.currentChainData
      if (!chain || typeof chain !== 'object') return chain
      const chainFields = chain.fields?.fields
      if (!chainFields || typeof chainFields !== 'object') return chain
      if (Object.keys(authoritative).length === 0) return chain
      return {
        ...chain,
        fields: {
          ...(chain.fields || {}),
          fields: {
            ...chainFields,
            ...authoritative,
          },
        },
      }
    })(),
  }

  writePageDataForForm(
    registryFormId,
    updatedPageData,
    'GlobalProvider.reloadElementViaControls',
    { originSlotId: slot, response: result },
  )

  // TABLE/TREETABLE UI: sync все buckets forms, где уже есть это поле (не только XML path).
  const storageFormId = resolveFormsStorageFormIdForContext(registryFormId, slot)
  for (const [key, entry] of Object.entries(authoritative)) {
    let patchedBuckets = 0
    for (const [fid, panels] of Object.entries(forms.value)) {
      if (!panels || typeof panels !== 'object') continue
      if (
        String(fid) !== String(storageFormId) &&
        String(fid) !== String(registryFormId) &&
        String(fid) !== String(slot)
      ) {
        continue
      }
      for (const [pid, els] of Object.entries(panels as Record<string, unknown>)) {
        if (!els || typeof els !== 'object') continue
        for (const [eid, fields] of Object.entries(els as Record<string, unknown>)) {
          if (!fields || typeof fields !== 'object' || Array.isArray(fields)) continue
          if (!Object.prototype.hasOwnProperty.call(fields, key)) continue
          ;(fields as Record<string, unknown>)[key] = entry
          patchedBuckets += 1
          try {
            setValue({
              formId: fid,
              panelId: pid,
              elementId: eid,
              value: { [key]: entry },
              pageDataSlot: slot,
              skipStore: true,
            })
          } catch {
            /* non-fatal */
          }
        }
      }
    }
    if (patchedBuckets === 0) {
      let panelId =
        key === elementName
          ? elementMeta?.panelId ?? actionContext?.panelId
          : undefined
      let elId = key === elementName ? elementMeta?.elementId : undefined
      if (panelId == null || elId == null) {
        const pageForm = (slot ? appStore.pagesData[slot] : null)?.form
        if (pageForm && typeof pageForm === 'object') {
          for (const pid of Object.keys(pageForm as Record<string, unknown>)) {
            const numericId =
              findNumericElementId(key, String(registryFormId), pid) ||
              findNumericElementId(key, slot, pid)
            if (numericId) {
              panelId = pid
              elId = numericId
              break
            }
          }
        }
      }
      if (panelId != null && elId != null) {
        try {
          setValue({
            formId: storageFormId,
            panelId,
            elementId: elId,
            value: { [key]: entry },
            pageDataSlot: slot,
            skipStore: true,
          })
          patchedBuckets = 1
        } catch {
          /* non-fatal */
        }
      }
    }
  }

  const fieldEntry = incoming[elementName]
  const registered =
    elements.value[elementName] ||
    resolveRegisteredElementForReload(elementName, actionContext)
  if (fieldEntry && registered && typeof registered.refill === 'function') {
    try {
      registered.refill({ [elementName]: fieldEntry })
    } catch {
      /* non-fatal */
    }
  }
}

const reloadElementViaControls = async (elementName: string, data: any, actionContext?: any) => {
  let endMeasure: (() => number) | null = null
  try {
    const rawContextFormId = actionContext?.formId ?? actionContext?.formPath?.formId
    const rawContextPanelId = actionContext?.panelId ?? actionContext?.formPath?.panelId
    const hasValidContextFormId =
      rawContextFormId != null &&
      String(rawContextFormId).trim() !== '' &&
      String(rawContextFormId).trim() !== '-1'
    const hasValidContextPanelId =
      rawContextPanelId != null &&
      String(rawContextPanelId).trim() !== '' &&
      String(rawContextPanelId).trim() !== '-1'

    if (!actionContext || !hasValidContextFormId) {
      actionContext = {
        ...(actionContext || {}),
        level: 'panel',
        formId: getCurrentRegistryFormId(),
        panelId: hasValidContextPanelId ? rawContextPanelId : getDefaultPanelId(),
        platform: actionContext?.platform ?? currentPlatform.value ?? undefined
      };
    } else if (!hasValidContextPanelId) {
      actionContext = {
        ...actionContext,
        panelId: getDefaultPanelId(),
      }
    }

    endMeasure = startWorkflowMeasure('GlobalProvider.reloadElementViaControls', {
      elementName,
      formId: actionContext?.formId ?? actionContext?.formPath?.formId,
      panelId: actionContext?.panelId ?? actionContext?.formPath?.panelId,
      source: actionContext?.source || actionContext?.level || 'unknown',
      payload: profilePayload(data),
    })

    const pageDataSlotOpt =
      String(actionContext?.pageDataSlot ?? actionContext?.formPath?.pageDataSlot ?? '').trim() ||
      ''
    const registeredElement = resolveRegisteredElementForReload(elementName, actionContext)

    const registeredContext =
      typeof registeredElement?.getContext === 'function'
        ? registeredElement.getContext()
        : registeredElement?.ctx
    const registeredPageId =
      registeredContext?.formPath?.pageId ??
      registeredContext?.pageId ??
      registeredElement?.pageId
    const registeredFormId =
      registeredContext?.formPath?.formId ??
      registeredContext?.formId ??
      registeredElement?.formId

    if (registeredPageId != null && registeredFormId != null && !actionContext?.skipPageFocusGuard) {
      const pageData = findPageDataByFormId(String(registeredFormId))
      const pageStatesRaw = pageData?.jsResult?.states || []
      const pageStates = Array.isArray(pageStatesRaw)
        ? pageStatesRaw
        : Object.values(pageStatesRaw)
      const registeredPageKey = normalizePageTabId(registeredPageId)
      const hiddenByState = pageStates.some((state: any) =>
        String(state?.element_level ?? '').toUpperCase() === 'PAGE' &&
        normalizePageTabId(state?.element_id) === registeredPageKey &&
        state?.is_visible === false
      )
      if (hiddenByState) {
        startWorkflowMeasure('GlobalProvider.reloadElement.skipHiddenPage', {
          elementName,
          elementId: registeredElement?.elementId,
          formId: registeredFormId,
          pageId: registeredPageId,
        })()
        return null
      }

      if (shouldApplyPageFocusGuardForReload(registeredPageId, pageData)) {
        const focusedPageState = [...pageStates]
          .reverse()
          .find((state: any) =>
            String(state?.element_level ?? '').toUpperCase() === 'PAGE' &&
            state?.is_focused === true &&
            state?.element_id != null
          ) as any
        const focusedPageKey = normalizePageTabId(focusedPageState?.element_id)
        if (focusedPageKey && focusedPageKey !== registeredPageKey) {
          startWorkflowMeasure('GlobalProvider.reloadElement.skipNonFocusedPage', {
            elementName,
            elementId: registeredElement?.elementId,
            formId: registeredFormId,
            pageId: registeredPageId,
            focusedPageId: focusedPageKey,
          })()
          return null
        }
      }
    }

    if (registeredElement && typeof registeredElement.reload === 'function') {
      return await registeredElement.reload(data, {
        applyResult: actionContext?.applyResult,
        source: actionContext?.source,
        skipPageFocusGuard: actionContext?.skipPageFocusGuard,
        skipOnElement:
          actionContext?.skipOnElement === true ||
          String(actionContext?.source ?? '').startsWith('En.reloadElement'),
        pageDataSlot:
          String(actionContext?.pageDataSlot ?? actionContext?.formPath?.pageDataSlot ?? '').trim() ||
          undefined,
      })
    }

    let collectedData: Record<string, any> = {};

    if (actionContext?.level) {
      // SAME_WINDOW dialog: live page under slot, not pagesData[registryFormId].
      const formId = actionContext.formId?.toString();
      const pageData =
        (pageDataSlotOpt ? appStore.pagesData[pageDataSlotOpt] : null) ||
        (formId ? appStore.pagesData[formId] : null) ||
        findPageDataByFormId(formId)
      const pageFields = pageData?.uniData?.fields?.fields || {};

      // Extract ONLY primitive field values
      const transformedPageFields: Record<string, any> = {};
      Object.keys(pageFields).forEach(key => {
        // Skip any field that contains complex data structures
        const isPaginationKey = key.includes('__page') || key.includes('__maxOnPage')
        const isListboxKey = key.includes('__listbox')
        const isActionKey = key.includes('__action')
        const isSystemField = key.startsWith('$') || key === 'fields' || key === 'ProcessInstances' ||
          key === 'exception' || key === 'clientData' || key === 'hasFormError' ||
          key === 'fieldAttributes' || key === 'errors' || key === 'attributes'

        if (isPaginationKey || isListboxKey || isActionKey || isSystemField) {
          return;
        }

        const fieldData = pageFields[key];

        // Only extract simple primitive values
        if (fieldData && typeof fieldData === 'object' && 'value' in fieldData && 'type' in fieldData) {
          const fieldType = fieldData.type;
          const fieldValue = fieldData.value;

          // Skip UniDataTable types completely
          if (fieldType === 'UniDataTable') {
            return;
          }

          // Only include primitive values
          if (fieldValue === null || fieldValue === undefined) {
            transformedPageFields[key] = fieldValue;
          } else if (typeof fieldValue === 'string' || typeof fieldValue === 'number' || typeof fieldValue === 'boolean') {
            transformedPageFields[key] = fieldValue;
          }
        } else if (fieldData && typeof fieldData === 'object' && 'value' in fieldData) {
          // Handle fields without type property but with value
          const value = fieldData.value;
          if (value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            transformedPageFields[key] = value;
          }
        } else if (fieldData === null || fieldData === undefined || typeof fieldData === 'string' || typeof fieldData === 'number' || typeof fieldData === 'boolean') {
          // Keep primitive values as-is
          transformedPageFields[key] = fieldData;
        }
      });

      collectedData = { ...collectedData, ...transformedPageFields };

    }

    // First, try to find the element in registered elements
    if (registeredElement) {

      try {
        const formId = actionContext?.formId || registeredElement.formId;

        // Find the correct panel where the element is actually located
        let panelId = actionContext?.panelId;
        if (!panelId || panelId.trim() === '') {
          let foundPanelId = null;
          const formData = forms.value[formId];
          if (formData) {
            for (const [currentPanelId, panelData] of Object.entries(formData)) {
              // Search by element name first
              if (panelData && panelData[elementName]) {
                foundPanelId = currentPanelId;
                break;
              }

              // If not found by name, search by element ID
              if (panelData && panelData[registeredElement.elementId]) {
                foundPanelId = currentPanelId;
                break;
              }
            }
          }

          if (foundPanelId) {
            panelId = foundPanelId;
          } else {
            // Fall back to registeredElement.panelId
            panelId = registeredElement.panelId;
          }
        }

        const dateRangeParent = {
          ...collectedData,
          ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {}),
        }
        // Fallback reload без element.reload: всё равно развернуть DateRange до sanitizer.
        applyDateRangeToClientDataFieldsFromRegistry(
          collectedData,
          dateRangeParent,
          elements.value as Record<string, unknown>,
          getForm,
        )
        const cleanedClientData = createMinimalClientData(collectedData);

        const result = await queuedReloadElement(
          {
            elementId: registeredElement.elementId,
            formId: formId,
            panelId: panelId,
            sessionId: actionContext?.formPath?.sessionId || actionContext?.sessionId || (findPageDataByFormId(formId) as any)?.sessionId,
            type: actionContext?.platform || currentPlatform.value || undefined
          },
          cleanedClientData
        );

        if (result && result.fields) {
          const reloadOriginSlotId =
            String(actionContext?.pageDataSlot ?? '').trim() ||
            String(registeredElement?.pageDataSlot ?? '').trim() ||
            pageDataSlotOpt ||
            getCurrentPageSlotId()
          applyReloadElementResultToPageStore(elementName, result, actionContext, {
            formId,
            panelId,
            elementId: registeredElement.elementId,
            pageDataSlot: reloadOriginSlotId,
          })
        } else {
          // Page data update not matched structure
        }

        return result;
      } catch (error) {
        console.error(`Error in registered element section for ${elementName}:`, error);
      }
    }

    const pageFields = appStore.pagesData;
    let elementInfo = null;

    // Search through all forms and panels to find the element
    for (const formId of Object.keys(pageFields)) {
      const formData = pageFields[formId];

      if (formData?.form) {
        for (const panelId of Object.keys(formData.form)) {
          const panelData = formData.form[panelId];
          const numericElementId = findNumericElementId(elementName, formId, panelId);
          if (numericElementId) {
            elementInfo = {
              formId,
              panelId,
              elementId: numericElementId,
              fieldName: elementName
            };
            break;
          }

          if (panelData && typeof panelData === 'object') {
            for (const fieldName of Object.keys(panelData)) {
              // Check for exact match or partial match
              if (fieldName === elementName || fieldName.includes(elementName) || elementName.includes(fieldName)) {
                // Try to find the numeric element ID from the element configuration
                const numericElementId = findNumericElementId(elementName, formId, panelId);
                if (numericElementId) {
                  elementInfo = {
                    formId,
                    panelId,
                    elementId: numericElementId,
                    fieldName
                  };
                  break;
                }
              }
            }
          }
          if (elementInfo) break;
        }
      }
      if (elementInfo) break;
    }

    if (!elementInfo) {
      // Create fallback using the current form context
      const currentSlotId = getCurrentPageSlotId();

      // Search through ALL panels in the form to find the element
      const pageData = appStore.pagesData[currentSlotId];
      let foundElementId: number | null = null;
      let foundPanelId: string | null = null;

      if (pageData?.form) {

        for (const [panelId, panelData] of Object.entries(pageData.form)) {
          // Try to find the element in this panel
          const numericElementId = findNumericElementId(elementName, currentSlotId, panelId);

          if (numericElementId) {
            foundElementId = numericElementId;
            foundPanelId = panelId;
            break;
          }
        }
      }

      if (foundElementId && foundPanelId) {
        elementInfo = {
          formId: currentSlotId,
          panelId: foundPanelId,
          elementId: foundElementId,
          fieldName: elementName
        };
      } else {
        console.warn(`Failed to find numeric element ID for element: ${elementName} in any panel`);
        return null;
      }
    }

    if (elementInfo) {
      const reloadPayload =
        data && typeof data === 'object' && !Array.isArray(data) ? { ...data } : {}
      // DateRange expand перед createMinimalClientData (иначе {from,to} теряется).
      applyDateRangeToClientDataFieldsFromRegistry(
        reloadPayload,
        reloadPayload,
        elements.value as Record<string, unknown>,
        getForm,
      )
      const cleanedClientData = createMinimalClientData(reloadPayload);
      const slotForSession =
        pageDataSlotOpt ||
        String(elementInfo.formId ?? '').trim() ||
        getCurrentPageSlotId()
      const sessionId =
        actionContext?.formPath?.sessionId ||
        actionContext?.sessionId ||
        ((slotForSession ? (appStore.pagesData[slotForSession] as any)?.sessionId : null) ??
        (appStore.pagesData[elementInfo.formId] as any)?.sessionId)

      const result = await queuedReloadElement(
        {
          elementId: elementInfo.elementId,
          formId: actionContext?.formId || elementInfo.formId,
          panelId: elementInfo.panelId,
          sessionId,
          type: actionContext?.platform || currentPlatform.value || undefined,
        },
        cleanedClientData
      )
      // Раньше result возвращался без записи в pagesData — storeLookup оставался stale.
      applyReloadElementResultToPageStore(elementName, result, actionContext, {
        formId: elementInfo.formId,
        panelId: elementInfo.panelId,
        elementId: elementInfo.elementId,
        pageDataSlot: pageDataSlotOpt || String(elementInfo.formId ?? ''),
      })
      return result
    }

    return null;
  } catch (error) {
    console.error(`Failed to reload element ${elementName}:`, error);
  } finally {
    endMeasure?.()
  }
};

// Helper functions for dynamic form/panel ID resolution
const pageDataSlotOverrideStack = ref<string[]>([])

const pushPageDataSlotOverride = (slot: string) => {
  const normalized = String(slot ?? '').trim()
  if (normalized) {
    pageDataSlotOverrideStack.value.push(normalized)
  }
}

const popPageDataSlotOverride = () => {
  pageDataSlotOverrideStack.value.pop()
}

const resolvePageSlotCandidate = (candidate: unknown): string => {
  const pages = appStore.pagesData as Record<string, any>
  const normalized = String(candidate ?? '').trim()
  if (!normalized) return ''
  if (pages?.[normalized]) return normalized

  for (const [storeKey, page] of Object.entries(pages)) {
    if (!page || typeof page !== 'object') continue
    const aliases = getPageIdentityAliases(page as Record<string, any>, { slotId: storeKey })
    if (aliases.includes(normalized)) {
      return storeKey
    }
  }

  return ''
}

const getCurrentPageSlotId = (): string => {
  const pages = appStore.pagesData as Record<string, any>
  const resolvePageSlot = (candidate: unknown): string => resolvePageSlotCandidate(candidate)
  const hasPageSlot = (slot: unknown): slot is string => resolvePageSlot(slot) !== ''

  const override = pageDataSlotOverrideStack.value[pageDataSlotOverrideStack.value.length - 1]
  if (override) {
    const resolvedOverride = resolvePageSlot(override)
    if (resolvedOverride) {
      return resolvedOverride
    }
  }

  const topDialogId = appStore.dialogIds?.length
    ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '').trim()
    : ''
  if (hasPageSlot(topDialogId)) {
    return resolvePageSlot(topDialogId)
  }
  const topDialogFid = topDialogId
    ? String(appStore.dialogs?.[topDialogId]?.fid ?? '').trim()
    : ''
  if (hasPageSlot(topDialogFid)) {
    return resolvePageSlot(topDialogFid)
  }

  const topOverlayId = appStore.overlayIds?.length
    ? String(appStore.overlayIds[appStore.overlayIds.length - 1] ?? '').trim()
    : ''
  if (hasPageSlot(topOverlayId)) {
    return resolvePageSlot(topOverlayId)
  }
  const topOverlayFid = topOverlayId
    ? String(appStore.overlays?.[topOverlayId]?.fid ?? '').trim()
    : ''
  if (hasPageSlot(topOverlayFid)) {
    return resolvePageSlot(topOverlayFid)
  }

  const currentPageId = appStore.currentPageId != null && String(appStore.currentPageId) !== '' && String(appStore.currentPageId) !== 'home'
    ? String(appStore.currentPageId)
    : ''
  if (hasPageSlot(currentPageId)) {
    return resolvePageSlot(currentPageId)
  }

  const routeId = route?.params?.id != null && String(route.params.id) !== ''
    ? String(route.params.id)
    : ''
  if (hasPageSlot(routeId)) {
    return resolvePageSlot(routeId)
  }

  const registeredFormIds = Object.keys(forms.value).filter(Boolean)
  if (registeredFormIds.length > 0) {
    const activeRegistered = [...registeredFormIds]
      .reverse()
      .map((id) => resolvePageSlot(id))
      .find(Boolean)
    if (activeRegistered) {
      return activeRegistered
    }
    return registeredFormIds[registeredFormIds.length - 1]
  }

  const pageIds = Object.keys(pages).filter(Boolean)
  if (pageIds.length > 0) {
    return pageIds[pageIds.length - 1]
  }

  return ''
};

/**
 * Перезагружает панель через `/platform/reload/panel` и синхронизирует результат
 * в `pagesData` + registry элементов.
 */
const reloadPanelViaControls = async (
  panelName: string | number,
  data?: any,
  context?: {
    pageDataSlot?: string
    formId?: string | number
    sessionId?: string | number
    eventCoreId?: string | number
  },
): Promise<any> => {
  const contextSlotId = String(context?.pageDataSlot ?? '').trim()
  const contextFormId = String(context?.formId ?? '').trim()
  const currentSlotId = contextSlotId || getCurrentPageSlotId()
  const formId = contextFormId || getCurrentRegistryFormId() || currentSlotId
  if (!formId) return null

  const resolveReloadPanelPageData = (): any => {
    if (contextSlotId && appStore.pagesData[contextSlotId]) {
      return appStore.pagesData[contextSlotId]
    }
    return findPageDataByFormId(formId) ?? appStore.pagesData[currentSlotId]
  }

  const basePageDataForRequest = resolveReloadPanelPageData()

  // Skip reloadPanel for closing pages (Swing parity: no updates during close)
  if (basePageDataForRequest?.$isClosing === true) {
    return null
  }

  const pageData = basePageDataForRequest
  const sessionId = resolveReloadPanelSessionId(context, [
    basePageDataForRequest,
    pageData,
    appStore.pagesData[currentSlotId],
  ])

  if (!sessionId) {
    return null
  }

  // Skip reloadPanel for closing sessionIds - server events should not process after close
  if (appStore?.isSessionClosing?.(sessionId)) {
    return null
  }

  let panelId: number | null = null
  if (typeof panelName === 'number' && Number.isFinite(panelName)) {
    panelId = panelName
  } else {
    const trimmed = String(panelName ?? '').trim()
    const numeric = parseInt(trimmed, 10)
    if (!Number.isNaN(numeric) && String(numeric) === trimmed) {
      panelId = numeric
    } else {
      // resolve by jsName: panel attrs (messagesPanel) или elements registry
      const fromFormPanels = resolvePanelIdByJsName(basePageDataForRequest, trimmed)
      if (fromFormPanels != null) {
        panelId = fromFormPanels
      }
      if (panelId == null) {
        const allElements = Object.values(elements.value) as Array<{ panelId?: number | string; jsName?: string }>
        for (const el of allElements) {
          if (el.jsName === trimmed && el.panelId != null) {
            const pid = typeof el.panelId === 'number' ? el.panelId : parseInt(String(el.panelId), 10)
            if (!Number.isNaN(pid)) { panelId = pid; break }
          }
        }
      }
      if (panelId == null) {
        // fallback: check forms registry for panelId key matching trimmed
        const formPanels = forms.value[formId] ?? {}
        for (const pid of Object.keys(formPanels)) {
          const pnum = parseInt(pid, 10)
          if (!Number.isNaN(pnum)) { panelId = pnum; break }
        }
      }
    }
  }

  if (panelId == null) {
    console.warn(`[reloadPanel] cannot resolve panelId for: ${panelName}`)
    return null
  }

  const pageFieldsSnapshot = basePageDataForRequest
    ? mergePageDataFieldSnapshot(basePageDataForRequest)
    : {}
  const panelFields = getForm({ formId, panelId: String(panelId), flatten: false }) ?? {}
  const panelFieldKeys = new Set(Object.keys(panelFields))
  for (const el of Object.values(elements.value) as Array<{ panelId?: number | string; fieldName?: string; name?: string }>) {
    const elPanelId = el.panelId != null ? Number(el.panelId) : NaN
    if (!Number.isFinite(elPanelId) || elPanelId !== panelId) continue
    const fieldKey = String(el.fieldName ?? el.name ?? '').trim()
    if (fieldKey) panelFieldKeys.add(fieldKey)
  }
  const layoutFieldNames = collectLayoutFieldNamesForPanels(
    basePageDataForRequest?.form,
    new Set([String(panelId)]),
  )
  for (const name of layoutFieldNames) {
    panelFieldKeys.add(name)
  }
  const enFieldsRaw = data && typeof data === 'object' && !Array.isArray(data) ? data : {}
  const enFields = scopeFieldMapToKeys(
    stripServerEventOverlayFields(enFieldsRaw as Record<string, unknown>),
    panelFieldKeys,
  )
  const mergedFields = buildReloadPanelRequestFields({
    pageFieldsSnapshot: pageFieldsSnapshot as Record<string, unknown>,
    panelFieldKeys,
    panelFields: panelFields as Record<string, unknown>,
    enFields: enFields as Record<string, unknown>,
  })

  const clientData = createSafeClientData({
    fields: {
      fields: mergedFields,
      fieldAttributes: {},
      errors: {},
      attributes: {},
    },
  })

  // ReloadRequestClientModel (как у /platform/reload/element): elementId = id панели, не panelId.
  const payload: Record<string, any> = {
    sessionId,
    elementId: panelId,
    clientData,
    isClone: false,
    cloneOf: null,
  }
  const coreType = currentPlatform.value
  if (coreType) payload.type = coreType

  try {
    const response = await apiService.platformReloadPanel(payload)
    const result = response?.data
    if (!result) return null

    const rawResultFields =
      result?.fields?.fields && typeof result.fields.fields === 'object'
        ? (result.fields.fields as Record<string, unknown>)
        : {}

    const scopedResultKeys = expandReloadPanelFieldKeys({
      panelFieldKeys,
      layoutFieldNames,
      resultFields: rawResultFields,
    })
    const resultFields = scopeReloadPanelResultFields(rawResultFields, scopedResultKeys)
    const normalizedResultFields = Object.fromEntries(
      Object.entries(resultFields).map(([key, value]) => [
        key,
        normalizeReloadPanelTableField(value),
      ]),
    ) as Record<string, unknown>

    const basePageData = resolveReloadPanelPageData()
    if (!basePageData) return result

    const nextChangedData = applyReloadPanelFieldsToChangedData(
      basePageData.jsResult?.changed_data,
      normalizedResultFields,
    )

    const nextUniFields = {
      ...(basePageData.uniData?.fields?.fields ?? {}),
      ...normalizedResultFields,
    }

    const updatedPageData = {
      ...basePageData,
      sessionId: result.sessionId ?? basePageData.sessionId,
      uniData: {
        ...basePageData.uniData,
        fields: {
          ...basePageData.uniData?.fields,
          // reload/panel: поля из ответа заменяют одноимённые в store (не mergePageFieldMaps).
          fields: nextUniFields,
        },
      },
      // Stale currentChainData must not win over reload/panel tables in soft merges
      // (mergePageFieldMaps uniData←chain) or registry fallbacks — same as reloadForm STACK.
      currentChainData: (() => {
        const chain = (basePageData.currentChainData as Record<string, unknown> | undefined) ?? {}
        const chainFields =
          (chain.fields as { fields?: Record<string, unknown> } | undefined)?.fields ?? {}
        return {
          ...chain,
          fields: {
            ...((chain.fields as Record<string, unknown> | undefined) ?? {}),
            fields: overlayAuthoritativeFieldKeys(chainFields, normalizedResultFields),
          },
        }
      })(),
      jsResult: {
        ...(basePageData.jsResult ?? {}),
        ...(result.jsResult ?? {}),
        states: mergeExchangeStatesForPageUpdate(result, basePageData),
        changed_data: nextChangedData,
        event_listeners: mergeEventListenersForPageUpdate(
          basePageData.jsResult?.event_listeners,
          result.jsResult?.event_listeners,
        ),
      },
    }

    writePageDataForForm(formId, updatedPageData, 'GlobalProvider.reloadPanelViaControls', {
      originSlotId: currentSlotId,
      response: result,
    })

    pruneTilesCloneStateAfterTableReload(normalizedResultFields, formId)
    notifyTilesInlineAfterLoadAfterTableReload(normalizedResultFields)

    const overlayOriginFromPanel = Object.values(elementsByKey.value).find((el) => {
      const rec = el as { panelId?: unknown; pageId?: unknown }
      return (
        String(rec.panelId ?? '') === String(panelId ?? '') &&
        isFormOverlayPageId(rec.pageId)
      )
    }) as { pageId?: unknown } | undefined
    syncPageDataToRegistry(formId, currentSlotId || undefined, {
      forceFieldNames: Object.keys(normalizedResultFields),
      originOverlayPageId: overlayOriginFromPanel?.pageId != null
        ? String(overlayOriginFromPanel.pageId)
        : undefined,
    })

    const incomingStates = normalizeExchangeStates(result)
    if (incomingStates.length > 0) {
      await applyIncomingExchangeStates({
        appStore,
        slotId: currentSlotId || String(formId),
        registryFormId: formId,
        incomingStates,
        changedData: updatedPageData.jsResult?.changed_data,
        processStates: formStatesResult.processStates,
        setPageDataSource: 'GlobalProvider.reloadPanelViaControls',
        resolveStoreKey: {
          originSlotId: currentSlotId,
          responseSchemaFormId: resolveResponseSchemaFormId(result),
        },
      })
    }

    if (Object.keys(normalizedResultFields).length > 0) {
      await Promise.all(
        Object.entries(normalizedResultFields).map(([fieldName, fieldData]) =>
          refillElementViaControls(fieldName, fieldData, String(formId)),
        ),
      )
    }

    // Clones remount after Vue flush; second epoch so inlineAfterLoad hits new widgets.
    if (collectUniDataTableFieldNamesFromReloadFields(normalizedResultFields).length > 0) {
      await nextTick()
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve())
      })
      notifyTilesInlineAfterLoadAfterTableReload(normalizedResultFields)
    }

    return result
  } catch (error: any) {
    if (isPlatformCoreNullError(error)) {
      return null
    }
    console.error(`[reloadPanel] failed for panel ${panelName}:`, error)
    return null
  }
}

/**
 * Soft-event `reload_form` (Swing: Window.doStackAction(-1) / AT_STACK).
 * POST /platform/action actionType=STACK_ACTION, actionId=-1 → полная form + data в слот.
 * Не путать с `/platform/reload/form` (delta merge полей).
 *
 * Call shapes: `reloadForm(ctx)` (formEventRouter) or `reloadForm(data, ctx)` (En / dialogClosed).
 */
const reloadFormViaStackAction = async (
  dataOrContext?: unknown,
  maybeContext?: {
    pageDataSlot?: string
    formId?: string | number
    sessionId?: string | number
    source?: string
    eventCoreId?: string | number
  },
): Promise<any> => {
  const context = resolveReloadFormStackContext(dataOrContext, maybeContext)
  const contextSlotId = String(context?.pageDataSlot ?? '').trim()
  const contextFormId = String(context?.formId ?? '').trim()
  const currentSlotId = contextSlotId || getCurrentPageSlotId()
  const dialogSlot = currentSlotId
    ? (appStore.dialogs as Record<string, any> | undefined)?.[currentSlotId]
    : null
  const slotPageData = currentSlotId
    ? (appStore.pagesData as Record<string, any>)[currentSlotId] ?? dialogSlot?.data ?? null
    : null
  const formId =
    contextFormId ||
    (slotPageData ? resolveElementRegistryFormId(slotPageData, currentSlotId) : '') ||
    String(dialogSlot?.fid ?? '').trim() ||
    getCurrentRegistryFormId() ||
    currentSlotId
  if (!formId) return null

  const basePageDataForRequest = slotPageData ?? findPageDataByFormId(formId)
  const sessionId = resolveReloadPanelSessionId(context, [
    basePageDataForRequest,
    appStore.pagesData[currentSlotId],
    dialogSlot,
  ])

  if (!sessionId) {
    return null
  }

  const platform = currentPlatform.value
  const payload = createPlatformActionParams({
    sessionId,
    isClone: false,
    ...(platform ? { type: platform } : {}),
    // UnifiedActionData.ActionType.STACK_ACTION ordinal (= Window.AT_STACK)
    actionType: 2,
    actionId: -1,
    openingParams: {
      newWindow: false,
      ignoreExistingStack: true,
    },
  })

  try {
    const response = await coreService.action(payload, { deferFormParse: true })
    if (!response) return null

    const basePageData =
      (currentSlotId
        ? (appStore.pagesData as Record<string, any>)[currentSlotId] ?? dialogSlot?.data ?? null
        : null) ?? findPageDataByFormId(formId)
    if (!basePageData) {
      console.warn(`[reloadForm/stack] skipped UI apply: no pageData`, { formId, currentSlotId })
      return response
    }

    const responseSchemaFormId = resolveResponseSchemaFormId(response)
    const numericFormId = parseInt(String(responseSchemaFormId || formId), 10)
    let form = response.form ?? basePageData.form
    if (response.form != null) {
      const { parseService } = await import('@/services/parse/parse.service')
      const parsed = parseService.getForm(
        response.form,
        Number.isFinite(numericFormId) ? numericFormId : 0,
      )
      if (formHasRenderableLayout(parsed) || typeof response.form !== 'string') {
        form = parsed
      }
    }

    // STACK response usually has fields only in jsResult.changed_data (no uniData) —
    // sync into uniData so onLoad/En snapshot see fresh values (emailConfirmed etc.).
    const stackFields = resolveJsResultChangedDataFieldMap(
      (response as { jsResult?: { changed_data?: unknown } })?.jsResult?.changed_data,
    )
    const responseUniFields =
      (response as { uniData?: { fields?: { fields?: Record<string, unknown> } } })?.uniData?.fields
        ?.fields ?? {}
    const nextUniFields = overlayAuthoritativeFieldKeys(
      overlayAuthoritativeFieldKeys(
        (basePageData.uniData?.fields?.fields as Record<string, unknown>) ?? {},
        responseUniFields,
      ),
      stackFields,
    )

    const updatedPageData: Record<string, unknown> = {
      ...basePageData,
      ...response,
      form,
      sessionId: response.sessionId ?? basePageData.sessionId,
      $fid: resolveResponseSchemaFormId(response) || basePageData.$fid,
      $parentPageId: basePageData.$parentPageId,
      $isDialog: basePageData.$isDialog ?? Boolean(dialogSlot),
      $isAdjacentWindow: basePageData.$isAdjacentWindow,
      clientDialogCloseConfig: basePageData.clientDialogCloseConfig,
      $boundDocumentIdentity: basePageData.$boundDocumentIdentity,
      uniData: {
        ...(basePageData.uniData ?? {}),
        ...((response as { uniData?: Record<string, unknown> }).uniData ?? {}),
        fields: {
          ...(basePageData.uniData?.fields ?? {}),
          ...((response as { uniData?: { fields?: Record<string, unknown> } }).uniData?.fields ?? {}),
          fields: nextUniFields,
        },
      },
      // Form.vue re-runs web_client_exec onLoad once (shared En — no parallel catch-up).
      $preBuiltRootOnLoad: false,
      $rootOnLoadNonce: String(response.sessionId ?? sessionId ?? Date.now()),
      // Stale currentChainData scalars (e.g. emailConfirmed:false) must not win over STACK fields
      // in mergePageDataFieldSnapshot (uniData ← chain ← changed_data).
      currentChainData: (() => {
        const chain =
          (response as { currentChainData?: Record<string, unknown> }).currentChainData ??
          (basePageData.currentChainData as Record<string, unknown> | undefined) ??
          {}
        const chainFields =
          (chain.fields as { fields?: Record<string, unknown> } | undefined)?.fields ?? {}
        return {
          ...chain,
          fields: {
            ...((chain.fields as Record<string, unknown> | undefined) ?? {}),
            fields: overlayAuthoritativeFieldKeys(chainFields, nextUniFields),
          },
        }
      })(),
    }

    if (currentSlotId && ((appStore.pagesData as Record<string, any>)[currentSlotId] || dialogSlot)) {
      appStore.setPageData(
        currentSlotId,
        updatedPageData,
        (basePageData.$parentPageId as string | undefined) ?? undefined,
        undefined,
        'GlobalProvider.reloadFormViaStackAction',
      )
    } else {
      writePageDataForForm(formId, updatedPageData, 'GlobalProvider.reloadFormViaStackAction', {
        originSlotId: currentSlotId,
        response,
      })
    }

    syncPageDataToRegistry(formId, currentSlotId || undefined)

    const sameSchema = isSameSchemaStackReload(String(formId), String(responseSchemaFormId ?? ''))
    if (currentSlotId && !sameSchema) {
      dispatchSameWindowReplace(currentSlotId)
    }

    if (sameSchema) {
      const onLoadAfterReload = resolveWebClientRootOnLoadScriptAfterReload(updatedPageData.form)
      if (onLoadAfterReload) {
        const stackSlotId = currentSlotId || String(formId)
        const appliedRegistryFormId = String(responseSchemaFormId || formId)
        const runTrigger = buildRootOnLoadRunTriggerFromPage(
          updatedPageData,
          stackSlotId,
          appliedRegistryFormId,
        )
        if (runTrigger) {
          reserveRootOnLoadGlobally(runTrigger)
        }
        await runEvent(
          combineRootOnLoadScript(readRootFormFunctions(updatedPageData.form), onLoadAfterReload),
          {
            formId: appliedRegistryFormId,
            pageSlotId: stackSlotId,
            eventName: 'onLoad',
          },
        )
      }
    }

    const incomingStates = normalizeExchangeStates(response)
    if (incomingStates.length > 0) {
      const stackSlotId = currentSlotId || String(formId)
      const appliedRegistryFormId = String(responseSchemaFormId || formId)
      if (!sameSchema) {
        clearFormShellVisibilityRuntime({
          slotId: stackSlotId,
          registryFormIds: [appliedRegistryFormId, String(formId)].filter(
            (id, idx, all) => id && all.indexOf(id) === idx,
          ),
          mapsOnly: true,
        })
      }
      await applyIncomingExchangeStates({
        appStore,
        slotId: stackSlotId,
        registryFormId: appliedRegistryFormId,
        incomingStates,
        changedData: (updatedPageData.jsResult as { changed_data?: unknown } | undefined)
          ?.changed_data,
        processStates: formStatesResult.processStates,
        setPageDataSource: 'GlobalProvider.reloadFormViaStackAction',
        resolveStoreKey: {
          originSlotId: currentSlotId,
          responseSchemaFormId: resolveResponseSchemaFormId(response),
        },
      })
    }

    await nextTick()
    return response
  } catch (error: any) {
    if (isPlatformCoreNullError(error)) {
      console.warn(`[reloadForm/stack] skipped (core unavailable)`, { formId, sessionId })
      return null
    }
    console.error(`[reloadForm/stack] failed:`, error)
    return null
  }
}

/**
 * Перезагружает форму через `/platform/reload/form` и синхронизирует результат
 * в `pagesData` + registry элементов.
 */
const reloadFormViaControls = async (
  data?: any,
  context?: {
    pageDataSlot?: string
    formId?: string | number
    sessionId?: string | number
    eventCoreId?: string | number
    omitPageFieldSnapshot?: boolean
  },
): Promise<any> => {
  const contextSlotId = String(context?.pageDataSlot ?? '').trim()
  const contextFormId = String(context?.formId ?? '').trim()
  const currentSlotId = contextSlotId || getCurrentPageSlotId()
  const slotPageData = currentSlotId
    ? (appStore.pagesData as Record<string, any>)[currentSlotId]
    : null
  const formId =
    contextFormId ||
    (slotPageData ? resolveElementRegistryFormId(slotPageData, currentSlotId) : '') ||
    getCurrentRegistryFormId() ||
    currentSlotId
  if (!formId) return null

  const basePageDataForRequest =
    slotPageData ?? findPageDataByFormId(formId)
  const pageData = basePageDataForRequest ?? appStore.pagesData[currentSlotId]
  const sessionId = resolveReloadPanelSessionId(context, [
    basePageDataForRequest,
    pageData,
    appStore.pagesData[currentSlotId],
  ])

  if (!sessionId) {
    return null
  }

  const pageFieldsSnapshot = basePageDataForRequest
    ? mergePageDataFieldSnapshot(basePageDataForRequest)
    : {}
  const mergedFields = buildReloadFormRequestFields(pageFieldsSnapshot as Record<string, unknown>, data, {
    omitPageFieldSnapshot: context?.omitPageFieldSnapshot === true,
  })

  const clientData = createSafeClientData({
    fields: {
      fields: mergedFields,
      fieldAttributes: {},
      errors: {},
      attributes: {},
    },
  })

  const payload: Record<string, any> = {
    sessionId,
    clientData,
    isClone: false,
    cloneOf: null,
  }
  const coreType = currentPlatform.value
  if (coreType) payload.type = coreType

  try {
    const response = await apiService.platformReloadForm(payload)
    const result = response?.data
    if (!result) return null

    const rawResultFields =
      result?.fields?.fields && typeof result.fields.fields === 'object'
        ? (result.fields.fields as Record<string, unknown>)
        : {}

    const normalizedResultFields = Object.fromEntries(
      Object.entries(rawResultFields).map(([key, value]) => [
        key,
        normalizeReloadPanelTableField(value),
      ]),
    ) as Record<string, unknown>

    const basePageData =
      (currentSlotId ? (appStore.pagesData as Record<string, any>)[currentSlotId] : null) ??
      findPageDataByFormId(formId)
    if (!basePageData) {
      console.warn(`[reloadForm] skipped UI apply: no pageData`, { formId, currentSlotId })
      return result
    }

    const nextChangedData = applyReloadPanelFieldsToChangedData(
      basePageData.jsResult?.changed_data,
      normalizedResultFields,
    )

    const nextUniFields = {
      ...(basePageData.uniData?.fields?.fields ?? {}),
      ...normalizedResultFields,
    }

    const updatedPageData = {
      ...basePageData,
      sessionId: result.sessionId ?? basePageData.sessionId,
      $preBuiltRootOnLoad: false,
      $rootOnLoadNonce: String(result.sessionId ?? sessionId ?? Date.now()),
      uniData: {
        ...basePageData.uniData,
        fields: {
          ...basePageData.uniData?.fields,
          fields: nextUniFields,
        },
      },
      currentChainData: (() => {
        const chain = (basePageData.currentChainData as Record<string, unknown> | undefined) ?? {}
        const chainFields =
          (chain.fields as { fields?: Record<string, unknown> } | undefined)?.fields ?? {}
        return {
          ...chain,
          fields: {
            ...((chain.fields as Record<string, unknown> | undefined) ?? {}),
            fields: overlayAuthoritativeFieldKeys(chainFields, normalizedResultFields),
          },
        }
      })(),
      jsResult: {
        ...(basePageData.jsResult ?? {}),
        ...(result.jsResult ?? {}),
        states: mergeExchangeStatesForPageUpdate(result, basePageData),
        changed_data: nextChangedData,
        event_listeners: mergeEventListenersForPageUpdate(
          basePageData.jsResult?.event_listeners,
          result.jsResult?.event_listeners,
        ),
      },
    }

    writePageDataForForm(formId, updatedPageData, 'GlobalProvider.reloadFormViaControls', {
      originSlotId: currentSlotId,
      response: result,
    })

    pruneTilesCloneStateAfterTableReload(normalizedResultFields, formId)
    notifyTilesInlineAfterLoadAfterTableReload(normalizedResultFields)

    syncPageDataToRegistry(formId, currentSlotId || undefined, {
      forceFieldNames: Object.keys(normalizedResultFields),
    })

    const incomingStates = normalizeExchangeStates(result)
    if (incomingStates.length > 0) {
      await applyIncomingExchangeStates({
        appStore,
        slotId: currentSlotId || String(formId),
        registryFormId: formId,
        incomingStates,
        changedData: updatedPageData.jsResult?.changed_data,
        processStates: formStatesResult.processStates,
        setPageDataSource: 'GlobalProvider.reloadFormViaControls',
        resolveStoreKey: {
          originSlotId: currentSlotId,
          responseSchemaFormId: resolveResponseSchemaFormId(result),
        },
      })
    }

    if (Object.keys(normalizedResultFields).length > 0) {
      await Promise.all(
        Object.entries(normalizedResultFields).map(([fieldName, fieldData]) =>
          refillElementViaControls(fieldName, fieldData, String(formId)),
        ),
      )
    }

    if (collectUniDataTableFieldNamesFromReloadFields(normalizedResultFields).length > 0) {
      await nextTick()
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve())
      })
      notifyTilesInlineAfterLoadAfterTableReload(normalizedResultFields)
    }

    const onLoadAfterReload = resolveWebClientRootOnLoadScriptAfterReload(updatedPageData.form)
    if (onLoadAfterReload) {
      await runEvent(
        combineRootOnLoadScript(readRootFormFunctions(updatedPageData.form), onLoadAfterReload),
        {
          formId,
          pageSlotId: currentSlotId || String(formId),
          eventName: 'onLoad',
        },
      )
    }

    return result
  } catch (error: any) {
    if (isPlatformCoreNullError(error)) {
      console.warn(`[reloadForm] skipped (core unavailable)`, {
        formId,
        sessionId,
      })
      return null
    }
    console.error(`[reloadForm] failed:`, error)
    return null
  }
}

const getCurrentPageIdentity = () => {
  const slotId = getCurrentPageSlotId()
  const pageData = slotId ? appStore.pagesData[slotId] : null
  const topDialogId = appStore.dialogIds?.length
    ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '').trim()
    : ''
  const topOverlayId = appStore.overlayIds?.length
    ? String(appStore.overlayIds[appStore.overlayIds.length - 1] ?? '').trim()
    : ''

  let explicitRegistryFormId = ''
  let explicitWindowInstanceId = slotId
  if (slotId && slotId === topDialogId) {
    explicitRegistryFormId = String(appStore.dialogs?.[slotId]?.fid ?? '').trim()
    explicitWindowInstanceId = topDialogId || slotId
  } else if (slotId && slotId === topOverlayId) {
    explicitRegistryFormId = String(appStore.overlays?.[slotId]?.fid ?? '').trim()
    explicitWindowInstanceId = topOverlayId || slotId
  }

  return resolvePageIdentity(pageData as Record<string, any> | null, {
    slotId,
    registryFormId: explicitRegistryFormId,
    windowInstanceId: explicitWindowInstanceId,
  })
}

const getCurrentFormId = (): string => getCurrentPageSlotId()
const getCurrentRegistryFormId = (): string => getCurrentPageIdentity().registryFormId || getCurrentPageSlotId()
const getCurrentWindowInstanceId = (): string => getCurrentPageIdentity().windowInstanceId || getCurrentPageSlotId()

const getDefaultPanelId = (): string => {
  const currentSlotId = getCurrentPageSlotId();
  if (!currentSlotId) return '';

  const formData = appStore.pagesData[currentSlotId];
  if (formData?.form) {
    const panelIds = Object.keys(formData.form);
    return panelIds.length > 0 ? panelIds[0] : '';
  }
  return '';
};

// Helper function to find numeric element ID from element name
const findNumericElementId = (elementName: string, formId: string, panelId: string): number | null => {
  try {
    const normalizeText = (value: unknown): string => {
      if (value == null) return ''
      if (typeof value === 'string' || typeof value === 'number') return String(value).trim()
      if (typeof value === 'object') {
        const candidate = value as Record<string, unknown>
        if (typeof candidate._text === 'string' || typeof candidate._text === 'number') {
          return String(candidate._text).trim()
        }
      }
      return ''
    }
    const extractNumericId = (value: unknown): number | null => {
      if (value == null) return null
      const asNumber = Number(value)
      if (Number.isFinite(asNumber) && asNumber > 0) return asNumber
      if (typeof value === 'object') {
        const candidate = value as Record<string, unknown>
        const fromText = Number(candidate._text)
        if (Number.isFinite(fromText) && fromText > 0) return fromText
      }
      return null
    }

    const registryPanel = forms.value?.[formId]?.[panelId]
    if (registryPanel && typeof registryPanel === 'object') {
      for (const [registryElementId, registryElementData] of Object.entries(registryPanel)) {
        const idFromKey = extractNumericId(registryElementId)
        if (idFromKey == null) continue

        if (
          registryElementData &&
          typeof registryElementData === 'object' &&
          !Array.isArray(registryElementData) &&
          Object.prototype.hasOwnProperty.call(registryElementData, elementName)
        ) {
          return idFromKey
        }
      }
    }

    // Look in pageData for element configuration
    const pageData = appStore.pagesData[formId];

    if (pageData?.uniData?.elements) {
      const elements = pageData.uniData.elements;

      for (const element of elements) {
        const explicitName =
          normalizeText(element?.name) ||
          normalizeText(element?._attributes?.name) ||
          normalizeText(element?.element?.name)
        if (explicitName && explicitName === elementName) {
          const id =
            extractNumericId(element?._attributes?.id) ??
            extractNumericId(element?.id) ??
            extractNumericId(element?.element?._attributes?.id) ??
            extractNumericId(element?.element?.id)
          if (id != null) {
            return id;
          }
        }
      }
    } else {
      // Try to find element in pageData directly
      if (pageData?.uniData) {
        // Check if the element exists in pageData directly
        if (pageData.uniData[elementName]) {
          // Try to extract ID from the element structure
          const elementData = pageData.uniData[elementName];
          const id = extractNumericId(elementData?._attributes?.id) ?? extractNumericId(elementData?.id)
          if (id != null) {
            return id;
          }
        }
      }
    }

    // Look in pageData.form structure
    if (pageData?.form?.[panelId]) {
      const panelData = pageData.form[panelId];

      // Check if there are element configurations in the panel
      for (const [key, value] of Object.entries(panelData)) {
        if (key.includes(elementName) && value && typeof value === 'object') {
          // Try to find element ID in the value structure
          const id =
            extractNumericId((value as any)?._attributes?.id) ??
            extractNumericId((value as any)?.id)
          if (id != null) {
            return id;
          }
        }
      }
    }

    for (const [name, element] of Object.entries(elements.value)) {
      if (name === elementName && element.elementId) {
        const id = typeof element.elementId === 'string' ? +element.elementId : element.elementId;
        if (id > 0) {
          return id;
        }
      }
    }

    for (const [name, element] of Object.entries(elements.value)) {
      if (name.includes(elementName) || elementName.includes(name)) {
        const id = typeof element.elementId === 'string' ? +element.elementId : element.elementId;
        if (id > 0) {
          return id;
        }
      }
    }

    // Look for table elements by type
    for (const [name, element] of Object.entries(elements.value)) {
      if (element && element.type?._text === 'Table' && name === elementName) {
        const id = typeof element.elementId === 'string' ? +element.elementId : element.elementId;
        if (id > 0) {
          return id;
        }
      }
    }

    const scanNameIdPair = (root: unknown): number | null => {
      const visited = new Set<object>()
      const stack: unknown[] = [root]

      while (stack.length > 0) {
        const node = stack.pop()
        if (!node || typeof node !== 'object') continue
        if (visited.has(node as object)) continue
        visited.add(node as object)

        if (Array.isArray(node)) {
          for (const item of node) stack.push(item)
          continue
        }

        const rec = node as Record<string, unknown>
        const recAttributes =
          rec._attributes && typeof rec._attributes === 'object'
            ? (rec._attributes as Record<string, unknown>)
            : undefined
        const candidateName =
          normalizeText(rec.name) ||
          normalizeText(rec.fieldName) ||
          normalizeText(recAttributes?.name)

        if (candidateName === elementName) {
          const candidateId =
            extractNumericId(rec.id) ??
            extractNumericId(rec.elementId) ??
            extractNumericId(recAttributes?.id)
          if (candidateId != null) {
            return candidateId
          }
        }

        for (const value of Object.values(rec)) {
          if (value && typeof value === 'object') {
            stack.push(value)
          }
        }
      }

      return null
    }

    const scannedId = scanNameIdPair(pageData?.form) ?? scanNameIdPair(pageData?.uniData)
    if (scannedId != null) {
      return scannedId
    }

    return null;
  } catch (error) {
    return null;
  }
};

/** Параметры для resolveRegistryVisibility из текущих registry GlobalProvider. */
const buildVisibilityResolveParams = (partial: {
  level?: string
  id?: string | number
  jsName?: string
  name?: string
  formId?: string
}) => {
  const override =
    pageDataSlotOverrideStack.value[pageDataSlotOverrideStack.value.length - 1]
  const enFormId = en.value?.getFormId?.()
  const fromEn =
    !override &&
    enFormId != null &&
    enFormId !== -1 &&
    String(enFormId).trim() !== ''
      ? String(enFormId)
      : undefined
  const slotId = String(
    override ||
      (typeof en.value?.getPageDataSlotId === 'function' ? en.value.getPageDataSlotId() : '') ||
      getCurrentPageSlotId() ||
      '',
  ).trim()
  const slotPage = slotId
    ? (appStore.pagesData as Record<string, any>)[slotId]
    : undefined
  return {
    formId: partial.formId ?? fromEn ?? getCurrentRegistryFormId(),
    elements: elements.value,
    actions: actions.value,
    overlaysRegistry: overlaysRegistry.value,
    panelsVisibilityRegistry: panelsVisibilityRegistry.value,
    layoutForm: slotPage?.form,
    ...partial,
  }
}

/** Рекурсивная видимость дочерних элементов панели/формы (как VisualEntity.setVisible в Swing). */
const propagateVisibilityToScopedChildren = (
  name: string,
  isVisible: boolean,
  scope?: { formId?: string; panelId?: string },
) => {
  const nameStr = String(name ?? '').trim()

  // Блокировать скрытие дочерних элементов если панель содержит файлы
  if (!isVisible && panelsWithFiles.value.has(nameStr)) {
    return
  }

  const seen = new Set<object>()
  for (const [elementName, el] of Object.entries(elements.value)) {
    if (!el || typeof el.setVisible !== 'function') continue
    // Alias keys (name + name#id) share one object — propagate once.
    if (seen.has(el as object)) continue
    seen.add(el as object)

    const belongsToScope =
      (scope?.formId &&
        String(el.formId ?? '') === String(scope.formId) &&
        (!scope.panelId || String(el.panelId ?? '') === String(scope.panelId))) ||
      (!scope &&
        (String(el.panelId ?? '') === nameStr || String(el.formId ?? '') === nameStr))

    if (belongsToScope && elementName !== nameStr) {
      el.setVisible(isVisible)
    }
  }
}

const resolveClientVisibilityPersistSlotId = (): string => {
  const override =
    pageDataSlotOverrideStack.value[pageDataSlotOverrideStack.value.length - 1] ?? ''
  const enSlot =
    typeof en.value?.getPageDataSlotId === 'function' ? en.value.getPageDataSlotId() : ''
  return resolveVisibilityPersistSlotId({
    overrideSlotId: override,
    enPageDataSlotId: enSlot,
    currentPageSlotId: getCurrentPageSlotId(),
    resolveExistingSlot: (candidate) => resolvePageSlotCandidate(candidate),
  })
}

const persistClientElementVisibilityState = (input: {
  elementId?: string | number | null
  elementName?: string | number | null
  visible: boolean
  levelNorm?: string
}) => {
  if (String(input.levelNorm ?? 'element').toLowerCase() !== 'element') {
    return
  }
  // Tiles setInlineVisibility: row-map + scoped registry, not jsResult.states per call.
  if (isTilesRowScopedRegistryName(String(input.elementName ?? ''))) {
    return
  }
  const patches = buildClientElementVisibilityPatches(input)
  if (patches.length === 0) return
  const persistSlot = resolveClientVisibilityPersistSlotId()
  persistVisibilityStatePatchesToPageStore(
    appStore,
    persistSlot,
    patches,
    'GlobalProvider.setVisible',
  )
}

const persistActionVisibilityState = (actionId: string | number, visible: boolean): void => {
  const patches: VisibilityStatePatch[] = []
  recordVisibilityStatePatch(patches, { level: 'ACTION', id: actionId, visible })
  if (patches.length === 0) return
  persistVisibilityStatePatchesToPageStore(
    appStore,
    resolveClientVisibilityPersistSlotId(),
    patches,
    'GlobalProvider.setVisible.action',
  )
}

const persistPanelVisibilityState = (panelId: string | number, visible: boolean): void => {
  const patches: VisibilityStatePatch[] = []
  recordVisibilityStatePatch(patches, { level: 'PANEL', id: panelId, visible })
  if (patches.length === 0) return
  const slotId = resolveClientVisibilityPersistSlotId()
  persistVisibilityStatePatchesToPageStore(
    appStore,
    slotId,
    patches,
    'GlobalProvider.setVisible.panel',
  )
}

const persistPageVisibilityState = (
  match: { id: string; jsName: string },
  visible: boolean,
): void => {
  const patches = buildClientPageVisibilityPatches({
    pageId: match.id || undefined,
    pageName: match.jsName || undefined,
    visible,
  })
  if (patches.length === 0) return
  persistVisibilityStatePatchesToPageStore(
    appStore,
    resolveClientVisibilityPersistSlotId(),
    patches,
    'GlobalProvider.setVisible.page',
  )
}

const persistClientElementEditableState = (input: {
  elementId?: string | number | null
  elementName?: string | number | null
  editable: boolean
}): void => {
  const patches: VisibilityStatePatch[] = []
  const idStr = input.elementId != null ? String(input.elementId).trim() : ''
  const name = String(input.elementName ?? '').trim()
  if (idStr && /^\d+$/.test(idStr)) {
    recordSetEditableByLevelPatch(patches, 'ELEMENT', input.elementId as string | number, input.editable)
  }
  if (name && !/^\d+$/.test(name)) {
    recordSetEditableNamePatch(patches, name, input.editable)
  }
  if (patches.length === 0) return
  persistVisibilityStatePatchesToPageStore(
    appStore,
    resolveClientVisibilityPersistSlotId(),
    patches,
    'GlobalProvider.setEditable',
  )
}

const persistPanelEditableState = (panelId: string | number, editable: boolean): void => {
  const patches: VisibilityStatePatch[] = []
  recordSetEditableByLevelPatch(patches, 'PANEL', panelId, editable)
  if (patches.length === 0) return
  persistVisibilityStatePatchesToPageStore(
    appStore,
    resolveClientVisibilityPersistSlotId(),
    patches,
    'GlobalProvider.setEditable.panel',
  )
}

type NameBasedVisibilityElementsByKey = Record<
  string,
  {
    setVisible?: (v: boolean) => void
    elementId?: unknown
    fieldName?: unknown
    formId?: unknown
    name?: unknown
  }
>

function resolvePageDataForNameBasedVisibility(scopeFormId?: string): {
  formId: string
  pageData: Record<string, unknown> | undefined
} {
  const hasSlotOverride = pageDataSlotOverrideStack.value.length > 0
  const formIdForName = String(
    scopeFormId ??
      (hasSlotOverride ? undefined : en.value?.getFormId?.()) ??
      getCurrentRegistryFormId() ??
      '',
  ).trim()
  const pageSlotId = String(
    (hasSlotOverride
      ? getCurrentPageSlotId()
      : en.value?.getPageDataSlotId?.() ?? getCurrentPageSlotId()) ??
      '',
  ).trim()
  const pageData =
    (pageSlotId ? (appStore.pagesData as Record<string, any>)[pageSlotId] : null) ??
    (formIdForName ? findPageDataByFormId(formIdForName) : null) ??
    ((en.value as { pageData?: unknown } | null | undefined)?.pageData as
      | Record<string, unknown>
      | undefined)
  const formId =
    resolveElementRegistryFormId(pageData as Record<string, any>, pageSlotId) ||
    formIdForName
  return { formId, pageData }
}

/** PAGE tab from form layout — persist before Section mounts (Swing VisualEntity page). */
function applyLayoutPageVisibility(name: string | number, isVisible: boolean): boolean {
  const idStr = String(name ?? '').trim()
  if (!idStr) return false
  const { pageData } = resolvePageDataForNameBasedVisibility()
  const match = findLayoutPageTabByVisibilityName(
    (pageData as { form?: unknown } | undefined)?.form ?? pageData,
    idStr,
  )
  if (!match) return false
  persistPageVisibilityState(match, isVisible)
  return true
}

/** Registry miss: resolve element by name (form meta / loose keys) and update shell visibility. */
function applyNameBasedElementVisibilityFallback(
  name: string,
  isVisible: boolean,
  options?: {
    scopeFormId?: string
    levelNorm?: string
    rememberScoped?: boolean
  },
): boolean {
  const idStr = String(name ?? '').trim()
  if (!idStr) return false

  const { formId, pageData } = resolvePageDataForNameBasedVisibility(options?.scopeFormId)
  const resolved = resolveNameBasedElementVisibilityTarget({
    name: idStr,
    formId,
    elements: elements.value,
    elementsByKey: elementsByKey.value as NameBasedVisibilityElementsByKey,
    pageData,
  })
  if (!resolved) return false

  resolved.entry?.setVisible?.(isVisible)
  setElementShellVisibleById(resolved.elementId, isVisible)
  rememberDeferredElementShellById(resolved.elementId, { visible: isVisible })
  elementVisibilityHooksByElementId.get(String(resolved.elementId))?.(isVisible)
  persistClientElementVisibilityState({
    elementId: resolved.elementId,
    elementName: idStr,
    visible: isVisible,
    levelNorm: options?.levelNorm ?? 'element',
  })
  if (resolved.source === 'formMeta') {
    rememberDeferredElementShell(idStr, { visible: isVisible }, options?.scopeFormId)
  }
  if (options?.rememberScoped !== false) {
    rememberPersistentScopedVisibility(idStr, isVisible)
  }
  return true
}

/**
 * Трёхаргументный setVisible из скриптов: en.setVisible("element", 39115, true).
 * Резолв через resolveRegistryVisibility (id → registry entry), не по ключу elements[id].
 * @see resolveRegistryVisibility.ts, dev/WUI-385.md
 */
const setVisibleByLevel = (
  level: string,
  id: string | number,
  isVisible: boolean,
  options?: SetVisibleByLevelOptions,
) => {
  const idStr = String(id)
  const levelNorm = normalizeRegistryLevel(level)
  const propagateChildren = options?.propagateChildren !== false

  const overlayById = Object.values(overlaysRegistry.value).find(
    (entry) => entry.overlayId === idStr,
  )
  if (overlayById?.setVisible) {
    overlayById.setVisible(isVisible)
    delete pendingOverlayVisibility.value[overlayById.name]
    delete pendingOverlayVisibility.value[overlayPendingIdKey(overlayById.overlayId)]
    return
  }

  if (levelNorm === 'overlaypage') {
    setPendingOverlayVisibility(undefined, idStr, isVisible)
    const overlayTarget = resolveVisibilityTarget(
      buildVisibilityResolveParams({ level: 'overlaypage', id }),
    )
    overlayTarget?.setVisible(isVisible)
    return
  }

  let target = resolveVisibilityTarget(buildVisibilityResolveParams({ level, id }))
  if (!target) {
    // registry formId может не совпасть с getCurrentRegistryFormId() (slot vs schema id).
    // Numeric id: в открытом диалоге без override не резолвим «чужой» слот.
    // Name-based: как двухаргументный setVisible — всегда retry без formId.
    const isNameBasedId = parseNumericId(id) == null
    const dialogOpenWithoutOverride =
      pageDataSlotOverrideStack.value.length === 0 && Boolean(appStore.dialogIds?.length)
    if (isNameBasedId || !dialogOpenWithoutOverride) {
      target = resolveVisibilityTarget({
        ...buildVisibilityResolveParams({ level, id }),
        formId: undefined,
      })
    }
  }

  const numericId = parseNumericId(id)
  const isElementLevel = levelNorm === 'element' && numericId != null
  const isElementByName = levelNorm === 'element' && numericId == null

  if (target) {
    if (target.kind === 'page') {
      applyLayoutPageVisibility(id, isVisible)
      return
    }
    if (target.kind === 'panel') {
      if (numericId != null) {
        if (!applyPanelVisibility(String(numericId), isVisible)) {
          // Registry match via resolve may still have a live setVisible (type coercion).
          target.setVisible(isVisible)
          rememberDeferredPanelVisibility(numericId, isVisible)
        }
        persistPanelVisibilityState(numericId, isVisible)
      } else {
        target.setVisible(isVisible)
      }
      // Propagate by panel id (4292), not registry key (2610:4292).
      // Skip on applyDispatchedVisibilities replay — see SetVisibleByLevelOptions.
      if (propagateChildren) {
        const panelKey = numericId != null ? String(numericId) : target.registryKey
        propagateVisibilityToScopedChildren(panelKey, isVisible)
        // Panel.vue v-if unmounts children — registry propagate alone cannot revive shells.
        syncPanelChildrenShellVisibility(panelChildMembershipIndex, panelKey, isVisible, {
          setElementShellVisibleById: (id, visible) => setElementShellVisibleById(id, visible),
          rememberDeferredElementShellById: (id, visible) =>
            rememberDeferredElementShellById(id, { visible }),
        })
      }
      return
    }
    target.setVisible(isVisible)
    if (target.kind === 'action' && numericId != null) {
      persistActionVisibilityState(numericId, isVisible)
      // Inline actions читают deferred (peek), не только mounted MAction registry.
      rememberDeferredActionVisibility(numericId, isVisible)
    }
    if (target.kind === 'element') {
      const resolvedElementId = target.elementId ?? numericId
      // Labels bind v-show to elementShellVisibleByElementId — must update for name-based setVisible too.
      if (resolvedElementId != null && String(resolvedElementId).trim() !== '') {
        setElementShellVisibleById(resolvedElementId, isVisible)
        elementVisibilityHooksByElementId.get(String(resolvedElementId))?.(isVisible)
      }
      const nameFromId = typeof id === 'string' && !/^\d+$/.test(String(id).trim()) ? String(id).trim() : ''
      persistClientElementVisibilityState({
        elementId: resolvedElementId,
        elementName: nameFromId || target.registryKey,
        visible: isVisible,
        levelNorm,
      })
    }
  }

  // Label (registerSimpleLabel): hook по elementId — надёжнее registry target.setVisible.
  // Также stash для replay при повторном registerElement.
  if (isElementLevel) {
    const idKey = String(numericId)
    setElementShellVisibleById(numericId!, isVisible)
    persistClientElementVisibilityState({
      elementId: numericId,
      visible: isVisible,
      levelNorm,
    })
    const hookById = elementVisibilityHooksByElementId.get(idKey)
    if (hookById) {
      hookById(isVisible)
      return
    } else if (!target) {
      rememberDeferredElementShellById(numericId!, { visible: isVisible })
      return
    }
    return
  }

  // Name-based element: every registry match + Label shell (v-show by elementId).
  if (isElementByName) {
    const namedMatches = findRegisteredElementsByName(elements.value, idStr)
    for (const { entry } of namedMatches) {
      entry.setVisible?.(isVisible)
      const resolvedElementId = entry.elementId
      if (resolvedElementId != null && String(resolvedElementId).trim() !== '') {
        setElementShellVisibleById(resolvedElementId, isVisible)
        elementVisibilityHooksByElementId.get(String(resolvedElementId))?.(isVisible)
      }
      persistClientElementVisibilityState({
        elementId: resolvedElementId as string | number | undefined,
        elementName: idStr,
        visible: isVisible,
        levelNorm,
      })
    }
    if (applyNameBasedElementVisibilityFallback(idStr, isVisible, { rememberScoped: false })) {
      if (namedMatches.length === 0) {
        persistClientElementVisibilityState({
          elementName: idStr,
          visible: isVisible,
          levelNorm,
        })
      }
      return
    }
    if (namedMatches.length > 0) {
      return
    }
    persistClientElementVisibilityState({
      elementName: idStr,
      visible: isVisible,
      levelNorm,
    })
    rememberDeferredElementShell(idStr, { visible: isVisible })
    return
  }

  if (target) {
    return
  }

  if (levelNorm === 'action' && numericId != null) {
    rememberDeferredActionVisibility(numericId, isVisible)
    return
  }

  if (levelNorm === 'panel' && numericId != null) {
    if (!applyPanelVisibility(String(numericId), isVisible)) {
      rememberDeferredPanelVisibility(numericId, isVisible)
    }
    persistPanelVisibilityState(numericId, isVisible)
    if (propagateChildren) {
      syncPanelChildrenShellVisibility(panelChildMembershipIndex, numericId, isVisible, {
        setElementShellVisibleById: (id, visible) => setElementShellVisibleById(id, visible),
        rememberDeferredElementShellById: (id, visible) =>
          rememberDeferredElementShellById(id, { visible }),
      })
    }
    return
  }

  if (levelNorm === 'page') {
    applyLayoutPageVisibility(id, isVisible)
  }
}

const applyPanelVisibility = (panelId: string, isVisible: boolean): boolean => {
  let applied = false
  for (const entries of Object.values(panelsVisibilityRegistry.value)) {
    for (const entry of entries) {
      // String-coerce: match resolveRegistryVisibility.findRegisteredPanel (number vs string ids).
      if (panelIdsEqual(entry.panelId, panelId) && entry.setVisible) {
        entry.setVisible(isVisible)
        applied = true
      }
    }
  }
  // processStates uses the same shell map for PANEL ids. Without this, stale
  // jsResult PANEL is_visible:false re-hides 4505/4506/4421 after client setVisible(true)
  // (elements persist a shell; panels did not).
  setElementShellVisibleById(panelId, isVisible)
  return applied
}

/**
 * Двухаргументный setVisible: en.setVisible("websiteUrlTable", true).
 * Также formProvider.setVisible из useFormStates (по element_name из states).
 * Числовая строка в name трактуется как platform id (совместимость).
 */
const setVisibleRecursive = (name: string, isVisible: boolean, scope?: { formId?: string; panelId?: string }) => {
  let applied = false

  const overlay = overlaysRegistry.value[name]
  if (overlay?.setVisible) {
    overlay.setVisible(isVisible)
    delete pendingOverlayVisibility.value[name]
    delete pendingOverlayVisibility.value[overlayPendingIdKey(overlay.overlayId)]
    return
  }

  const overlayById = Object.values(overlaysRegistry.value).find(
    (entry) => entry.overlayId === name || entry.name === name,
  )
  if (overlayById?.setVisible) {
    overlayById.setVisible(isVisible)
    delete pendingOverlayVisibility.value[overlayById.name]
    delete pendingOverlayVisibility.value[overlayPendingIdKey(overlayById.overlayId)]
    return
  }

  const numericPanelId = parseInt(name, 10)
  if (!Number.isNaN(numericPanelId) && applyPanelVisibility(String(numericPanelId), isVisible)) {
    applied = true
  }

  let target = resolveVisibilityTarget(
    buildVisibilityResolveParams({
      name,
      formId: scope?.formId,
    }),
  )
  if (!target) {
    target = resolveVisibilityTarget(
      buildVisibilityResolveParams({
        name,
        formId: undefined,
      }),
    )
  }
  const numericElementId = parseNumericId(name)
  const pageApplied = applyLayoutPageVisibility(name, isVisible)
  if (target) {
    target.setVisible(isVisible)
    applied = true
    rememberPersistentScopedVisibility(name, isVisible)
    if (target.kind === 'page' && !pageApplied) {
      persistPageVisibilityState(
        {
          id: String(target.elementId ?? target.registryKey ?? ''),
          jsName: parseNumericId(name) == null ? name : '',
        },
        isVisible,
      )
    }
    if (target.kind === 'element') {
      const resolvedElementId = target.elementId ?? numericElementId
      if (resolvedElementId != null && String(resolvedElementId).trim() !== '') {
        setElementShellVisibleById(resolvedElementId, isVisible)
        elementVisibilityHooksByElementId.get(String(resolvedElementId))?.(isVisible)
      }
      persistClientElementVisibilityState({
        elementId: resolvedElementId,
        elementName: name,
        visible: isVisible,
        levelNorm: 'element',
      })
    }
  } else if (pageApplied) {
    applied = true
  } else if (
    applyNameBasedElementVisibilityFallback(name, isVisible, { scopeFormId: scope?.formId })
  ) {
    applied = true
  } else if (!scope && numericElementId != null) {
    rememberDeferredElementShellById(numericElementId, { visible: isVisible })
    rememberPersistentScopedVisibility(name, isVisible)
  } else {
    // Tiles setInlineVisibility: scoped name до registerAction/registerElement — отложить, не терять.
    rememberDeferredElementShell(name, { visible: isVisible }, scope?.formId)
    rememberPersistentScopedVisibility(name, isVisible)
  }

  // Parent→children only for panel/form id (el.panelId === name).
  // With scope (Tiles setInlineVisibility), must not apply to all panel children.
  if (!scope) {
    propagateVisibilityToScopedChildren(name, isVisible)
  }

  if (applied) return

  // Overlay pending только по имени overlay; numeric element id сюда не попадает (WUI-385)
  if (!scope && !parseNumericId(name)) {
    setPendingOverlayVisibility(name, undefined, isVisible)
  }
}

/**
 * Set editable state for an element, panel, or form.
 * Like Swing VisualEntity.setEditable(), propagates to all children.
 */
const setEditableRecursive = (
  name: string,
  isEditable: boolean,
  scope?: { formId?: string; panelId?: string },
  options?: { persist?: boolean },
) => {
  const nameStr = String(name ?? '').trim()
  const element = elements.value[nameStr]

  if (element && element.setEditable) {
    element.setEditable(isEditable)
  } else if (!scope) {
    const registryFormId = getCurrentRegistryFormId()
    rememberDeferredElementShell(nameStr, { editable: isEditable }, registryFormId || undefined)
  }

  for (const [elementName, el] of Object.entries(elements.value)) {
    const belongsToScope =
      (scope?.formId &&
        String(el.formId ?? '') === String(scope.formId) &&
        (!scope.panelId || String(el.panelId ?? '') === String(scope.panelId))) ||
      (!scope &&
        (String(el.panelId ?? '') === nameStr || String(el.formId ?? '') === nameStr))

    if (belongsToScope && el.setEditable && elementName !== nameStr) {
      el.setEditable(isEditable)
    }
  }

  if (options?.persist !== false && !scope) {
    persistClientElementEditableState({ elementName: nameStr, editable: isEditable })
  }
}

/** Swing JSContext.setEditable(level, id, editable). */
const setEditableByLevel = (level: string, id: string | number, isEditable: boolean) => {
  const idStr = String(id ?? '').trim()
  const levelNorm = normalizeRegistryLevel(level)
  const numericId = parseNumericId(id)

  if (levelNorm === 'panel') {
    const panelKey = numericId != null ? String(numericId) : idStr
    setEditableRecursive(panelKey, isEditable, undefined, { persist: false })
    rememberDeferredElementShell(panelKey, { editable: isEditable })
    persistPanelEditableState(panelKey, isEditable)
    return
  }

  let target = resolveVisibilityTarget(buildVisibilityResolveParams({ level, id }))
  if (!target) {
    target = resolveVisibilityTarget({
      ...buildVisibilityResolveParams({ level, id }),
      formId: undefined,
    })
  }
  if (target?.kind === 'element') {
    const entry = elements.value[target.registryKey]
    if (typeof entry?.setEditable === 'function') {
      entry.setEditable(isEditable)
    } else {
      setEditableRecursive(target.registryKey, isEditable, undefined, { persist: false })
    }
    persistClientElementEditableState({
      elementId: target.elementId ?? numericId,
      elementName: numericId == null ? idStr : String(target.registryKey ?? ''),
      editable: isEditable,
    })
    rememberDeferredElementShell(target.registryKey, { editable: isEditable })
    return
  }

  setEditableRecursive(idStr, isEditable)
}

/**
 * Set enabled state for an element
 * Propagates to children when they have setEnable.
 */
const setEnableRecursive = (name: string, isEnabled: boolean, scope?: { formId?: string; panelId?: string }) => {
  const element = elements.value[name]
  if (element && typeof (element as any).setEnable === 'function') {
    (element as any).setEnable(isEnabled)
  } else if (!scope) {
    rememberDeferredElementShell(name, { enabled: isEnabled })
  }
  for (const [elementName, el] of Object.entries(elements.value)) {
    const belongsToScope =
      (scope?.formId && el.formId === scope.formId && (!scope.panelId || el.panelId === scope.panelId)) ||
      (!scope && (el.panelId === name || el.formId === name))
    if (belongsToScope && typeof (el as any).setEnable === 'function' && elementName !== name) {
      (el as any).setEnable(isEnabled)
    }
  }
}

/**
 * Установить фокус на элемент по имени.
 */
const findRegisteredElementByFieldName = (name: string) => {
  const direct = elements.value[name]
  if (direct) return direct

  for (const element of Object.values(elements.value)) {
    if (String(element?.fieldName ?? '').trim() === name) {
      return element
    }
  }
  for (const element of Object.values(elementsByKey.value)) {
    if (String(element?.fieldName ?? '').trim() === name) {
      return element
    }
  }
  return undefined
}

const focusFieldDom = (fieldName: string): boolean => {
  const slug = String(fieldName ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
  if (!slug) return false

  const candidates = document.querySelectorAll(`[data-testid$="-${slug}"]`)
  for (const node of candidates) {
    const focusable =
      node instanceof HTMLInputElement ||
      node instanceof HTMLTextAreaElement ||
      node instanceof HTMLSelectElement ||
      node instanceof HTMLButtonElement
        ? node
        : node.querySelector('input,textarea,select,button,[tabindex]:not([tabindex="-1"])')
    if (focusable instanceof HTMLElement) {
      focusable.focus()
      return true
    }
  }
  return false
}

const requestFocusOnElement = (name: string) => {
  const element = findRegisteredElementByFieldName(name)
  if (element?.receiveFocus) {
    element.receiveFocus()
    return
  }
  // используется в TreeTable
  if (/^\d+$/.test(String(name).trim())) {
    const searchField = elements.value.searchString
    if (searchField?.receiveFocus) {
      searchField.receiveFocus()
      return
    }
  }
  if (focusFieldDom(name)) {
    return
  }
  console.warn(`[GlobalProvider.requestFocus] Element "${name}" not found or doesn't support focus`)
}

const scrollTo = scrollToElement

/**
 * Set error message for an element by name (VisualEntity.setError).
 */
const setElementError = (name: string, error: string | null) => {
  const element = findRegisteredElementByFieldName(name)
  if (element && element.setError) {
    element.setError(error)
  } else {
    console.warn(`[GlobalProvider.setElementError] Element "${name}" not found or has no setError hook`)
  }
}

/** Swing JSContext.setFormError — page-scoped form error banner. */
const setFormError = (message: string | null, slotId?: string | number) => {
  const fromEn =
    typeof en.value?.getPageDataSlotId === 'function' ? en.value.getPageDataSlotId() : ''
  const slot = String(slotId ?? appStore.currentPageId ?? fromEn ?? '').trim()
  if (!slot) return
  setFormErrorForSlot(slot, message)
}

/**
 * Remove text field border (SwingText.removeTextFieldBorder).
 */
const removeTextFieldBorder = (name: string) => {
  const element = elements.value[name]
  if (element && element.removeTextFieldBorder) {
    element.removeTextFieldBorder()
  } else {
    console.warn(`[GlobalProvider.removeTextFieldBorder] Element "${name}" not found or has no removeTextFieldBorder hook`)
  }
}

/**
 * Set cursor position to end of text field (SwingText.setCursorPositionToEnd).
 */
const setCursorPositionToEnd = (name: string) => {
  const element = elements.value[name]
  if (element && element.setCursorPositionToEnd) {
    element.setCursorPositionToEnd()
  } else {
    console.warn(`[GlobalProvider.setCursorPositionToEnd] Element "${name}" not found or has no setCursorPositionToEnd hook`)
  }
}

const expandAllElement = (name: string) => {
  const element = elements.value[name]
  if (element && typeof element.expandAll === 'function') {
    element.expandAll()
  }
}

const openSuggesterElement = (name: string, formId?: string) => {
  const registryId = String(formId ?? getCurrentRegistryFormId() ?? '').trim()
  const slotId = String(getCurrentPageSlotId() ?? '').trim()
  const candidates = [registryId, slotId, ''].filter((id, i, arr) => id !== '' || i === arr.length - 1)

  for (const fid of candidates) {
    const element = resolveOpenSuggesterRegistryEntry(
      elements.value as Record<string, unknown>,
      name,
      fid || undefined,
    )
    if (element?.openSuggester) {
      element.openSuggester()
      return
    }
  }
}

const reloadTreeTableNodeElement = async (
  name: string,
  rowNum: number,
  needReloadParent?: boolean,
  selectedNodeData?: unknown,
) => {
  const element = elements.value[name]
  if (element && typeof element.reloadNode === 'function') {
    return element.reloadNode(rowNum, needReloadParent ?? false, selectedNodeData)
  }
  console.warn(
    `[GlobalProvider.reloadTreeTableNodeElement] Element "${name}" not found or has no reloadNode hook`,
  )
}

const setMultipleLimitElement = (name: string, limit: number) => {
  const element = elements.value[name]
  if (element && typeof element.setMultipleLimit === 'function') {
    element.setMultipleLimit(limit)
  }
}

const setAutocompleteElement = (name: string, mode: string) => {
  const element = elements.value[name]
  if (element && typeof element.setAutocompleteMode === 'function') {
    element.setAutocompleteMode(mode)
  }
}

const setEnableSuggestEditElement = (name: string, enabled: boolean) => {
  const element = elements.value[name]
  if (element && typeof element.setEnableSuggestEdit === 'function') {
    element.setEnableSuggestEdit(enabled)
  }
}

const setHasEditorElement = (name: string, hasEditor: boolean) => {
  const element = elements.value[name]
  if (element && typeof element.setHasEditor === 'function') {
    element.setHasEditor(hasEditor)
  }
}

const setVerifyRegexp = (name: string, regexp: string) => {
  const element = elements.value[name]
  if (element && typeof element.setVerifyRegexp === 'function') {
    element.setVerifyRegexp(regexp)
  } else {
    console.warn(`[GlobalProvider.setVerifyRegexp] Element "${name}" not found or has no setVerifyRegexp hook`)
  }
}

const setMultipleDelimiterElement = (name: string, delimiter: string) => {
  const element = elements.value[name]
  if (element && typeof element.setMultipleDelimiter === 'function') {
    element.setMultipleDelimiter(delimiter)
  }
}

const appendBytesElement = (name: string, fileName: string, base64Data: string): void => {
  const element = elements.value[name]
  if (element && typeof element.appendBytes === 'function') {
    // Добавляет именно файл
    element.appendBytes(fileName, base64Data)
  } else {
    console.warn(`[appendBytes] Element not found or appendBytes hook not registered: ${name}`)
  }
}

const appendFileCopiesElement = (name: string, attachments: any): void => {
  const element = elements.value[name]

  if (element && typeof element.appendFileCopies === 'function') {
    element.appendFileCopies(attachments)
  } else {
    console.warn(`[appendFileCopies] Element not found or appendFileCopies hook not registered: ${name}`)
  }
}

const setPageError = (pageId: number, hasError: boolean) => {

  for (const [name, element] of Object.entries(elements.value)) {
    if (Number(element.elementId) === pageId && element.setError) {
      element.setError(hasError)
      return
    }
  }
  console.warn(`[GlobalProvider.setPageError] Page with ID ${pageId} not found`)
}

/**
 * Очистить данные элемента.
 */
const emptyElement = (name: string) => {
  const directElement = elements.value[name]
  const matchedElement =
    directElement ||
    Object.values(elements.value).find((candidate: any) => {
      if (!candidate || typeof candidate !== 'object') return false
      const candidateName = String(candidate.fieldName ?? candidate.name ?? '').trim()
      return candidateName !== '' && candidateName === String(name).trim()
    })

  if (matchedElement && typeof matchedElement.empty === 'function') {
    matchedElement.empty()
    return
  }

  const currentFormId = getCurrentRegistryFormId()
  const currentPanelId = getDefaultPanelId()
  if (currentFormId && currentPanelId) {
    setValue({
      formId: currentFormId,
      panelId: currentPanelId,
      elementId: String(name),
      value: { [name]: null },
    })
    return
  }

  console.warn(`[GlobalProvider.empty] Element "${name}" not found in registry`)
}

const processOpenUrls = (openUrls?: OpenUrl) => {
  if (!Array.isArray(openUrls) || openUrls.length === 0) return

  for (const urlData of openUrls) {
    let finalUrl = urlData.url

    if (urlData.parameters?.length) {
      const params = new URLSearchParams()
      for (const param of urlData.parameters) {
        params.append(String(param.name), String(param.value))
      }
      finalUrl += (finalUrl.includes('?') ? '&' : '?') + params.toString()
    }

    window.open(finalUrl)
  }
}

provide(RegisterFormKey, registerForm)
provide(UnRegisterFormKey, unRegisterForm)
provide(RegisterPanelKey, registerPanel)
provide(UnRegisterPanelKey, unRegisterPanel)
provide(RegisterOverlayKey, registerOverlay)
provide(UnRegisterOverlayKey, unregisterOverlay)
provide(RegisterElementKey, registerElement)
provide(UnRegisterElementKey, unRegisterElement)
provide(RegisterActionKey, registerAction)
provide(UnRegisterActionKey, unRegisterAction)
provide(SetValueKey, setValue)
/** @legacy inject — предпочтителен `actionService.executeAction` через `MAction`. */
provide(OnActionKey, onAction)
provide(OnElementKey, onElement)
provide(GetFormKey, getForm)
provide(GetPageDataKey, getPageData)
provide(RunActionByNameKey, runActionByName)
provide(RunActionKey, runAction)
provide(RunEventKey, runEvent)
provide(RunCodeKey, runCode)
provide(ReloadElementKey, reloadElementViaControls)
provide(EnKey, computed(() => en.value))
provide(OpenUrls, processOpenUrls)

provide(PageDataKey, () => {
  const currentSlotId = getCurrentPageSlotId()
  return appStore.pagesData[currentSlotId] || {}
})
provide(PageDataSlotKey, () => getCurrentPageSlotId())
provide(PageRegistryFormIdKey, () => getCurrentRegistryFormId())
provide(PageWindowInstanceIdKey, () => getCurrentWindowInstanceId())

const refillElementViaControls = (
  elementName: string,
  fieldData: any,
  formId?: string,
  elementOverride?: any,
  mutationMeta?: { cause?: AttachmentStoreMutationCause; detail?: Record<string, unknown> },
) => {
  const resolveRefillFormId = (raw: string): string => {
    const fid = String(raw ?? '').trim()
    if (!fid) return fid
    if (forms.value[fid]) return fid

    const activeRegistryFormIds = new Set<string>()
    for (const el of Object.values(elements.value) as any[]) {
      const ef = String((el as any)?.formId ?? '').trim()
      if (ef) activeRegistryFormIds.add(ef)
    }
    if (activeRegistryFormIds.has(fid)) return fid

    let mapped = ''
    for (const [slotId, pd] of Object.entries(appStore.pagesData as Record<string, any>)) {
      if (!pd || typeof pd !== 'object') continue
      const slot = String(slotId ?? '').trim()
      const registryId = resolveElementRegistryFormId(pd, slot)
      const aliases = [
        slot,
        registryId,
        pd?.jsResult?.form_id,
        pd?.form?.form?._attributes?.id,
        pd?.form?._attributes?.id,
        pd?.formId,
      ]
        .map((v: any) => String(v ?? '').trim())
        .filter(Boolean)
      if (!aliases.includes(fid)) continue

      const candidates = [registryId, slot]
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
      const hit = candidates.find((c) => forms.value[c] || activeRegistryFormIds.has(c))
      if (hit) {
        mapped = hit
        break
      }
      mapped = candidates[0] || fid
    }
    return mapped || fid
  }

  const currentFormIdRaw = formId || (route?.params?.id as string) || Object.keys(appStore.pagesData)[0] || ''
  const currentFormId = resolveRefillFormId(String(currentFormIdRaw))

  const curFid = String(currentFormId ?? '')
  const extraMaps = [elementsByKey.value as Record<string, any>]
  let targets: any[] = elementOverride
    ? [elementOverride]
    : collectRegisteredElementsByName(
        elements.value as Record<string, any>,
        elementName,
        currentFormId,
        extraMaps,
      )
  if (!elementOverride && targets.length === 0) {
    let element =
      resolveRegisteredElementByName(elements.value as Record<string, any>, elementName, currentFormId, extraMaps) ??
      elements.value[elementName]
    if (element && curFid && String((element as any).formId ?? '') !== curFid) {
      const formElements = Object.entries(elements.value).find(([name, el]: [string, any]) =>
        name === elementName && String((el as any).formId ?? '') === curFid
      )
      if (formElements) {
        element = formElements[1] as any
      } else {
        element = undefined
      }
    }

    if (!element && elementName) {
      const withRefill = Object.entries(elements.value).filter(
        ([name, el]: [string, any]) => name === elementName && typeof el?.refill === 'function',
      )
      if (withRefill.length === 1) {
        element = withRefill[0][1] as any
      } else if (withRefill.length > 1 && curFid) {
        const activeFormIds = new Set(Object.keys(forms.value))
        const pick = withRefill.find(([, el]) => activeFormIds.has(String((el as any).formId ?? '')))
        if (pick) {
          element = pick[1] as any
        }
      }
    }
    if (element) targets = [element]
  }

  // Prepare field data in correct format
  let typedFieldData: any
  if (fieldData !== undefined && fieldData !== null) {
    if (typeof fieldData === 'object' && !Array.isArray(fieldData) && 'value' in fieldData && 'type' in fieldData) {
      // Already in correct format
      typedFieldData = fieldData
    } else {
      // Wrap it
      typedFieldData = {
        type: typeof fieldData === 'string' ? 'String' :
          typeof fieldData === 'number' ? (Number.isInteger(fieldData) ? 'Long' : 'Double') :
            typeof fieldData === 'boolean' ? 'Boolean' :
              Array.isArray(fieldData) ? 'Array' : 'Object',
        value: fieldData
      }
    }
  } else {
    // fieldData is undefined or null - pass null as value
    typedFieldData = {
      type: 'String',
      value: null
    }
  }

  if (targets.length === 0) {
    return
  }

  const incomingIsTable = isUniDataTableFieldShape(typedFieldData)
  targets = targets.filter(
    (element) =>
      !shouldSkipTilesCloneScalarRefill({
        elementType: (element as { elementType?: unknown }).elementType,
        getType: (element as { getType?: () => unknown }).getType,
        fieldName: (element as { fieldName?: unknown }).fieldName,
        name: (element as { name?: unknown }).name,
        ctx: (element as { ctx?: unknown }).ctx,
        incomingIsTable,
      }),
  )
  if (targets.length === 0) {
    return
  }

  for (const element of targets) {
    const elNorm = element
      ? String(
          (element as any).elementType ??
            ((element as any).getType?.() as string) ??
            '',
        ).toUpperCase()
      : ''
    if (
      element &&
      REFILL_SKIP_EMPTY_IF_SLOT_HAS_VALUE_TYPES.has(elNorm) &&
      fieldEntryLooksEmptyIncoming(typedFieldData)
    ) {
      const panelId = String((element as any).panelId ?? '')
      const elId = String((element as any).elementId ?? '')
      const slotField =
        forms.value[curFid]?.[panelId]?.[elId]?.[elementName]
      const cur =
        slotField && typeof slotField === 'object' && 'value' in slotField
          ? (slotField as { value: unknown }).value
          : slotField
      if (fieldEntryHasPresentableValue({ value: cur })) {
        continue
      }
    }

    if (element && typeof element.refill === 'function') {
      let outField = typedFieldData
      if (typedFieldData?.type === 'UniDataTable') {
        outField = stripLeakedFormRows(typedFieldData) as typeof typedFieldData
      }
      const panelId = String((element as any).panelId ?? '')
      const elId = String((element as any).elementId ?? '')
      const currentSlotField =
        forms.value[curFid]?.[panelId]?.[elId]?.[elementName]
      if (fieldEntryEqual(currentSlotField, outField)) {
        continue
      }
      if (elNorm === 'FILEATTACHMENTS') {
        const gpCause = mutationMeta?.cause ?? 'gp-refill'
        markAttachmentStoreMutationCause({
          fieldName: elementName,
          elementId: elId,
          cause: gpCause,
          detail: { formId: curFid, ...mutationMeta?.detail },
        })
      }
      const refillData: Record<string, any> = { [elementName]: outField }
      try {
        element.refill(refillData)
      } catch (error) {
        console.error(`[GlobalProvider.refillElement] Error calling element.refill:`, error)
        throw error
      }
    }
  }
}

const lazyPageReloadInFlight = new Set<string>()

/**
 * Ленивая вкладка формы
 */
const reloadLazyPage = async (pageId: number, formIdArg?: string) => {
  const fid = String(formIdArg || getCurrentRegistryFormId() || '')
  const pid = Number(pageId)
  if (!fid || !Number.isFinite(pid)) return

  const flightKey = `${fid}:${pid}`
  if (lazyPageReloadInFlight.has(flightKey)) return
  lazyPageReloadInFlight.add(flightKey)

  try {
    const pageData = findPageDataByFormId(fid)
    const sessionId = (pageData as any)?.sessionId

    // Если sessionId отсутствует или невалиден, пропустить reload (форма ещё инициализируется)
    if (sessionId == null || String(sessionId).trim() === '' || String(sessionId) === '-1') {
      console.warn(`[GlobalProvider.reloadLazyPage] Skipping reload for page ${pid} — sessionId not ready for form ${fid}`)
      return
    }

    const formPayload = getForm({ formId: fid }) || {}
    const reloadRow = {
      fields: {
        id: { type: 'Long', value: pid },
        level: { type: 'String', value: 'page' },
      },
      errors: {},
      attributes: {},
    }
    const out = {
      ...formPayload,
      [IKey.ACTION]: { type: 'String', value: IKey.PAGE_RELOAD },
      reload__elements: {
        type: 'UniDataTable',
        value: {
          rows: [reloadRow],
          columns: [] as unknown[],
          attributes: {},
          empty: false,
        },
      },
    }

    const result = await queuedReloadElement(
      {
        elementId: pid,
        formId: fid,
        sessionId: sessionId,
        type: currentPlatform.value ?? undefined,
      },
      out,
    )

    if (!result) return

    const basePageData = findPageDataByFormId(fid)
    if (!basePageData) return

    const updatedPageData = {
      ...basePageData,
      sessionId: result.sessionId ?? basePageData.sessionId,
      uniData: {
        ...basePageData.uniData,
        clientData: {
          ...basePageData.uniData?.clientData,
          params: {
            ...basePageData.uniData?.clientData?.params,
            ...(result.clientData?.params || {}),
          },
        },
        fields: {
          ...basePageData.uniData?.fields,
          fields: {
            ...basePageData.uniData?.fields?.fields,
            ...(result.fields?.fields || {}),
          },
        },
      },
      jsResult: result.jsResult
        ? {
            ...basePageData.jsResult,
            ...result.jsResult,
            states: mergeExchangeStatesForPageUpdate(result, basePageData),
            changed_data: result.jsResult.changed_data || basePageData.jsResult?.changed_data,
            event_listeners: mergeEventListenersForPageUpdate(
              basePageData.jsResult?.event_listeners,
              result.jsResult.event_listeners,
            ),
          }
        : basePageData.jsResult,
    }

    const lazyPageSlotId = String(pageId || getCurrentPageSlotId() || '')

    writePageDataForForm(fid, updatedPageData, 'GlobalProvider.reloadLazyPage', {
      originSlotId: lazyPageSlotId || undefined,
      response: result,
    })

    const incomingStates = normalizeExchangeStates(result)
    if (incomingStates.length > 0) {
      await applyIncomingExchangeStates({
        appStore,
        slotId: lazyPageSlotId || String(fid),
        registryFormId: fid,
        incomingStates,
        changedData: updatedPageData.jsResult?.changed_data,
        processStates: formStatesResult.processStates,
        setPageDataSource: 'GlobalProvider.reloadLazyPage',
        resolveStoreKey: {
          originSlotId: pageId || getCurrentPageSlotId(),
          responseSchemaFormId: resolveResponseSchemaFormId(result),
        },
      })
    }

    const currentKey = appStore.currentPageId != null ? String(appStore.currentPageId) : ''
    syncPageDataToRegistry(fid, currentKey || undefined, {
      originOverlayPageId: toFormOverlayPageId(pid),
    })
  } catch (error) {
    console.error('[GlobalProvider.reloadLazyPage]', fid, pageId, error)
  } finally {
    lazyPageReloadInFlight.delete(flightKey)
  }
}

const collectLiveFieldOverridesForForm = (
  formId?: string | number,
  options?: { pageDataSlot?: string },
) =>
  collectLiveFieldOverrides(formId, elements.value, {
    ...options,
    extraMaps: [elementsByKey.value],
  })

async function applyPendingTreeTableSearchForForm(formId: string | number): Promise<void> {
  const wanted = String(formId)
  const pageData = findPageDataByFormId(formId)
  const pageFields = pageData
    ? overlayAuthoritativeFieldKeys(
        mergePageDataFieldSnapshot(pageData),
        resolveJsResultChangedDataFieldMap(pageData?.jsResult?.changed_data),
      )
    : {}
  const preferredTable = resolveTreeTableFieldNameFromPageFields(pageFields)
  const slotId = String(pageData?.id ?? pageData?.formId ?? wanted)

  for (const el of Object.values(elements.value)) {
    if (el == null) continue
    const elFormId = String(el.formId ?? '')
    if (
      elFormId !== wanted &&
      !elementRegistryFormIdsMatch(elFormId, wanted, slotId, pageData)
    ) {
      continue
    }
    const type = el.getType?.() ?? el.elementType ?? ''
    if (type !== 'TREETABLE') continue
    const fieldName = String(el.fieldName ?? '').trim()
    if (preferredTable && fieldName && fieldName !== preferredTable) continue
    if (typeof el.applyTreeTableSearch !== 'function') continue
    await el.applyTreeTableSearch(pageFields)
    return
  }
}

const flushPendingEditableTableEdits = async (formId?: string | number): Promise<boolean> =>
  flushPendingEditableTableEditsFromRegistry(elements.value, formId, nextTick)

const retryEditableTableOnChangeForForm = async (formId?: string | number): Promise<void> => {
  const fid = formId != null ? String(formId) : ''
  await retryEditableTableOnChangeIfNeeded(elements.value, formId, runAction, (fieldName) => {
    if (!fid) return undefined
    const formData = getForm({ formId: fid, flatten: true }) as Record<string, unknown>
    return formData?.[fieldName]
  })
}

const formProvider = {
  getForm,
  getPageData,
  collectLiveFieldOverrides: collectLiveFieldOverridesForForm,
  setValue,
  registerElement,
  unRegisterElement,
  isPageLazy: () => false,
  reloadLazyPage,
  runActionByName,
  setActionData,
  getEventActionForElement,
  runAction,
  runEvent,
  runCode,
  getTilesRowScope: () => en.value?._getTilesRowScope?.() ?? null,
  trackPendingPlatformAction: trackPendingElementEvent,
  getPersistedScopedVisibility,
  getTilesRowActionVisibilityStore: () => tilesRowActionVisibilityStore,
  getTilesInlineAfterLoadEpoch: () => tilesInlineAfterLoadEpoch,
  getTilesInlineAfterLoadReloadTables: () => tilesInlineAfterLoadReloadTables,
  onElement,
  reloadElement: reloadElementViaControls,
  reloadPanel: reloadPanelViaControls,
  // JSContext.reloadForm → POST /platform/reload/form + refill. STACK (actionId=-1) only for Window.doStackAction.
  reloadForm: reloadFormViaControls,
  reloadFormStack: reloadFormViaStackAction,
  resolveElementTableSwingMethod,
  refillElement: refillElementViaControls,
  queuePendingOverlayRefill,
  flushPendingOverlayRefills,
  rememberShownOverlayPageIdsFromVisibilities,
  getOverlayDuplicatedFieldNames: overlayDuplicatedFieldNamesForForm,
  setVisible: setVisibleRecursive,
  setVisibleByLevel,
  elementShellVisibleByElementId,
  setElementShellVisibleById,
  // Регистрация панели/элемента с файлами для защиты от скрытия
  registerPanelWithFiles: (panelId: string | number) => {
    const key = String(panelId ?? '').trim()
    if (key) {
      panelsWithFiles.value.add(key)
      // Сразу установить видимость чтобы переопределить старое false
      elementShellVisibleByElementId.value = {
        ...elementShellVisibleByElementId.value,
        [key]: true,
      }
    }
  },
  unregisterPanelWithFiles: (panelId: string | number) => {
    const key = String(panelId ?? '').trim()
    if (key) panelsWithFiles.value.delete(key)
  },
  registerElementWithFiles: (elementId: string | number) => {
    const key = String(elementId ?? '').trim()
    if (key) {
      elementsWithFiles.value.add(key)
      // Сразу установить видимость чтобы переопределить старое false
      elementShellVisibleByElementId.value = {
        ...elementShellVisibleByElementId.value,
        [key]: true,
      }
    }
  },
  unregisterElementWithFiles: (elementId: string | number) => {
    const key = String(elementId ?? '').trim()
    if (key) elementsWithFiles.value.delete(key)
  },
  setEditable: setEditableRecursive,
  setEditableByLevel,
  setEnable: setEnableRecursive,
  requestFocus: requestFocusOnElement,
  empty: emptyElement,
  setElementError,
  setFormError,
  removeTextFieldBorder,
  setCursorPositionToEnd,
  expandAllElement,
  openSuggesterElement,
  reloadTreeTableNode: reloadTreeTableNodeElement,
  setMultipleLimitElement,
  setAutocompleteElement,
  setEnableSuggestEditElement,
  setHasEditorElement,
  setMultipleDelimiterElement,
  appendBytesElement,
  appendFileCopiesElement,
  setVerifyRegexp,
  scrollToElement: scrollTo,
  getActions: () => actions.value,
  registerTableSelection,
  getTableSelection,
  getTableSelections,
  getElements: () => elements.value,
  getElementsByKey: () => elementsByKey.value,
  getOverlays: () => overlaysRegistry.value,
  applyPendingTreeTableSearch: applyPendingTreeTableSearchForForm,
  flushPendingEditableTableEdits,
  retryEditableTableOnChangeIfNeeded: retryEditableTableOnChangeForForm,
  awaitPendingElementEvent,
  syncPageDataToRegistry,
  pushPageDataSlotOverride,
  popPageDataSlotOverride,
  applyPendingActionStates: (actionId: number, actionName: string, formId: string) => {
    // set by useFormStates
  },
  applyPendingElementStates: (elementId: number | string, elementName: string, formId: string) => {
    // set by useFormStates
  },
  resyncTabSlotFields: undefined as
    | ((pageSlotId: string, options?: { skipSnapshotRestore?: boolean }) => void)
    | undefined,
  replayPageVisibilityStatesForSlot: undefined as
    | ((pageSlotId: string) => void)
    | undefined,
}

const formStatesResult = useFormStates({ formProvider })
if (formStatesResult.applyPendingActionStates) {
  formProvider.applyPendingActionStates = formStatesResult.applyPendingActionStates
}
if (formStatesResult.applyPendingElementStates) {
  formProvider.applyPendingElementStates = formStatesResult.applyPendingElementStates
}

const { attachHotKeys, detachHotKeys } = useHotKeys({
  actions,
  appStore,
  currentPlatform,
  findPageDataByFormId,
  formProvider,
  formStates: formStatesResult,
  getActionDialogClosedScript,
  getCurrentFormId,
  getCurrentRegistryFormId,
  getForm,
  getPageData,
  getTableSelection,
  getTableSelections,
  onAction,
  reloadElementViaControls,
  router,
  runCode,
  tempActions,
})

provide(FormProviderKey, formProvider)

const syncedPageDataSideEffectSignatures = new Map<string, string>()

const resolveRegistryFormIdFromPageEntry = (pageData: any, pageSlotId: string): string => {
  const formId = pageData?.jsResult?.form_id ||
    pageData?.status ||
    pageData?.form?.form?._attributes?.id ||
    pageData?.form?.form?._attributes?.fid ||
    pageData?.formId ||
    pageData?.$fid ||
    pageSlotId
  return String(formId)
}

/** Синхронизация forms registry по storage id (без привязки к elements[].pageDataSlot). */
const syncFormsStorageFromPageFields = (
  registryFormId: string,
  pageFields: Record<string, any>,
  options?: { originOverlayPageId?: string },
) => {
  const storageId = resolveFormsStorageFormId(registryFormId)
  const formPanels = forms.value[storageId]
  if (!formPanels || typeof formPanels !== 'object') return

  const registered = Object.values(elementsByKey.value) as Array<{
    panelId?: unknown
    elementId?: unknown
    pageId?: unknown
    fieldName?: unknown
  }>
  const originOverlayPageId = normalizeOriginOverlayPageId(
    options?.originOverlayPageId,
    registered.map((el) => el.pageId),
  )

  for (const [panelId, panel] of Object.entries(formPanels)) {
    if (!panel || typeof panel !== 'object') continue
    for (const [elementId, elementSlot] of Object.entries(panel as Record<string, unknown>)) {
      if (!elementSlot || typeof elementSlot !== 'object') continue
      const elementPageId = findRegisteredElementPageId(registered, panelId, elementId)
      for (const baseFieldName of Object.keys(elementSlot as Record<string, unknown>)) {
        if (
          !shouldSyncPageFieldToElementPage(elementPageId, originOverlayPageId, {
            fieldName: baseFieldName,
            registeredElements: registered,
            pendingOverlayRefillFields: pendingOverlayRefillFieldNames(),
            layoutDuplicatedFieldNames: overlayDuplicatedFieldNamesForForm(registryFormId),
          })
        ) {
          continue
        }
        assignPageFieldToRegistrySlot(
          elementSlot as Record<string, unknown>,
          baseFieldName,
          pageFields,
        )
      }
    }
  }
}

const refillTabSlotElementsFromPageData = (
  pageSlotId: string,
  pageFields: Record<string, any>,
  _pageData: any,
  registryFormId: string,
) => {
  const pageSlot = String(pageSlotId ?? '').trim()
  if (!pageSlot || !pageFields || typeof pageFields !== 'object') return

  for (const [, element] of Object.entries(elementsByKey.value)) {
    if (!element || typeof (element as any).refill !== 'function') continue
    if (!pageSlotElementRefillMatch(element as any, pageSlot)) continue
    const elementType = String(normalizeFormElementType(element as any) ?? '').toUpperCase()
    // Скалярные интерактивные контролы обновляются прямыми state/apply-путями.
    // Широкий side-effect refill из pageData пропускаем, чтобы не было двойных записей и мигания.
    // TABLE/TREETABLE после en.reloadElement уже в registry через onReloaded.setValue;
    // поздний refill из mergePageDataFieldSnapshot (часто sticky empty в changed_data)
    // затирает 0→1 / восстанавливает 1→0.
    if (
      elementType === 'TEXT' ||
      elementType === 'PASSWORD' ||
      elementType === 'TEXTAREA' ||
      elementType === 'MULTILINETEXT' ||
      elementType === 'LISTBOX' ||
      elementType === 'COMBOBOX' ||
      elementType === 'TABLE' ||
      elementType === 'TREETABLE' ||
      elementType === 'GROUPTABLE' ||
      elementType === 'EDITABLE'
    ) {
      continue
    }

    const baseFieldName = String(
      (element as any).fieldName ?? (element as any).name ?? '',
    )
    if (!baseFieldName) continue

    const fieldData = resolvePageFieldEntry(pageFields, baseFieldName)
    if (fieldData === undefined) continue

    try {
      refillElementViaControls(baseFieldName, fieldData, registryFormId, element, {
        cause: 'resync-tab-slot',
        detail: { pageSlotId: pageSlot },
      })
    } catch {
      /* non-fatal UI sync */
    }
  }
}

/** Вернуть поля из pagesData в element registry (после смены вкладки KeepAlive не перечитывает forms). */
const resyncTabSlotFields = (
  pageSlotId: string,
  options?: { skipSnapshotRestore?: boolean },
) => {
  const id = String(pageSlotId ?? '').trim()
  if (!id) return

  if (!options?.skipSnapshotRestore) {
    appStore.restorePageSlotFieldSnapshot(id)
  }

  const pageData = appStore.pagesData[id]
  if (!pageData) return

  const pageFields = mergePageDataFieldSnapshot(pageData)
  if (!pageFields || Object.keys(pageFields).length === 0) return

  const formId = resolveRegistryFormIdFromPageEntry(pageData, id)
  syncedPageDataSideEffectSignatures.delete(id)
  syncPageDataToRegistry(formId, id, {
    forceFieldNames: Object.keys(pageFields),
  })
  syncFormsStorageFromPageFields(formId, pageFields)
  refillTabSlotElementsFromPageData(id, pageFields, pageData, formId)
}

formProvider.resyncTabSlotFields = resyncTabSlotFields

const replayPageVisibilityStatesForSlot = (pageSlotId: string) => {
  const id = String(pageSlotId ?? '').trim()
  if (!id) return
  const pageData = appStore.pagesData[id] as Record<string, any> | undefined
  if (!pageData) return

  const states = Array.isArray(pageData.jsResult?.states) ? pageData.jsResult.states : []
  const contextFormId = String(
    pageData?.jsResult?.form_id ??
      pageData?.status ??
      pageData?.formId ??
      id,
  ).trim()

  replayStoredPageVisibilityStates(states, {
    setVisibleByLevel,
    setVisibleByName: (name, visible) => {
      setVisibleRecursive(name, visible, contextFormId ? { formId: contextFormId } : undefined)
    },
    setEditableByLevel,
    setEditableByName: (name, editable) => {
      setEditableRecursive(name, editable, contextFormId ? { formId: contextFormId } : undefined)
    },
  })
}

formProvider.replayPageVisibilityStatesForSlot = replayPageVisibilityStatesForSlot

const pageIdsForStoreSideEffects = (): string[] => {
  const ids = new Set<string>()
  const cur = appStore.currentPageId
  if (cur && cur !== 'home') {
    ids.add(String(cur))
  }
  const committed = appStore.lastPageDataCommitId
  if (committed) {
    ids.add(String(committed))
  }
  if (cur) {
    const adj = (appStore.lastAdjacentPageIdByParent as Record<string, string>)?.[String(cur)]
    if (adj) {
      ids.add(String(adj))
    }
  }
  // Диалоги (напр. 2771) должны регистрировать onServerEvent, даже если не current tab.
  for (const dialogId of appStore.dialogIds ?? []) {
    if (dialogId) ids.add(String(dialogId))
  }
  return [...ids]
}

const applyPageDataSideEffects = (pageId: string, pageData: any) => {
  if (!pageData || !formHasRenderableLayout(pageData.form)) return

  const formId = pageData?.jsResult?.form_id ||
    pageData?.status ||
    pageData?.form?.form?._attributes?.id ||
    pageData?.form?.form?._attributes?.fid ||
    pageData?.formId ||
    pageData?.$fid ||
    pageId

  if (!formId) return

  const formIdStr = String(formId)
  const pageSessionId = readPlatformSessionIdFromPageEntry(pageData)
  if (pageSessionId) {
    for (const el of Object.values(elements.value)) {
      if (String(el?.formId ?? '') === formIdStr) {
        el.platformSessionId = pageSessionId
      }
    }
  }

  const scheduleRegisterOnServerEvent = () => {
    if (isFormPageBootstrapActive(pageId)) {
      setTimeout(scheduleRegisterOnServerEvent, 300)
      return
    }
    void registerOnServerEvent(formIdStr, pageData, pageId)
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(scheduleRegisterOnServerEvent, { timeout: 2000 })
  } else {
    setTimeout(scheduleRegisterOnServerEvent, 0)
  }

  const pageFields = mergePageDataFieldSnapshot(pageData)
  if (!pageFields || Object.keys(pageFields).length === 0) return

  const sideEffectSignature = buildPageDataCommitSignature(pageId, pageData)
  const fieldsChanged = syncedPageDataSideEffectSignatures.get(pageId) !== sideEffectSignature
  const isActiveSlot = String(appStore.currentPageId ?? '') === String(pageId)
  const hasElementsOnSlot = Object.values(elements.value).some((el) =>
    pageSlotElementRefillMatch(el as any, pageId),
  )
  const isCommittedSlot = String(appStore.lastPageDataCommitId ?? '') === String(pageId)
  const shouldRefillElements = isActiveSlot || hasElementsOnSlot || isCommittedSlot
  syncedPageDataSideEffectSignatures.set(pageId, sideEffectSignature)

  if (!fieldsChanged) return

  const runSync = () => {
    if (isFormPageBootstrapActive(pageId)) {
      setTimeout(runSync, 300)
      return
    }
    syncPageDataToRegistry(formId, pageId, {
      forceFieldNames: shouldRefillElements ? Object.keys(pageFields) : undefined,
    })
    syncFormsStorageFromPageFields(formId, pageFields)
    if (shouldRefillElements) {
      refillTabSlotElementsFromPageData(pageId, pageFields, pageData, formId)
    }
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(runSync, { timeout: 800 })
  } else {
    setTimeout(runSync, 0)
  }
}

let pagesDataRevisionEffectsQueued = false

const applyPagesDataRevisionSideEffects = () => {
  const newPagesData = appStore.pagesData
  for (const pageId of pageIdsForStoreSideEffects()) {
    applyPageDataSideEffects(pageId, newPagesData[pageId])
  }

  Array.from(syncedPageDataSideEffectSignatures.keys()).forEach((pageId) => {
    if (pageId in newPagesData) return
    syncedPageDataSideEffectSignatures.delete(pageId)
    serverEventRegistry.clearForm(String(pageId))
    serverEventScripts.delete(String(pageId))
  })
}

watch(
  () => appStore.pagesDataRevision,
  () => {
    if (pagesDataRevisionEffectsQueued) return
    pagesDataRevisionEffectsQueued = true
    queueMicrotask(() => {
      pagesDataRevisionEffectsQueued = false
      applyPagesDataRevisionSideEffects()
    })
  },
  { immediate: true },
);

watch(
  serverEventContextSignature,
  () => {
    applyPagesDataRevisionSideEffects()
  },
  { immediate: false },
)

const scheduleResyncActiveTab = (pageSlotId: string, attempt = 0) => {
  const id = String(pageSlotId ?? '').trim()
  if (!id) return

  const pageData = appStore.pagesData[id]
  if (!pageData) return

  const formId = resolveRegistryFormIdFromPageEntry(pageData, id)
  const hasElements = Object.values(elements.value).some((el) => {
    if (!el) return false
    const elPageSlot = String((el as any).pageDataSlot ?? '').trim()
    const elFormId = String(el.formId ?? '')
    return elPageSlot === id || elFormId === id || elFormId === formId
  })

  if (!hasElements && attempt < 24) {
    requestAnimationFrame(() => scheduleResyncActiveTab(id, attempt + 1))
    return
  }

  resyncTabSlotFields(id)
}

watch(
  () => appStore.currentPageId,
  (newPageId, oldPageId) => {
    if (!newPageId || newPageId === oldPageId) return
    scheduleResyncActiveTab(String(newPageId))
  },
  { immediate: false },
);

watch(
  () => elementsRegistryRevision.value,
  () => {
    const currentPageId = appStore.currentPageId;
    if (!currentPageId) return;

    const pageData = appStore.pagesData[currentPageId];
    if (!pageData) return;

    const formId = pageData?.jsResult?.form_id ||
      pageData?.status ||
      pageData?.form?.form?._attributes?.id ||
      pageData?.form?.form?._attributes?.fid ||
      pageData?.formId ||
      pageData?.$fid ||
      currentPageId;

    // Check if there are any elements registered for this form
    const hasElements = Object.values(elements.value).some(el => {
      if (!el) return false;
      const elFormId = el.formId?.toString();
      return elFormId === currentPageId?.toString() || elFormId === formId?.toString();
    });

    // Only sync if we have elements and pageData has fields
    const pageFields = mergePageDataFieldSnapshot(pageData);
    if (hasElements && Object.keys(pageFields).length > 0) {
      // Use debounced sync to avoid multiple calls during element registration
      if (syncTimeouts.value[formId]) {
        clearTimeout(syncTimeouts.value[formId]);
      }

      syncTimeouts.value[formId] = setTimeout(() => {
        delete syncTimeouts.value[formId];
        syncPageDataToRegistry(formId, currentPageId);
      }, 50); // 50ms delay to allow all elements to register
    }

    // Also sync adjacent (split) page if present
    const lastAdjacentId = (appStore.lastAdjacentPageIdByParent as Record<string, string>)?.[currentPageId];
    if (lastAdjacentId && appStore.pagesData[lastAdjacentId]) {
      const adjacentData = appStore.pagesData[lastAdjacentId];
      const adjacentFormId = adjacentData?.jsResult?.form_id ||
        adjacentData?.status ||
        adjacentData?.form?.form?._attributes?.id ||
        adjacentData?.form?.form?._attributes?.fid ||
        adjacentData?.formId ||
        adjacentData?.$fid ||
        lastAdjacentId;

      const hasAdjacentElements = Object.values(elements.value).some(el => {
        if (!el) return false;
        const elFormId = el.formId?.toString();
        return elFormId === lastAdjacentId?.toString() || elFormId === adjacentFormId?.toString();
      });

      const adjacentFields = mergePageDataFieldSnapshot(adjacentData);
      if (hasAdjacentElements && Object.keys(adjacentFields).length > 0) {
        if (syncTimeouts.value[adjacentFormId]) {
          clearTimeout(syncTimeouts.value[adjacentFormId]);
        }

        syncTimeouts.value[adjacentFormId] = setTimeout(() => {
          delete syncTimeouts.value[adjacentFormId];
          syncPageDataToRegistry(adjacentFormId, lastAdjacentId);
        }, 50);
      }
    }
  },
  { immediate: false },
);

/**
 * Helper function to convert values to UniData format
 * TODO: заменить везде на вызовы непосредственно TypeSerializer.serialize
 * @param value
 */
const toUniDataField = (value: any): any => {
  return TypeSerializer.serialize(value)
}

/**
 * Helper преобразования File в объект для сериализации, тело в base64
 *
 * @param file
 */
const fileToBase64 = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

const resolveClientImportHandler = (methodName: string | null | undefined) => {
  const normalizedMethodName = typeof methodName === 'string' ? methodName.trim() : ''
  if (!normalizedMethodName) {
    return null
  }

  const imports = appStore?.imports || {}
  const handler = imports[normalizedMethodName]

  if (!handler || typeof handler.func !== 'function') {
    return null
  }

  return handler
}

const extractUniDataFields = (source: any): Record<string, any> => {
  if (!source || typeof source !== 'object') {
    return {}
  }

  const fields =
    source.fields?.fields ??
    source.fields ??
    source.clientData?.fields?.fields ??
    source.clientData?.fields

  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return {}
  }

  return fields
}

const normalizeUniDataFields = (source: any): Record<string, any> => {
  const normalized: Record<string, any> = {}

  for (const [key, value] of Object.entries(extractUniDataFields(source))) {
    normalized[key] = toUniDataField(value)
  }

  return normalized
}

const isUniDataResponseLike = (payload: any): boolean => {
  return Boolean(payload && typeof payload === 'object' && ('fields' in payload || 'clientData' in payload))
}

const toUniDataResponsePayload = (payload: any): any => {
  if (payload instanceof UniData) {
    return createBaseUniDataResponse({
      ...payload._getAllFields(),
    })
  }

  if (isUniDataResponseLike(payload)) {
    return payload
  }

  return createBaseUniDataResponse()
}

const mergeUniDataResponseFields = (
  payload: any,
  additionalFields: Record<string, any>,
  options: { preserveExisting?: boolean } = {}
) => {
  const responsePayload = toUniDataResponsePayload(payload)
  const currentFields = responsePayload.fields?.fields && typeof responsePayload.fields.fields === 'object'
    ? responsePayload.fields.fields
    : {}

  responsePayload.fields = {
    ...responsePayload.fields,
    fields: options.preserveExisting
      ? {
        ...additionalFields,
        ...currentFields,
      }
      : {
        ...currentFields,
        ...additionalFields,
      },
  }

  return responsePayload
}

const hasIncomingChangedData = (request: JsRequest): boolean => {
  const source = request.changedData ?? request.changed_data
  return Object.keys(extractUniDataFields(source)).length > 0
}

const collectChangedUniDataFields = (
  beforeFields: Record<string, any>,
  afterFields: Record<string, any>
): Record<string, any> => {
  const changedFields: Record<string, any> = {}
  const allFieldNames = new Set([
    ...Object.keys(beforeFields || {}),
    ...Object.keys(afterFields || {}),
  ])

  for (const fieldName of allFieldNames) {
    const beforeField = beforeFields?.[fieldName]
    const afterField = afterFields?.[fieldName]

    if (beforeField === undefined && afterField !== undefined) {
      changedFields[fieldName] = toUniDataField(afterField)
      continue
    }

    if (beforeField !== undefined && afterField === undefined) {
      changedFields[fieldName] = {
        value: null,
        type: beforeField?.type || 'String',
      }
      continue
    }

    if (
      beforeField !== undefined &&
      afterField !== undefined &&
      (
        JSON.stringify(beforeField?.value) !== JSON.stringify(afterField?.value) ||
        beforeField?.type !== afterField?.type
      )
    ) {
      changedFields[fieldName] = toUniDataField(afterField)
    }
  }

  return changedFields
}

const createClientImportEnContext = (request: JsRequest) => {
  const currentSlotId = getCurrentPageSlotId()
  const currentPanelId = getDefaultPanelId()
  const pageData = appStore.pagesData[currentSlotId]
  const imports = appStore?.imports || {}

  const registrations: Array<[string, any]> = [
    ...Object.entries(imports),
  ].filter(([, item]) => item && typeof (item as any).func === 'function')

  const baseFields = hasIncomingChangedData(request)
    ? normalizeUniDataFields(request.changedData ?? request.changed_data)
    : {
      ...normalizeUniDataFields(pageData?.uniData),
      ...normalizeUniDataFields(pageData?.jsResult?.changed_data),
    }

  return createClientImportRuntime({
    currentFormId: currentSlotId,
    currentPanelId,
    pageData,
    baseFields,
    registrations,
    elements: elements.value,
    actions: actions.value,
    getForm,
    verifyForm,
    runActionByName,
    runFormEvent,
    invokeBusEvent,
    reloadElement: reloadElementViaControls,
    runAction,
    empty: emptyElement,
  })
}

function isJsRequestElementOrActionClass(className: string): boolean {
  const n = (className || '').trim()
  if (!n) return false
  return (
    n === 'ELEMENT' ||
    n === 'ACTION' ||
    n.endsWith('.ELEMENT') ||
    n.endsWith('.ACTION')
  )
}

function getFormActionsListForCurrentPage(): any[] {
  const fid = getCurrentRegistryFormId()
  const pd =
    findPageDataByFormId(fid) ||
    (appStore.pagesData as Record<string, any>)[getCurrentPageSlotId()] ||
    (appStore.pagesData as Record<string, any>)[fid]
  const raw = pd?.form?.actions?.action
  if (!raw) return []
  return Array.isArray(raw) ? raw : [raw]
}

/** Минимальный контекст как у registerAction(MAction), собранный из узла action формы. */
function buildActionCtxFromFormMeta(actionMeta: any, formIdStr: string): any {
  const layout = actionMeta?.layout || {}
  const layoutLevel = layout.level?._text || 'form'
  const layoutId = layout.id?._text ?? layout.id
  const idNum = Number(actionMeta?._attributes?.id)
  return {
    id: Number.isFinite(idNum) ? idNum : 0,
    name: actionMeta?.name?._text || '',
    formId: formIdStr,
    panelId: '',
    elementId:
      layoutLevel === 'element' && layoutId != null && layoutId !== ''
        ? String(layoutId)
        : undefined,
    pageId: layoutLevel === 'page' && layoutId != null && layoutId !== '' ? String(layoutId) : undefined,
    level: layoutLevel,
    data: actionMeta,
    selectedData: null,
  }
}

/** doAction по WS: object_id обычно id операции; допускаем id элемента из layout/реестра (как в Swing). */
function resolveRegisteredActionForWsDoAction(objectId: number): any | undefined {
  const direct = actions.value[String(objectId)]
  if (direct) return direct
  const list = Object.values(actions.value) as any[]
  const byLayout = list.find((entry) => {
    const layout = entry?.data?.layout
    const lid = layout?.id?._text ?? layout?.id
    return lid != null && lid !== '' && Number(lid) === objectId
  })
  if (byLayout) return byLayout
  const byElement = list.find(
    (entry) => entry?.elementId != null && Number(entry.elementId) === objectId,
  )
  if (byElement) return byElement

  const fromMeta = getFormActionsListForCurrentPage().find(
    (a) => Number(a?._attributes?.id) === objectId,
  )
  if (fromMeta) {
    return buildActionCtxFromFormMeta(fromMeta, getCurrentRegistryFormId())
  }
  return undefined
}

/**
 * Определяет `sessionId` платформы для обработки JS-запроса по WebSocket.
 *
 * Используется перед вызовом хуков элемента (`getData` / `getFullData` у `FILEATTACHMENTS` и др.):
 * id передаётся в `runWithJsRequestSessionId`, чтобы upload/download вложений в `ElementRenderer`
 * попали в ту же сессию, что и форма на middleware (action 27259, executeScript).
 *
 * Порядок fallback:
 * 1. `request.coreSessionId` — id из топика `/client/task/js-request/{coreId}`;
 * 2. `registered.platformSessionId` — снимок при `registerElement`;
 * 3. `sessionId` / `cloneOf` страницы по `registered.formId`;
 * 4. сессия текущего слота вкладки;
 * 5. `appStore.currentSessionId`;
 * 6. первый валидный `sessionId` среди `pagesData`.
 *
 * @returns нормализованный id или `null`, если сессию определить нельзя
 */
const findRegisteredElementByObjectId = (
  rawObjectId: unknown,
): { formId?: string | number | null; platformSessionId?: string | null; elementId?: unknown } | undefined => {
  const objectId =
    rawObjectId != null && rawObjectId !== '' ? Number(rawObjectId) : Number.NaN
  if (!Number.isFinite(objectId)) return undefined
  for (const elem of Object.values(elements.value)) {
    if (Number(elem?.elementId) === objectId) {
      return elem as {
        formId?: string | number | null
        platformSessionId?: string | null
        elementId?: unknown
      }
    }
  }
  return undefined
}

/**
 * Поиск FILEATTACHMENTS по `object_id` из WS (WUI-316).
 * Возвращает `fileAttachmentHooks.onOfficeCallbackStatus` → `handleOfficeCallbackStatus`.
 */
const findElementForOfficeRouter = (objectId: number) => {
  for (const elem of Object.values(elements.value)) {
    if (Number(elem?.elementId) === objectId) {
      const onOfficeCallbackStatus =
        typeof elem.onOfficeCallbackStatus === 'function'
          ? elem.onOfficeCallbackStatus
          : undefined
      return {
        fieldName: elem.fieldName,
        elementType: elem.elementType,
        onOfficeCallbackStatus,
      }
    }
  }
  return undefined
}

let disposeOfficeEventRouter: (() => void) | null = null
let disposeFormEventRouter: (() => void) | null = null

const resolveJsRequestSessionId = (
  request: JsRequest,
  registered?: { formId?: string | number | null; platformSessionId?: string | null },
): string | null => {
  const fromCore = normalizePlatformSessionId(request.coreSessionId)
  if (fromCore) return fromCore

  const fromRegistered = normalizePlatformSessionId(registered?.platformSessionId)
  if (fromRegistered) return fromRegistered

  if (registered?.formId != null && String(registered.formId).trim() !== '') {
    const pageForElement = findPageDataByFormId(String(registered.formId))
    const fromPage = readPlatformSessionIdFromPageEntry(pageForElement)
    if (fromPage) return fromPage
  }

  const regByObjectId = findRegisteredElementByObjectId(request.objectId ?? request.object_id)
  if (regByObjectId?.formId != null && String(regByObjectId.formId).trim() !== '') {
    const pageForElement = findPageDataByFormId(String(regByObjectId.formId))
    const fromPage = readPlatformSessionIdFromPageEntry(pageForElement)
    if (fromPage) return fromPage
    const fromReg = normalizePlatformSessionId(regByObjectId.platformSessionId)
    if (fromReg) return fromReg
  }

  const slotPage = appStore.pagesData[getCurrentPageSlotId()]
  const fromSlot = readPlatformSessionIdFromPageEntry(slotPage)
  if (fromSlot) return fromSlot

  const fromCurrent = normalizePlatformSessionId(appStore.currentSessionId)
  if (fromCurrent) return fromCurrent

  for (const pageEntry of Object.values(appStore.pagesData || {})) {
    const sid = readPlatformSessionIdFromPageEntry(pageEntry)
    if (sid) return sid
  }

  return normalizePlatformSessionId(getJsRequestSessionId())
}

useScriptAutoHandler(async (request: JsRequest) => {
  // Handle case where args is missing from server (server-side serialization issue)
  // TODO: выделить класс на основе JsRequest, который будет инкапсулировать работу с форматом данных вебсокета,
  //  скрывая работу с сериализацией и прочую обработку и выдавая чистые данные
  const args = Array.isArray(request.args) ? [...request.args] : []
  const argsParams = request.param_types ?? request.paramTypes ?? []
  args.forEach((item: any, index, arr) => {
    if (item && typeof item === 'object' && 'type' in item && 'value' in item) {
      arr[index] = TypeSerializer.deserialize(item)
      return
    }
    arr[index] = TypeSerializer.deserializeLegacy(item, argsParams[index])
  })
  const {className, methodName} = request
  const endJsRequestMeasure = startWorkflowMeasure('websocket.jsRequest', {
    className,
    methodName,
    objectId: request.objectId ?? request.object_id,
    args: profilePayload(args),
    changedData: profilePayload(request.changedData ?? request.changed_data),
  })

  const registeredForRequest = findRegisteredElementByObjectId(
    request.objectId ?? request.object_id,
  )
  return runWithJsRequestSessionId(
    resolveJsRequestSessionId(request, registeredForRequest),
    async () => {
  try {
  const withSocketId = (payload: any = {fields: {fields: {}}}) => {
    const nextPayload = payload && typeof payload === 'object' ? {...payload} : {fields: {fields: {}}}
    if (!nextPayload.fields || typeof nextPayload.fields !== 'object') {
      nextPayload.fields = {fields: {}}
    }
    if (!nextPayload.fields.fields || typeof nextPayload.fields.fields !== 'object') {
      nextPayload.fields.fields = {}
    }
    nextPayload.fields.fields.__socketId = {
      type: 'String',
      value: request.socketId || 'unknown',
    }
    return nextPayload
  }

  // Normalize className - remove any whitespace and handle both short and full class names
  const normalizedClassName = className ? String(className).trim() : ''

  if (import.meta.env.DEV) {
    console.debug('[JsRequest]', {
      className: normalizedClassName,
      methodName,
      objectId: request.objectId ?? request.object_id,
    })
  }

  // Check if it's a JSContext request (handle various formats)
  // Check if className contains "JSContext" (most permissive check)
  const isJSContextRequest = normalizedClassName.includes('JSContext')

  if (isJSContextRequest) {
    const formId = getCurrentRegistryFormId()
    const pageDataSlot = getCurrentPageSlotId()
    const pageData = appStore.pagesData[pageDataSlot]
    const { IKey } = await import('@/common/constants/ikey')
    const getCurrentJsContextElementByName = (rawName: unknown) => {
      const elementName = String(rawName ?? '').trim()
      if (!elementName) return undefined
      const registeredElement = elements.value[elementName]
      if (!registeredElement) return undefined
      const registeredElementFormId = String(registeredElement.formId ?? '').trim()
      const currentRegistryFormId = String(formId ?? '').trim()
      if (registeredElementFormId === currentRegistryFormId) {
        return registeredElement
      }
      if (
        elementRegistryFormIdsMatch(
          registeredElementFormId,
          currentRegistryFormId,
          pageDataSlot,
          pageData,
        )
      ) {
        return registeredElement
      }
      return undefined
    }

    const createJsContextResponse = (
      fields: Record<string, any> = {},
      options: { hasFormError?: boolean; responseValue?: any } = {}
    ) => {
      const nextFields = {
        ...fields,
      }

      if (options.responseValue !== undefined) {
        nextFields[IKey.JS_WEB_RESPONSE] = toUniDataField(options.responseValue)
      }

      return createBaseUniDataResponse(
        ensureUniDataWireFields(nextFields),
        Boolean(options.hasFormError),
      )
    }
    const normalizeCollectedJsContextData = (source: any): Record<string, any> => {
      const raw = toRaw(source)
      if (!raw || typeof raw !== 'object' || Array.isArray(raw) || raw instanceof String) {
        return {}
      }
      return raw
    }
    const getPutDataFallbackFields = (): Record<string, any> => {
      const pageFieldSnapshot = pageData
        ? (mergePageDataFieldSnapshot(pageData) as Record<string, any>)
        : {}
      return mergeFieldMaps(
        pageFieldSnapshot,
        resolveJsResultChangedDataFieldMap(pageData?.jsResult?.changed_data),
        normalizeUniDataFields(pageData?.currentChainData),
        normalizeUniDataFields(getActiveExecuteScriptCurrentData()),
        normalizeUniDataFields(request.changedData ?? request.changed_data),
      )
    }

    const resolveFormsRegistryField = (requestedName: string): unknown => {
      const regElement = getCurrentJsContextElementByName(requestedName)
      if (!regElement?.formId || !regElement?.panelId || !regElement?.elementId) {
        return undefined
      }
      const slice = getForm({
        formId: regElement.formId,
        panelId: regElement.panelId,
        elementId: regElement.elementId,
        flatten: true,
      })
      if (
        slice &&
        typeof slice === 'object' &&
        Object.prototype.hasOwnProperty.call(slice, requestedName)
      ) {
        return slice[requestedName]
      }
      return undefined
    }

    const getMiddleScriptFieldSnapshot = (): Record<string, any> =>
      normalizeUniDataFields(request.changedData ?? request.changed_data)

    const withPutDataFallback = (
      source: any,
      requestedFieldName?: unknown
    ): Record<string, any> => {
      const collectedFields = normalizeCollectedJsContextData(source)
      const requestedName =
        typeof requestedFieldName === 'string' ? requestedFieldName.trim() : ''
      const incomingFields = getPutDataFallbackFields()
      const formsRegistryField =
        requestedName !== '' ? resolveFormsRegistryField(requestedName) : undefined

      const merged = mergePutDataCollectedFields(
        collectedFields,
        requestedName,
        incomingFields,
        formsRegistryField,
      ) as Record<string, any>

      return overlayAuthoritativeFieldKeys(merged, getMiddleScriptFieldSnapshot()) as Record<string, any>
    }
    const normalizeCollectedMultiSelectField = (
      source: Record<string, any>,
      requestedFieldName: unknown,
      element: any,
    ): Record<string, any> => {
      const requestedName =
        typeof requestedFieldName === 'string' ? requestedFieldName.trim() : ''
      if (
        !requestedName ||
        !isMultiSelectElement(element) ||
        !Object.prototype.hasOwnProperty.call(source, requestedName)
      ) {
        return source
      }

      return {
        ...source,
        [requestedName]: toMultiSelectTypedValue(source[requestedName]),
      }
    }
    const normalizePutFormListboxFields = (source: Record<string, any>): void => {
      const listboxKeys = Object.keys(source).filter((key) => key.endsWith('__listbox'))
      for (const key of listboxKeys) {
        const baseName = key.slice(0, -'__listbox'.length)
        if (!baseName) continue

        const baseEntry = source[baseName]
        const baseValue =
          baseEntry && typeof baseEntry === 'object' && 'value' in baseEntry
            ? (baseEntry as { value?: unknown }).value
            : baseEntry
        const isEmptyBase =
          baseValue === null ||
          baseValue === undefined ||
          (typeof baseValue === 'string' && baseValue.trim() === '')

        if (isEmptyBase) {
          const listboxEntry = source[key]
          const listboxValue =
            listboxEntry && typeof listboxEntry === 'object' && 'value' in listboxEntry
              ? (listboxEntry as { value?: unknown }).value
              : listboxEntry
          const rows = (listboxValue as { rows?: unknown[] } | null)?.rows
          const firstRow = Array.isArray(rows) && rows.length > 0 ? rows[0] : undefined
          const fields =
            firstRow && typeof firstRow === 'object' && 'fields' in (firstRow as object)
              ? (firstRow as { fields?: Record<string, any> }).fields
              : undefined
          const codeTyped = fields?.code ?? fields?.kind ?? fields?.value ?? fields?._id
          if (codeTyped && typeof codeTyped === 'object' && 'value' in codeTyped) {
            source[baseName] = {
              type: typeof codeTyped.type === 'string' ? codeTyped.type : 'String',
              value: codeTyped.value ?? '',
            }
          } else if (codeTyped !== undefined) {
            source[baseName] = setType(codeTyped)
          }
        }

        for (const suffix of ['__listbox', '__items', '__action']) {
          delete source[`${baseName}${suffix}`]
        }
      }
    }
    const resolveSelectedPageId = (_formArea?: unknown) => {
      const selectedPageId =
        pageData?.jsResult?.page_id ??
        pageData?.currentPageId ??
        appStore.currentPageId ??
        formId

      const numericPageId = Number(selectedPageId)
      return Number.isFinite(numericPageId) ? numericPageId : 0
    }

    switch (methodName) {
      case 'putFormData':
        const verify = args[0] !== undefined ? Boolean(args[0]) : false

        // Как в Swing: при verify=true проверяем все контролы текущей формы.
        let verified = true
        if (verify) {
          verified = verifyElements(elements.value, (element) => {
            return element.formId === formId
          })
        }

        // Важно: putFormData не должен опираться только на getForm/снимок pageData.
        // Для text onChange (путь executeScript) актуальный ввод может быть в:
        // - active executeScript currentData
        // - request.changedData / changed_data
        // - changed-снимке страницы
        // - live DOM (TEXT skipStore) — как En.putFormData / createClientDataUniData
        // Используем withPutDataFallback для слияния слоёв и защиты от устаревших значений формы.
        const normalizedAllFormData = normalizeCollectedJsContextData(
          getForm({ formId, flatten: true, pageDataSlot }),
        )
        const mergedFormData = mergeLiveFieldOverridesIntoTypedFormData(
          withPutDataFallback(normalizedAllFormData),
          collectLiveFieldOverrides(formId, elements.value, {
            extraMaps: [elementsByKey.value],
          }),
        )
        // Swing putFormData synth Action: leaf table getData (checkbox / EDITABLE / omit TABLE).
        const synthSelections = resolveFormTableSelectionsForSynthCollect({
          formId,
          getTableSelections,
        })
        const formDataForPut = applySynthActionTableLeafCollect(
          mergedFormData as Record<string, unknown>,
          {
            elements: elements.value as Record<string, unknown>,
            extraMaps: [elementsByKey.value as Record<string, unknown>],
            formId,
            selections: synthSelections,
          },
        ) as Record<string, any>
        applyListboxWireTypesFromListboxFields(formDataForPut)
        normalizePutFormListboxFields(formDataForPut)

        const cleanFields: Record<string, any> = {}
        for (const key of Object.keys(formDataForPut)) {
          cleanFields[key] = toUniDataField(formDataForPut[key])
        }

        // Скрытый/пустой FILEATTACHMENTS: getForm не отдаёт поле → пустая UniDataTable из form XML.
        applyFileAttachmentsToClientDataFieldsFromRegistry(
          cleanFields,
          formDataForPut,
          elements.value as Record<string, unknown>,
          { formRoot: pageData?.form },
        )
        // DateRange: UI {from,to} → Swing __first/__second в putFormData snapshot.
        applyDateRangeToClientDataFieldsFromRegistry(
          cleanFields,
          formDataForPut,
          elements.value as Record<string, unknown>,
          getForm,
        )
        applyFormIdentificatorAndListboxGetDataToFields(
          cleanFields,
          elements.value as Record<string, unknown>,
          {
            formId,
            formRoot: pageData?.form,
            extraMaps: [elementsByKey.value],
          },
        )

        cleanFields['__VERIFIED'] = {
          type: 'Boolean',
          value: verified
        }

        return createJsContextResponse(cleanFields, {
          hasFormError: !verified,
          responseValue: verified,
        })

      case 'putData':
        if (args.length === 0) {
          console.warn('[JSContext] putData() called with no arguments - this method doesn\'t exist in JSContext')
          return createJsContextResponse()
        } else if (args.length === 2 && typeof args[0] === 'string' && typeof args[1] === 'number') {
          // putData(String level, long id)
          const level = args[0]
          const id = args[1]

          let collectedData: Record<string, any> = {}

          if (level === 'form') {
            collectedData = getForm({ formId: id, flatten: true })
          } else if (level === 'panel') {
            collectedData = getForm({ formId, panelId: id, flatten: false })
          } else if (level === 'element') {
            const element = Object.values(elements.value).find((el: any) => el.elementId === id)
            if (element) {
              collectedData = getForm({
                formId: element.formId,
                panelId: element.panelId,
                elementId: id,
                flatten: true
              })
            }
          } else if (level === 'page') {
            collectedData = getPageData({ formId, pageId: id, flatten: true })
          } else {
            // Fallback to pageData fields
            collectedData = toRaw(pageData?.uniData?.fields?.fields || {})
          }

          collectedData = withPutDataFallback(collectedData)
          // Swing putData(level,id): synthetic Action → leaf table getData.
          collectedData = applySynthActionTableLeafCollect(
            collectedData as Record<string, unknown>,
            {
              elements: elements.value as Record<string, unknown>,
              extraMaps: [elementsByKey.value as Record<string, unknown>],
              formId,
              selections: resolveFormTableSelectionsForSynthCollect({ formId, getTableSelections }),
            },
          ) as Record<string, any>

          const cleanLevelFields: any = {}
          for (const key of Object.keys(collectedData)) {
            cleanLevelFields[key] = toUniDataField(collectedData[key])
          }

          return createJsContextResponse(cleanLevelFields)
        } else if (args.length === 2 && typeof args[0] === 'string' && typeof args[1] === 'boolean') {
          // putData(String name, boolean verify)
          const elementName = args[0]
          const shouldVerify = args[1]

          let verified = true
          if (shouldVerify) {
            const element = getCurrentJsContextElementByName(elementName)
            if (element && typeof element.verify === 'function') {
              verified = element.verify()
            }
          }

          const element = getCurrentJsContextElementByName(elementName)
          let collectedData: Record<string, any> = {}

          if (element && element.formId && element.panelId && element.elementId) {
            collectedData = getForm({
              formId: element.formId,
              panelId: element.panelId,
              elementId: element.elementId,
              flatten: true
            })
          } else {
            collectedData = toRaw(pageData?.uniData?.fields?.fields || {})
          }

          collectedData = withPutDataFallback(collectedData, elementName)
          collectedData = normalizeCollectedMultiSelectField(collectedData, elementName, element)

          const cleanVerifyFields: any = {}
          for (const key of Object.keys(collectedData)) {
            cleanVerifyFields[key] = toUniDataField(collectedData[key])
          }

          cleanVerifyFields['__VERIFIED'] = {
            type: 'Boolean',
            value: verified
          }

          return createJsContextResponse(cleanVerifyFields, {
            hasFormError: !verified,
            responseValue: verified,
          })
        } else if (args.length === 1) {
          const elementName = args[0]
          // Handle empty string or null name
          if (!elementName || (typeof elementName === 'string' && elementName.trim() === '')) {
            console.warn('[JSContext] putData() called with empty/null name - returning empty UniData')
            return createJsContextResponse()
          }

          const element = getCurrentJsContextElementByName(elementName)

          let collectedData: Record<string, any> = {}

          if (element && element.formId && element.panelId && element.elementId) {
            collectedData = getForm({
              formId: element.formId,
              panelId: element.panelId,
              elementId: element.elementId,
              flatten: true
            })
          } else {
            collectedData = toRaw(pageData?.uniData?.fields?.fields || {})
          }

          collectedData = withPutDataFallback(collectedData, elementName)
          collectedData = normalizeCollectedMultiSelectField(collectedData, elementName, element)

          const cleanPutDataFields: any = {}
          for (const key of Object.keys(collectedData)) {
            cleanPutDataFields[key] = toUniDataField(collectedData[key])
          }

          return createJsContextResponse(cleanPutDataFields)
        }
        break

      case 'putFullData':
        if (!args || args.length === 0 || args[0] === undefined || args[0] === null) {
          console.error('[JSContext] putFullData() called without element name argument. args:', args)
          return createJsContextResponse()
        }

        const fullDataName = String(args[0])
        const registeredFaForPutFullData = resolveFileAttachmentsPutFullDataElement(
          [elements.value, elementsByKey.value],
          fullDataName,
        )
        const element =
          (registeredFaForPutFullData as { formId?: unknown } | undefined) ??
          getCurrentJsContextElementByName(fullDataName)
        const registeredForPutFullData =
          registeredFaForPutFullData ?? element ?? elements.value[fullDataName]
        const elTypeNormPutFullData =
          registeredForPutFullData?.elementType ??
          normalizeFormElementType(registeredForPutFullData)
        const putFullDataTilesRowScope = en.value?._getTilesRowScope?.() ?? null

        let collectedPutFullData: Record<string, any> = {}
        if (element && element.formId && element.panelId && element.elementId) {
          collectedPutFullData = getForm({
            formId: element.formId,
            panelId: element.panelId,
            elementId: element.elementId,
            flatten: true,
            pageDataSlot,
            tilesRowScope: putFullDataTilesRowScope,
          })
        }
        else {
          collectedPutFullData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        if (
          typeof collectedPutFullData !== 'object' ||
          collectedPutFullData === null ||
          Array.isArray(collectedPutFullData) ||
          collectedPutFullData instanceof String
        ) {
          collectedPutFullData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        const pageMergedForPutFullData = toRaw(mergePageDataFieldSnapshot(pageData))
        const scopedFromPagePutFullData = pickPutFullDataScopedPageFields(
          pageMergedForPutFullData as Record<string, unknown>,
          fullDataName,
        )
        collectedPutFullData = mergeFieldMaps(scopedFromPagePutFullData, collectedPutFullData)

        collectedPutFullData = withPutDataFallback(collectedPutFullData, fullDataName)
        collectedPutFullData = normalizeCollectedMultiSelectField(
          collectedPutFullData,
          fullDataName,
          element,
        )

        let fileAttachmentsFromPutFullDataHook = false
        let tilesFromPutFullDataHook = false

        const putFullDataHookFn =
          typeof (registeredForPutFullData as any)?.getFullData === 'function'
            ? (registeredForPutFullData as any).getFullData
            : typeof (registeredForPutFullData as any)?.getData === 'function'
              ? (registeredForPutFullData as any).getData
              : null

        const isFileAttachmentsPutFullData =
          String(elTypeNormPutFullData ?? '').toUpperCase() === 'FILEATTACHMENTS' ||
          isFileAttachmentFieldInForm(pageData?.form, fullDataName)
        const faNamesForPutFullData = isFileAttachmentsPutFullData
          ? collectFileAttachmentFieldNamesFromForm(pageData?.form)
          : []
        const storageFormIdForPutFullData = isFileAttachmentsPutFullData
          ? resolveFormsStorageFormIdForContext(formId, pageDataSlot)
          : ''
        const formPanelsForFa = isFileAttachmentsPutFullData
          ? (forms.value[storageFormIdForPutFullData] ??
            forms.value[String(formId)] ??
            forms.value[formId])
          : undefined
        const faFromAllPanels = isFileAttachmentsPutFullData
          ? collectFileAttachmentFieldsFromFormsPanels(
              formPanelsForFa as Record<string, Record<string, Record<string, unknown>> | unknown>,
              faNamesForPutFullData.length > 0 ? faNamesForPutFullData : [fullDataName],
              mergeFieldMaps,
            )
          : {}

        const applyPreferredFileAttachmentsPutFullData = () => {
          const preferred = pickPreferredFileAttachmentsField(
            collectedPutFullData[fullDataName],
            faFromAllPanels[fullDataName],
            (pageMergedForPutFullData as Record<string, unknown>)?.[fullDataName],
          )
          if (
            preferred !== undefined &&
            (isUniDataTableEntry(preferred) || uniDataTableToFileItems(preferred).length > 0)
          ) {
            collectedPutFullData[fullDataName] = fileItemsToUniDataTable(
              uniDataTableToFileItems(preferred),
            )
          } else {
            collectedPutFullData[fullDataName] = emptyFileAttachmentsUniDataTable()
          }
        }

        const faMeta = parseFileAttachmentsElementMeta(
          (registeredForPutFullData as { element?: unknown } | undefined)?.element,
        )
        const faElementId = resolvePositiveElementId(
          (registeredForPutFullData as { elementId?: unknown } | undefined)?.elementId,
          (registeredForPutFullData as { elementId?: unknown } | undefined)?.elementId,
        )
        const faTableStillPending = () =>
          fileAttachmentsTableHasPendingUploadWithoutServerCode(
            collectedPutFullData[fullDataName],
            faMeta,
            faElementId,
          )

        const normalizePutFullDataCollected = () => {
          if (tilesFromPutFullDataHook) return
          const comboboxExtraKeys =
            elTypeNormPutFullData === 'COMBOBOX'
              ? parseComboBoxIdFieldPairsFromElement(registeredForPutFullData).map((p) => p.outKey)
              : []
          normalizeElementCollectedDataForExport(collectedPutFullData, {
            elementName: fullDataName,
            elTypeNorm: elTypeNormPutFullData,
            registered: registeredForPutFullData,
            fromElementSlot: collectedPutFullData,
            pageMerged: pageMergedForPutFullData as Record<string, unknown>,
            fileAttachmentsFromHook: fileAttachmentsFromPutFullDataHook,
          })
          collectedPutFullData = prunePutFullDataCollectedFields(
            collectedPutFullData,
            fullDataName,
            elTypeNormPutFullData,
            {
              includeTilesRowNum: Boolean(putFullDataTilesRowScope),
              extraKeys: comboboxExtraKeys,
            },
          )
        }

        const invokePutFullDataRegisteredHook = async () => {
          if (
            !(
              (elTypeNormPutFullData === 'TILES' || elTypeNormPutFullData === 'FILEATTACHMENTS') &&
              registeredForPutFullData &&
              putFullDataHookFn
            )
          ) {
            return
          }
          try {
            const hookSessionId = resolveJsRequestSessionId(request, registeredForPutFullData as any)
            if (hookSessionId) {
              ;(registeredForPutFullData as { platformSessionId?: string }).platformSessionId =
                hookSessionId
            }
            const hookData = await runWithJsRequestSessionId(hookSessionId, async () => {
              // FILEATTACHMENTS: close+wait Р7 как у En.putFullData, затем getFullData upload.
              if (elTypeNormPutFullData === 'FILEATTACHMENTS') {
                const prepare = (registeredForPutFullData as { prepareForFormSave?: () => unknown })
                  ?.prepareForFormSave
                if (typeof prepare === 'function') {
                  await prepare.call(registeredForPutFullData)
                }
              }
              return withAttachmentUploadSessionOverride(hookSessionId, () =>
                putFullDataHookFn.call(registeredForPutFullData),
              )
            })

            if (
              elTypeNormPutFullData === 'FILEATTACHMENTS' &&
              hookData &&
              typeof (hookData as any)._getAllFields === 'function'
            ) {
              const all = (hookData as any)._getAllFields?.() as Record<string, unknown>
              if (all && typeof all === 'object') {
                for (const [key, field] of Object.entries(all)) {
                  if (field !== undefined) {
                    collectedPutFullData[key] = field
                  }
                }
                fileAttachmentsFromPutFullDataHook = true
                const hookOnly = pruneAttachmentElementRpcFields(collectedPutFullData, fullDataName)
                for (const key of Object.keys(collectedPutFullData)) {
                  delete collectedPutFullData[key]
                }
                Object.assign(collectedPutFullData, hookOnly)
              }
            } else {
              let hookField: any = undefined

              if (hookData && typeof hookData === 'object') {
                if (typeof (hookData as any).getField === 'function') {
                  const tf = (hookData as any).getField(fullDataName)
                  if (tf && typeof tf === 'object' && 'value' in tf) {
                    hookField = tf
                  }
                }

                if (
                  hookField === undefined &&
                  typeof (hookData as any)._getAllFields === 'function'
                ) {
                  const all = (hookData as any)._getAllFields?.()
                  if (all && typeof all === 'object' && fullDataName in all) {
                    hookField = (all as Record<string, any>)[fullDataName]
                  }
                }

                if (hookField === undefined && fullDataName in (hookData as Record<string, any>)) {
                  hookField = (hookData as Record<string, any>)[fullDataName]
                }
              }

              if (hookField !== undefined) {
                if (elTypeNormPutFullData === 'TILES') {
                  collectedPutFullData = pruneTilesPutFullDataFields(
                    { [fullDataName]: hookField },
                    fullDataName,
                    { includeRowNum: Boolean(putFullDataTilesRowScope) },
                  )
                  tilesFromPutFullDataHook = true
                } else {
                  collectedPutFullData[fullDataName] = hookField
                  fileAttachmentsFromPutFullDataHook =
                    elTypeNormPutFullData === 'FILEATTACHMENTS'
                }
              }
            }
          } catch (error) {
            if (elTypeNormPutFullData === 'FILEATTACHMENTS') {
              if (error instanceof AttachmentUploadError) {
                throwRuntimeJsRequestError(request, error.message, error)
              }
              throw error
            }
            console.warn('[JSContext] putFullData: failed to read element hook data', {
              fullDataName,
              elTypeNorm: elTypeNormPutFullData,
              error,
            })
            throwRuntimeJsRequestError(
              request,
              '[JSContext] putFullData: failed to read element hook data',
              error,
            )
          }
        }

        try {
          if (isFileAttachmentsPutFullData) {
            stashAttachmentPutFullDataWireTable(
              fullDataName,
              faFromAllPanels[fullDataName],
              (pageMergedForPutFullData as Record<string, unknown>)?.[fullDataName],
            )
          }

          await invokePutFullDataRegisteredHook()
          normalizePutFullDataCollected()

          if (isFileAttachmentsPutFullData) {
            applyPreferredFileAttachmentsPutFullData()
            let overlayRetryDone = false
            if (faTableStillPending()) {
              overlayRetryDone = true
              await invokePutFullDataRegisteredHook()
              normalizePutFullDataCollected()
              applyPreferredFileAttachmentsPutFullData()
            }
            if (
              fileAttachmentsPutFullDataShouldThrowPending({
                overlayRetryDone,
                tableStillPending: faTableStillPending(),
              })
            ) {
              const msg = t('attachments.uploadPendingMissingFile')
              const { useNotification } = await import('@/common/composables/useNotification')
              useNotification().error({
                summary: t('attachments.uploadError'),
                detail: msg,
              })
              throwRuntimeJsRequestError(request, msg)
            }
          }
        } finally {
          if (isFileAttachmentsPutFullData) {
            clearAttachmentPutFullDataWireTable(fullDataName)
          }
        }

        const cleanFullFields: any = {}
        for (const key of Object.keys(collectedPutFullData)) {
          cleanFullFields[key] = toUniDataField(collectedPutFullData[key])
        }

        return createJsContextResponse(cleanFullFields)

      case 'putPageData':
        // Get current pageId from pageData
        const currentPageId = pageData?.jsResult?.page_id || pageData?.currentPageId
        let collectedPageData: Record<string, any> = {}

        if (currentPageId && formId) {
          collectedPageData = getPageData({ formId, pageId: currentPageId, flatten: true })
        } else {
          collectedPageData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        if (typeof collectedPageData !== 'object' || collectedPageData === null || Array.isArray(collectedPageData) || collectedPageData instanceof String) {
          collectedPageData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        const cleanPageFields: any = {}
        for (const key of Object.keys(collectedPageData)) {
          cleanPageFields[key] = toUniDataField(collectedPageData[key])
        }

        return createJsContextResponse(cleanPageFields)

      case 'putPanelData':
        const currentPanelId = pageData?.jsResult?.panel_id || getDefaultPanelId()
        let collectedPanelData: Record<string, any> = {}

        if (currentPanelId && formId) {
          collectedPanelData = getForm({ formId, panelId: currentPanelId, flatten: false })
        } else {
          collectedPanelData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        if (typeof collectedPanelData !== 'object' || collectedPanelData === null || Array.isArray(collectedPanelData) || collectedPanelData instanceof String) {
          collectedPanelData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        const cleanPanelFields: any = {}
        for (const key of Object.keys(collectedPanelData)) {
          cleanPanelFields[key] = toUniDataField(collectedPanelData[key])
        }

        return createJsContextResponse(cleanPanelFields)

      case 'getSelectedPageId':
        return createJsContextResponse({}, {
          responseValue: resolveSelectedPageId(args[0]),
        })

      case 'reloadElement':
        const nameToReload = args[0]
        if (nameToReload) {
          await reloadElementViaControls(nameToReload, {}, {
            formId,
            panelId: getDefaultPanelId(),
            platform: currentPlatform.value || undefined,
          })
        }
        return {}

      case 'setVisible': {
        const coerceVisible = (value: unknown): boolean | undefined => {
          if (value === undefined || value === null) return undefined
          if (typeof value === 'boolean') return value
          const t = String(value).trim().toLowerCase()
          if (t === 'true' || t === '1') return true
          if (t === 'false' || t === '0') return false
          return Boolean(value)
        }
        if (args.length >= 3) {
          const visible = coerceVisible(args[2])
          const level = String(args[0] ?? '')
          const id = args[1]
          if (visible !== undefined && level && id != null && id !== '') {
            setVisibleByLevel(level, id, visible)
          }
        } else if (args.length >= 2 && typeof args[0] === 'string') {
          const visible = coerceVisible(args[1])
          if (visible !== undefined) {
            setVisibleRecursive(args[0], visible)
          }
        }
        return createJsContextResponse()
      }

      case 'setValue':
        const fieldName = args[0]
        const value = args[1]
        if (fieldName) {
          // Prefer real element coordinates from registry.
          // Middle scripts often call JSContext.setValue(fieldName, value) (via en.refillElement / en.getData().setField).
          // If we store by `elementId = fieldName`, later `putFullData(name)` (which resolves numeric elementId)
          // won't see the value in `getForm({ formId, panelId, elementId })`.
          // TODO: проверить этот кейс!
          // const elementName = String(fieldName)
          // const reg = getCurrentJsContextElementByName(elementName)
          // const targetFormId = reg?.formId ?? getCurrentRegistryFormId()
          // const targetPanelId = reg?.panelId ?? getDefaultPanelId()
          // const targetElementId = reg?.elementId ?? elementName

          // setValue({
          //   value: { [elementName]: value },
          //   formId: targetFormId,
          //   panelId: targetPanelId,
          //   elementId: targetElementId,
          // })

          setValue({
            value: { [fieldName]: value },
            formId: getCurrentRegistryFormId(),
            panelId: getDefaultPanelId(),
            elementId: fieldName
          })
        }
        return {}

      case 'putElementState':
        const stateElementName = args[0]
        if (!stateElementName || (typeof stateElementName === 'string' && stateElementName.trim() === '')) {
          console.warn('[JSContext] putElementState() called with empty/null name')
          return createJsContextResponse()
        }

        const stateElement = getCurrentJsContextElementByName(stateElementName)
        let collectedStateData: Record<string, any> = {}

        if (stateElement && stateElement.formId && stateElement.panelId && stateElement.elementId) {
          collectedStateData = getForm({
            formId: stateElement.formId,
            panelId: stateElement.panelId,
            elementId: stateElement.elementId,
            flatten: true
          })
        } else {
          collectedStateData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        if (typeof collectedStateData !== 'object' || collectedStateData === null || Array.isArray(collectedStateData) || collectedStateData instanceof String) {
          collectedStateData = toRaw(pageData?.uniData?.fields?.fields || {})
        }

        const cleanStateFields: any = {}
        for (const key of Object.keys(collectedStateData)) {
          cleanStateFields[key] = toUniDataField(collectedStateData[key])
        }

        return createJsContextResponse(cleanStateFields)

      default:
        throwUnknownJsRequestHandler(request, 'JSContext method')
    }
  }

  if (normalizedClassName === 'ProgressBar' || normalizedClassName?.endsWith('.ProgressBar')) {
    const { messagesService } = await import('@/services/messages/messages.service')
    const { progressBarService } = await import('@/services/progress/progress.service')
    const { IKey } = await import('@/common/constants/ikey')
    const resolveProgressId = (rawId: unknown): string | null => {
      const requestedId = typeof rawId === 'string' ? rawId : ''
      if (requestedId && progressBarService.get(requestedId)) {
        return requestedId
      }

      const fallbackInstance = progressBarService.getLastInstance()
      if (requestedId && fallbackInstance && fallbackInstance.id !== requestedId) {
        console.warn('[ProgressBar] Unknown id, using last instance instead', {
          requestedId,
          fallbackId: fallbackInstance.id,
          methodName,
        })
      }
      return fallbackInstance?.id || null
    }

    switch (methodName) {
      case 'init': {
        const description = args[0] || ''
        const max = args[1] !== undefined ? args[1] : -1
        const cancelable = args[2] !== undefined ? args[2] : false

        const instance = progressBarService.init(description, max, cancelable)
        progressBarService.show(instance.id)

        const idValue = { type: 'String', value: instance.id }
        return withSocketId({
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: idValue
            }
          },
          [IKey.JS_WEB_RESPONSE]: idValue
        })
      }

      case 'show': {
        const id = resolveProgressId(args[0])
        if (id) {
          progressBarService.show(id)
        }
        return withSocketId()
      }

      case 'inc': {
        const id = resolveProgressId(args[0])
        if (id) {
          const instance = progressBarService.get(id)
          if (instance) {
            progressBarService.inc(id)
          }
        }
        return withSocketId()
      }

      case 'close': {
        const id = resolveProgressId(args[0])
        if (id) {
          progressBarService.close(id)
        }
        return withSocketId()
      }

      case 'onCancel': {
        const id = resolveProgressId(args[0])
        const source = typeof args[1] === 'string' ? args[1] : ''
        if (id && source) {
          progressBarService.setOnCancel(id, () => {
            const runHandler = async () => {
              try {
                const script = `(${source})`
                if (en.value) {
                  await en.value.run(`${script}()`)
                } else {
                  const fn = eval(script)
                  if (typeof fn === 'function') {
                    fn()
                  }
                }
              } catch (error) {
                console.error('[ProgressBar] onCancel handler failed', error)
              }
            }
            void runHandler()
          })
        }
        return withSocketId()
      }

      case 'setHeadlineProgress': {
        const id = resolveProgressId(args[0])
        const headlineProgress = args[1] !== undefined ? Boolean(args[1]) : true
        if (id) {
          progressBarService.setHeadlineProgress(id, headlineProgress)
        }
        return withSocketId()
      }

      case 'isCanceled': {
        const id = resolveProgressId(args[0])
        if (id) {
          const canceled = progressBarService.isCanceled(id)
          return withSocketId({
            fields: {
              fields: {
                [IKey.JS_WEB_RESPONSE]: {
                  type: 'Boolean',
                  value: canceled
                }
              }
            }
          })
        }
        return withSocketId({
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'Boolean',
                value: false
              }
            }
          }
        })
      }

      default:
        throwUnknownJsRequestHandler(request, 'ProgressBar method')
    }
  }

  if (normalizedClassName === 'client-import') {
    const clientImportHandlerItem = resolveClientImportHandler(methodName)
    if (!clientImportHandlerItem) {
      throwUnknownJsRequestHandler(request, 'client-import method')
    }

    const {IKey} = await import('@/common/constants/ikey')
    const { enContext, restore } = createClientImportEnContext(request)
    const boundArgs = bindClientImportElementArgs(
      args,
      elements.value as Record<string, any>,
      getCurrentRegistryFormId(),
      [elementsByKey.value as Record<string, any>],
    )

    try {
      const fieldsBeforeExecution = JSON.parse(JSON.stringify(enContext.getData()._getAllFields()))
      const fn = clientImportHandlerItem.func
      const shouldAwait =
        Boolean((clientImportHandlerItem as any).async) || fn?.constructor?.name === 'AsyncFunction'
      const result = shouldAwait ? await fn(...boundArgs) : fn(...boundArgs)
      const fieldsAfterExecution = enContext.getData()._getAllFields()
      const changedFields = collectChangedUniDataFields(fieldsBeforeExecution, fieldsAfterExecution)

      const payload =
        result instanceof UniData || isUniDataResponseLike(result)
          ? mergeUniDataResponseFields(result, changedFields, {
              preserveExisting: true,
            })
          : createBaseUniDataResponse({
              ...changedFields,
              [IKey.JS_WEB_RESPONSE]: toUniDataField(result),
            })
      if (payload?.fields?.fields && typeof payload.fields.fields === 'object') {
        payload.fields.fields = ensureUniDataWireFields(
          payload.fields.fields as Record<string, unknown>,
        )
      }
      return withSocketId(payload)
    } finally {
      restore()
    }
  }

  if (normalizedClassName === 'Messages' || normalizedClassName?.endsWith('.Messages') || normalizedClassName?.endsWith('Messages')) {
    const { messagesService } = await import('@/services/messages/messages.service')
    const { IKey } = await import('@/common/constants/ikey')
    const UniDataTable = (await import('@/common/classes/UniDataTable')).default

    switch (methodName) {
      case 'showMessage':
        await messagesService.showMessage(args[0] || '', args[1] || '')
        return { fields: { fields: {} } }

      case 'showMessageError':
        await messagesService.showMessageError(args[0] || '', args[1] || '')
        return { fields: { fields: {} } }

      case 'showToast':
        messagesService.showToast(args[0] || '')
        return { fields: { fields: {} } }

      case 'showConfirmDialog': {
        const result = await messagesService.showConfirmDialog(args[0] || '', args[1] || '')
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'Integer',
                value: result
              }
            }
          }
        }
      }

      case 'checkConfirmation': {
        const result = await messagesService.checkConfirmation(args[0] || '', args[1] || '')
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'Boolean',
                value: result
              }
            }
          }
        }
      }

      case 'showCheckBoxChooser': {
        const variants = args[2] ? new UniDataTable(args[2]) : null
        const result = await messagesService.showCheckBoxChooser(args[0] || '', args[1] || '', variants!)

        if(result === null){
          return {
            fields: {
              fields: {}
            }
          }
        }

        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: toUniDataField(result)
            }
          }
        }
      }

      case 'showFilesToSignChooser': {
        const title = args[0] || ''
        // files may come as genericArray< UniDataTableFields > and is deserialized to plain array by TypeSerializer
        const files = Array.isArray(args[1]) ? args[1] : []
        const selectedIndices = Array.isArray(args[2]) ? args[2] : []
        const maxSizeRaw = Number(args[3])
        const maxSize = Number.isFinite(maxSizeRaw) ? maxSizeRaw : 0

        const result = await messagesService.showFilesToSignChooser(
          title,
          files,
          selectedIndices,
          maxSize,
        )

        const serializedResult = result
          ? result.rows.map((row: any) => ({
              type: 'UniDataTableFields',
              value: {
                fields: row.fields || {},
                errors: row.errors || {},
                attributes: row.attributes || {},
              },
            }))
          : []

        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'genericArray',
                value: serializedResult
              }
            }
          }
        }
      }

      case 'showFilesChooser': {
        const title = args[0] || ''
        const files = Array.isArray(args[1]) ? args[1] : []
        const selectedIndices = Array.isArray(args[2]) ? args[2] : []
        const maxSizeRaw = Number(args[3])
        const maxSize = Number.isFinite(maxSizeRaw) ? maxSizeRaw : 0
        const overLimitMessage = String(args[4] ?? '')
        const canBeEmpty = Boolean(args[5])

        const result = await messagesService.showFilesChooser(
          title,
          files,
          selectedIndices,
          maxSize,
          overLimitMessage,
          canBeEmpty,
        )

        const serializedResult = result
          ? result.rows.map((row: any) => ({
              type: 'UniDataTableFields',
              value: {
                fields: row.fields || {},
                errors: row.errors || {},
                attributes: row.attributes || {},
              },
            }))
          : []

        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'genericArray',
                value: serializedResult
              }
            }
          }
        }
      }

      case 'showChooser': {
        let result: string | null = null
        if (args.length === 3 && Array.isArray(args[2])) {
          // showChooser(message, title, variants[][] | genericArray<MapEntry>)
          result = await messagesService.showChooser(args[0] || '', args[1] || '', args[2])
        } else if (args.length === 5) {
          // showChooser(message, title, variants, keyColumn, descriptionColumn)
          const variants = args[2] ? new UniDataTable(args[2]) : null
          result = await messagesService.showChooser(args[0] || '', args[1] || '', variants!, args[3], args[4])
        }
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      case 'showInput': {
        const result = await messagesService.showInput(args[0] || '', args[1])
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      case 'showInputWithTitle': {
        const result = await messagesService.showInputWithTitle(args[0] || '', args[1] || '', args[2])
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      case 'showInputText': {
        let result: string | null = null
        if (args.length === 2) {
          // showInputText(title, message)
          result = await messagesService.showInputText(args[0] || '', args[1] || '')
        } else if (args.length === 3) {
          // showInputText(message, title, def)
          result = await messagesService.showInputText(args[0] || '', args[1] || '', args[2])
        }
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      case 'showButtonChooser': {
        let result: string | null = null
        if (args.length === 3 && Array.isArray(args[2])) {
          // showButtonChooser(message, title, variants[][])
          result = await messagesService.showButtonChooser(args[0] || '', args[1] || '', args[2])
        } else if (args.length === 5) {
          // showButtonChooser(message, title, variants, keyColumn, descriptionColumn)
          const variants = args[2] ? new UniDataTable(args[2]) : null
          result = await messagesService.showButtonChooser(args[0] || '', args[1] || '', variants!, args[3], args[4])
        }
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      case 'showButtonChooserByColumns': {
        const result = await messagesService.showButtonChooserByColumns(args[0] || '', args[1] || '', args[2] || [], args[3] || 1)
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      case 'showChooserWithTable': {
        const variants = args[2] ? new UniDataTable(args[2]) : null
        const result = await messagesService.showChooserWithTable(args[0] || '', args[1] || '', variants!, args[3] || [])
        if (result === null) {
          return { fields: { fields: {} } }
        }
        return {
          fields: {
            fields: {
              [IKey.JS_WEB_RESPONSE]: {
                type: 'String',
                value: result
              }
            }
          }
        }
      }

      default:
        throwUnknownJsRequestHandler(request, 'Messages method')
    }
  }
  if (isJsRequestElementOrActionClass(normalizedClassName)) {

    const rawObjectId = request.objectId ?? request.object_id
    const objectId =
      rawObjectId != null && rawObjectId !== ''
        ? Number(rawObjectId)
        : null

    if (objectId !== null && Number.isFinite(objectId)) {
      const formId = getCurrentRegistryFormId()
      const pageData = appStore.pagesData[getCurrentPageSlotId()]
      const elementPageFields = pageData ? mergePageDataFieldSnapshot(pageData) : {}

      const resolveElementNameByObjectId = (): string | undefined => {
        for (const [name, elem] of Object.entries(elements.value)) {
          if (Number(elem?.elementId) === objectId) {
            return name
          }
        }
        return undefined
      }

      const getTableName = () => resolveElementNameByObjectId()

      switch (methodName) {
        case 'doAction': {
          const actionCtx = resolveRegisteredActionForWsDoAction(objectId)

          if (!actionCtx) {
            console.warn(
              `[${normalizedClassName}] doAction: no action context found for objectId`,
              objectId,
            )
            throwInvalidJsRequestPayload(
              request,
              `[${normalizedClassName}] doAction: no action context found for objectId`,
              { objectId },
            )
          }

          const actionData = actionCtx?.data
          if (!actionData?._attributes?.id) {
            console.warn(
              `[${normalizedClassName}] doAction: action context has no action data/id`,
              objectId,
              actionCtx,
            )
            throwInvalidJsRequestPayload(
              request,
              `[${normalizedClassName}] doAction: action context has no action data/id`,
              { objectId, actionCtx },
            )
          }

          const layout = actionData?.layout || {}
          const layoutLevel = layout.level?._text || actionCtx.level || 'form'
          const layoutId = layout.id?._text || layout.id
          const pageId = layoutLevel === 'page' && layoutId ? layoutId : actionCtx.pageId
          const formId = String(actionCtx.formId ?? getCurrentFormId() ?? '')
          const selectedData = actionCtx.selectedData ?? actionData.selected?.fields ?? null

          const actionPlatform =
            actionData.platform?._text ||
            currentPlatform.value ||
            findPageDataByFormId(formId)?.type ||
            undefined

          const actionContext = {
            id: String(actionCtx.id ?? actionData._attributes.id),
            name: actionCtx.name || actionData.name?._text || '',
            elementId:
              layoutLevel === 'element' && layoutId
                ? String(layoutId)
                : actionCtx.elementId != null
                  ? String(actionCtx.elementId)
                  : undefined,
            panelId: actionCtx.panelId != null ? String(actionCtx.panelId) : undefined,
            formId: formId || undefined,
            pageId: pageId != null ? String(pageId) : undefined,
            level: (layoutLevel || actionCtx.level || 'form') as
              | 'form'
              | 'panel'
              | 'element'
              | 'page',
            data: actionData,
            platform: actionPlatform,
            windowType: actionData.window__type?._text,
            onClick: actionData.onClick?._text,
            confirm: actionData.confirm?._text,
            transfer: actionData.transfer?._text,
            transferType: actionData.transferType?._text,
            event: {
              dialogClosed: getActionDialogClosedScript(actionData as Record<string, any>),
              chainCalled: actionData.event?.chainCalled?._text,
            },
            flow: actionData.flow?._text,
            selectedData,
            ...(hasIncomingChangedData(request)
              ? {
                  middleScriptUniData: createSafeClientData({
                    fields: normalizeUniDataFields(
                      request.changedData ?? request.changed_data,
                    ),
                  }),
                }
              : {}),
          }

          const { actionService } = await import('@/services/action/action.service')
          const depsPageData =
            findPageDataByFormId(formId) ||
            appStore.pagesData?.[formId] ||
            appStore.pagesData?.[String(appStore.currentPageId ?? '')]

          await actionService.executeAction(actionContext, {
            appStore,
            router,
            actions: tempActions,
            getForm,
            getPageData,
            getTableSelection,
            getTableSelections,
            formStates: formStatesResult,
            pageData: depsPageData,
            runCode,
            formProvider,
            reloadElement: reloadElementViaControls,
          })

          return withSocketId()
        }
        case 'invokeEvent': {
          const elementName = resolveElementNameByObjectId()

          if (!elementName) {
            console.warn('[ELEMENT] invokeEvent: no element registered for objectId', objectId)
            throwInvalidJsRequestPayload(
              request,
              '[ELEMENT] invokeEvent: no element registered for objectId',
              { objectId },
            )
          }

          const invokeEventName = args[0]
          const invokeInputData = args[1]


          const registeredElement = elements.value[elementName]

          if (registeredElement && typeof registeredElement.invokeEvent === 'function') {
            await registeredElement.invokeEvent(invokeEventName, invokeInputData)
          } else {
            console.warn('[ELEMENT] invokeEvent: invokeEvent is not available on registered element', {
              elementName,
              objectId,
            })
            throwInvalidJsRequestPayload(
              request,
              '[ELEMENT] invokeEvent: invokeEvent is not available on registered element',
              {
                elementName,
                objectId,
              },
            )
          }

          const fieldsAfterExecution = JSON.parse(JSON.stringify(en.value?.getData()._getAllFields() ?? {}))
          const normalizedFieldsAfterExecution: Record<string, any> = {}

          for (const [fieldName, fieldValue] of Object.entries(fieldsAfterExecution)) {
            normalizedFieldsAfterExecution[fieldName] = toUniDataField(fieldValue)
          }

          return withSocketId(createBaseUniDataResponse(normalizedFieldsAfterExecution))
        }

        case 'reloadNode': {
          const elementName = resolveElementNameByObjectId()
          if (!elementName) {
            console.warn('[ELEMENT] reloadNode: no element registered for objectId', objectId)
            throwInvalidJsRequestPayload(
              request,
              '[ELEMENT] reloadNode: no element registered for objectId',
              { objectId },
            )
          }

          const registeredElement = elements.value[elementName]
          const elTypeNorm =
            registeredElement?.elementType ?? normalizeFormElementType(registeredElement)
          if (String(elTypeNorm).toUpperCase() !== 'TREETABLE') {
            console.warn('[ELEMENT] reloadNode: element is not TREETABLE', {
              elementName,
              elementType: elTypeNorm,
            })
            throwInvalidJsRequestPayload(
              request,
              '[ELEMENT] reloadNode: element is not TREETABLE',
              {
                elementName,
                elementType: elTypeNorm,
              },
            )
          }

          const buildPayloadFn =
            registeredElement && typeof registeredElement.buildReloadNodePayload === 'function'
              ? registeredElement.buildReloadNodePayload
              : null

          if (!buildPayloadFn) {
            console.warn('[ELEMENT] reloadNode: buildReloadNodePayload is not available', elementName)
            throwInvalidJsRequestPayload(
              request,
              '[ELEMENT] reloadNode: buildReloadNodePayload is not available',
              { elementName },
            )
          }

          const rowNumRaw = Number(args[0] ?? -1)
          const rowNum = Number.isFinite(rowNumRaw) ? rowNumRaw : -1
          const needReloadParent = Boolean(args[1])

          const fields = buildPayloadFn.call(
            registeredElement,
            rowNum,
            needReloadParent,
          ) as Record<string, any>

          const normalizedReloadFields: Record<string, any> = {}
          for (const [fieldName, fieldValue] of Object.entries(fields || {})) {
            normalizedReloadFields[fieldName] = toUniDataField(fieldValue)
          }

          return withSocketId(createBaseUniDataResponse(normalizedReloadFields))
        }

        case 'getFullData':
        case 'getData': {
          const elementName = resolveElementNameByObjectId()
          if (!elementName) {
            console.warn('[ELEMENT] getData: no element registered for objectId', objectId)
            throwInvalidJsRequestPayload(
              request,
              '[ELEMENT] getData: no element registered for objectId',
              { objectId },
            )
          }

          const registered = elements.value[elementName]
          const elTypeNorm =
            registered?.elementType ?? normalizeFormElementType(registered)

          const pageMergedEarly = toRaw(mergePageDataFieldSnapshot(pageData))

          // TREETABLE: всегда пересборка Swing getDataChildren (не сырой pageFields)
          if (isTreeTableElementType(elTypeNorm) && registered) {
            try {
              const ctx =
                typeof (registered as any).buildTreeTableGetDataExportContext === 'function'
                  ? (registered as any).buildTreeTableGetDataExportContext()
                  : null
              const backing =
                typeof (registered as any).getTreeTableBackingRowsForExport === 'function'
                  ? (registered as any).getTreeTableBackingRowsForExport()
                  : extractUniDataTableRows(pageMergedEarly?.[elementName])
              const columns =
                ctx?.columns?.length > 0
                  ? ctx.columns
                  : parseTreeTableRuntimeColumnsFromPage(pageMergedEarly, elementName)
              const fieldName = ctx?.fieldName || elementName

              const hookField = rebuildTreeTableGetDataForWs(fieldName, backing, columns, {
                addAll: true,
                defaultExpandByDefault:
                  ctx?.defaultExpandByDefault === true ||
                  (registered as { treeTableDefaultExpandByDefault?: boolean })
                    ?.treeTableDefaultExpandByDefault === true,
              })
              if (hookField !== undefined) {
                return createBaseUniDataResponse({
                  [elementName]: toUniDataField(hookField),
                })
              }
            } catch (error) {
              console.warn('[ELEMENT] getData: failed to read TreeTable data from element hook', {
                elementName,
                error,
              })
              throwRuntimeJsRequestError(
                request,
                '[ELEMENT] getData: failed to read TreeTable data from element hook',
                error,
              )
            }
          }

          let fromElementSlot: Record<string, any> = {}
          if (registered?.formId && registered?.panelId && registered?.elementId != null) {
            const slot = getForm({
              formId: registered.formId,
              panelId: registered.panelId,
              elementId: registered.elementId,
              flatten: true,
            })
            if (slot && typeof slot === 'object' && !Array.isArray(slot) && !(slot instanceof String)) {
              fromElementSlot = slot
            }
          }

          const pageMerged = pageMergedEarly

          const scopedFromPage: Record<string, any> = {}
          const suffixPrefix = `${elementName}__`
          for (const key of Object.keys(pageMerged)) {
            if (key === elementName || key.startsWith(suffixPrefix)) {
              scopedFromPage[key] = pageMerged[key]
            }
          }

          const collectedData = mergeFieldMaps(scopedFromPage, fromElementSlot)

          let fileAttachmentsFromHook = false

          // TILES / FILEATTACHMENTS: приоритет данных из хука элемента (getFullData → getFullData, иначе getData)
          const elementDataHookFn =
            methodName === 'getFullData' && typeof (registered as any)?.getFullData === 'function'
              ? (registered as any).getFullData
              : typeof (registered as any)?.getData === 'function'
                ? (registered as any).getData
                : null

          if (
            (elTypeNorm === 'TILES' || elTypeNorm === 'FILEATTACHMENTS') &&
            registered &&
            elementDataHookFn
          ) {
            try {
              const hookSessionId = resolveJsRequestSessionId(request, registered as any)
              if (hookSessionId) {
                ;(registered as { platformSessionId?: string }).platformSessionId = hookSessionId
              }
              const hookArg =
                elTypeNorm === 'FILEATTACHMENTS' && methodName === 'getData' ? args[0] : undefined
              const hookData = await runWithJsRequestSessionId(hookSessionId, async () =>
                withAttachmentUploadSessionOverride(hookSessionId, () =>
                  elementDataHookFn.call(registered, hookArg),
                ),
              )

              if (elTypeNorm === 'FILEATTACHMENTS' && hookData && typeof (hookData as any)._getAllFields === 'function') {
                const all = (hookData as any)._getAllFields?.() as Record<string, unknown>
                if (all && typeof all === 'object') {
                  const hookFormId = registered?.formId != null ? String(registered.formId) : ''
                  const hookPageData = hookFormId ? findPageDataByFormId(hookFormId) : undefined
                  const isInlineGetData =
                    methodName === 'getData' &&
                    shouldUseInlineAttachmentGetData(args[0], {
                      pageActions: collectFormActionsFromPageData(hookPageData),
                      registeredActions: actions.value,
                    }) &&
                    !(all as Record<string, unknown>)[elementName]
                  for (const [key, field] of Object.entries(all)) {
                    if (field !== undefined) {
                      collectedData[key] = field
                    }
                  }
                  fileAttachmentsFromHook = true
                  if (!isInlineGetData) {
                    const hookOnly = pruneAttachmentElementRpcFields(collectedData, elementName)
                    for (const key of Object.keys(collectedData)) {
                      delete collectedData[key]
                    }
                    Object.assign(collectedData, hookOnly)
                  }
                }
              } else {
                let hookField: any = undefined

                if (hookData && typeof hookData === 'object') {
                  if (typeof (hookData as any).getField === 'function') {
                    const tf = (hookData as any).getField(elementName)
                    if (tf && typeof tf === 'object' && 'value' in tf) {
                      hookField = tf
                    }
                  }

                  if (hookField === undefined && typeof (hookData as any)._getAllFields === 'function') {
                    const all = (hookData as any)._getAllFields?.()
                    if (all && typeof all === 'object' && elementName in all) {
                      hookField = (all as Record<string, any>)[elementName]
                    }
                  }

                  if (hookField === undefined && elementName in (hookData as Record<string, any>)) {
                    hookField = (hookData as Record<string, any>)[elementName]
                  }
                }

                if (hookField !== undefined) {
                  collectedData[elementName] = hookField
                }
              }
            } catch (error) {
              if (elTypeNorm === 'FILEATTACHMENTS') {
                throw error
              }
              console.warn('[ELEMENT] getData: failed to read element hook data', {
                elementName,
                elTypeNorm,
                error,
              })
              throwRuntimeJsRequestError(
                request,
                '[ELEMENT] getData: failed to read element hook data',
                error,
              )
            }
          }

          normalizeElementCollectedDataForExport(collectedData, {
            elementName,
            elTypeNorm,
            registered,
            fromElementSlot,
            pageMerged: pageMerged as Record<string, unknown>,
            fileAttachmentsFromHook: fileAttachmentsFromHook,
          })

          // Jackson UniDataTableFieldsDeserializer требует у каждого поля узлы type/value
          const cleanGetDataFields: Record<string, any> = {}
          for (const key of Object.keys(collectedData)) {
            cleanGetDataFields[key] = toUniDataField(collectedData[key])
          }

          return createBaseUniDataResponse(cleanGetDataFields)
        }

        case 'getCellModel': {
          const tableName = getTableName()
          if (!tableName) return { fields: { fields: {} } }

          const rowIndex = Number(args[0]) || 0
          const columnName = args[1] != null ? String(args[1]) : ''

          const rawCols = (elements.value[tableName] as any)?.columns?.column
          const colsArr: any[] = rawCols ? (Array.isArray(rawCols) ? rawCols : [rawCols]) : []
          const colConfig = colsArr.find((c: any) => c?.name?._text === columnName)
          const columnId = colConfig?.id?._text ?? colConfig?._attributes?.id ?? null

          const tableRows: any[] = elementPageFields[tableName]?.value?.rows ?? []
          const row = tableRows[rowIndex] ?? null

          let cellData: any = null

          if (row) {
            const rowData = row?._rowData ?? row
            const rowFields = rowData?.fields ?? row?.fields ?? {}


            if (columnId) {
              const idKey = `${columnId}__listbox`
              const byId = rowFields[idKey]
              if (byId?.value?.rows || byId?.rows) {
                cellData = byId?.value ?? byId
              }
            }
          }

          if (!cellData) return { fields: { fields: {} } }

          const rows: unknown[] = cellData?.rows ?? []

          return {
            fields: {
              fields: {
                '__js_web_response': toUniDataField({
                  rows
                })
              }
            }
          }
        }

        case 'getPaginatorParams': {
          const tableName = getTableName()

          if (tableName) {
            const registeredElement = (elements.value as any)[tableName]
            const paginationParams = registeredElement?.getPaginatorParams()

            return {
              fields: {
                fields: paginationParams
              }
            }
          } else return { fields: { fields: {} } }
        }
        case 'reloadColumnProperties': {
          const tableName = getTableName()
          if (!tableName) return withSocketId()

          const rawArg = args[0]
          const propsTable =
            rawArg instanceof UniDataTable
              ? rawArg
              : rawArg && typeof rawArg === 'object' && Array.isArray((rawArg as any).rows)
                ? new UniDataTable(rawArg as any)
                : null
          if (!propsTable?.rows?.length) return withSocketId()

          const el = elements.value[tableName] as any
          const colsWrap = el?.columns
          if (!colsWrap?.column) return withSocketId()

          const arr = Array.isArray(colsWrap.column) ? colsWrap.column : [colsWrap.column]

          const readRowField = (row: any, keys: string[]): string | undefined => {
            if (row && typeof row.getFieldAsString === 'function') {
              for (const key of keys) {
                const v = row.getFieldAsString(key, '')
                if (v !== '') return v
              }
            }
            const fields = row?.fields
            if (fields && typeof fields === 'object') {
              for (const key of keys) {
                const cell = (fields as any)[key]
                if (cell == null) continue
                if (typeof cell === 'object' && 'value' in cell) {
                  const v = (cell as any).value
                  if (v != null && String(v) !== '') return String(v)
                } else if (String(cell) !== '') {
                  return String(cell)
                }
              }
            }
            return undefined
          }

          const parseVisible = (raw: string) => {
            const s = raw.trim().toLowerCase()
            if (s === 'false' || s === '0' || s === 'no') return false
            if (s === 'true' || s === '1' || s === 'yes') return true
            return true
          }

          for (const row of propsTable.rows as any[]) {
            const colName = readRowField(row, ['name', 'NAME', 'Name'])
            if (!colName) continue

            const visibleStr = readRowField(row, ['Visible', 'VISIBLE', 'visible'])
            if (visibleStr === undefined) continue

            const target = arr.find((c: any) => c?.name?._text === colName)
            if (target) {
              if (!target.defVisible) target.defVisible = {}
              target.defVisible._text = parseVisible(visibleStr) ? '1' : '0'
            }
          }

          return withSocketId()
        }

        case 'getColumnProperties': {
          const tableName = getTableName()

          if (!tableName) return { fields: { fields: {} } }

          const rawColumns = (elements.value[tableName] as any)?.columns?.column
          if (!rawColumns) return { fields: { fields: {} } }

          const arr = Array.isArray(rawColumns) ? rawColumns : [rawColumns]
          const rows = arr
            .filter((c: any) => c != null && c.name?._text)
            .map((c: any) => ({
              fields: {
                name: toUniDataField(c.name._text),
                description: toUniDataField(c.description?._text ?? c.name._text),
                Visible: toUniDataField(c.defVisible?._text === '0' ? '0' : '1'),
                order: toUniDataField(Number(c.order?._text) || 900),
                width: toUniDataField(Number(c.width?._text) || 1),
              }
            }))
            .sort((a: any, b: any) => (a.fields.order.value ?? 900) - (b.fields.order.value ?? 900))

          return {
            fields: {
              fields: {
                '__js_web_response': toUniDataField({
                  rows,
                })
              }
            }
          }
        }

        case 'getColumnInfo': {
          const tableName = getTableName()

          if (!tableName) return { fields: { fields: {} } }

          const rawColumns = (elements.value[tableName] as any)?.columns?.column
          if (!rawColumns) return { fields: { fields: {} } }

          const arr = Array.isArray(rawColumns) ? rawColumns : [rawColumns]
          const rows = arr
            .filter((c: any) => c != null && c.name?._text)
            .map((c: any) => ({
              fields: {
                id: toUniDataField(c.id?._text ?? ''),
                name: toUniDataField(c.name._text),
                description: toUniDataField(c.description?._text ?? c.name._text),
                type: toUniDataField(c.type?._text ?? 'Text'),
                is_visible: toUniDataField(c.defVisible?._text === '0' ? '0' : '1'),
                has_format: toUniDataField(c.format?._text ? '1' : '0'),
              }
            }))

          return {
            fields: {
              fields: {
                '__js_web_response': toUniDataField({
                  rows,
                })
              }
            }
          }
        }
        case 'getSelectedPrimaryKey': {
          const tableName = getTableName()
          if (!tableName) return { fields: { fields: {} } }

          const registeredElement = (elements.value as any)[tableName]
          const rawColumns = registeredElement?.columns?.column
          if (!rawColumns) return { fields: { fields: {} } }
          const selection = getTableSelection(
            registeredElement?.formId,
            registeredElement?.panelId,
            registeredElement?.elementId,
          )

          return buildSelectedPrimaryKeyWsResponse({
            rawColumns,
            selectionRow: selection?.rows?.[0] ?? null,
          })
        }
        case 'getPrimaryKeyColumns': {
          const tableName = getTableName()
          if (!tableName) return { fields: { fields: {} } }

          const rawCols = (elements.value[tableName] as any)?.columns?.column
          return resolvePrimaryKeyColumnNames(rawCols)
        }

        default:
          throwUnknownJsRequestHandler(request, 'ELEMENT method')
      }
    }
  }

  if (normalizedClassName === 'FilePrintHelper' || normalizedClassName.includes('FilePrintHelper')) {
    switch (methodName) {
      case 'print': {
        const base64Data = args[0]
        const fileName = args[1] != null ? String(args[1]) : ''
        if (!base64Data || typeof base64Data !== 'string') {
          throwInvalidJsRequestPayload(request, 'FilePrintHelper.print: missing or invalid base64 data', {
            fileName,
            base64Type: typeof base64Data,
          })
        }
        const { printFileFromBase64 } = await import('@/services/print/filePrint.service')
        printFileFromBase64(base64Data, fileName)
        return withSocketId()
      }
      default:
        throwUnknownJsRequestHandler(request, 'FilePrintHelper method')
    }
  }

  throwUnknownJsRequestHandler(request, 'className')
  } finally {
    endJsRequestMeasure()
  }
  })
})

const addFormEventListener = (listener: {
  level: string
  id: number | string
  event_name: string
  function: string
  web_client_exec: boolean
  to_remove: boolean
}) => {
  const slotId = String(getCurrentPageSlotId() ?? '').trim()
  if (!slotId) return
  const page = appStore.pagesData[slotId]
  if (!page || typeof page !== 'object') return
  if (!page.jsResult || typeof page.jsResult !== 'object') {
    page.jsResult = {}
  }
  const list = page.jsResult.event_listeners
  if (!Array.isArray(list)) {
    page.jsResult.event_listeners = []
  }
  page.jsResult.event_listeners.push(listener)
}

const closePlatformCoresOnUnload = () => {
  appStore.closeAllPlatformCoresOnUnload()
}

const getPageReloadGuardContext = () => {
  const openTabIds = resolveOpenTabIdsForReloadGuard({
    tabIdsOrdered: documentTabsStore.tabIdsOrdered,
    currentPageId: appStore.currentPageId,
    routeName: route.name,
  })

  return {
    openTabIds,
    dialogIds: appStore.dialogIds,
    routeName: route.name,
  }
}

const { attachPageReloadGuard, detachPageReloadGuard } = usePageReloadGuard({
  shouldConfirmReload: () => shouldConfirmPageReload(getPageReloadGuardContext()),
  getHasOpenForms: () => hasOpenPlatformForms(getPageReloadGuardContext()),
  onBeforeUnload: closePlatformCoresOnUnload,
})

provide(SkipNextPageLeaveConfirmKey, skipNextPageLeaveConfirm)

onMounted(() => {
  registerFormShellVisibilityCleanup((options) => {
    const formIds = buildShellCleanupFormIdSet(options)
    const liveByKey = elementsByKey.value as Record<string, ShellVisibilityRegistryEntry>
    const { elementIds, deferredNameKeys } = collectShellVisibilityCleanupTargets(
      elements.value as Record<string, ShellVisibilityRegistryEntry>,
      options,
      liveByKey,
    )

    if (formIds.size === 0) {
      elementShellVisibleByElementId.value = {}
      deferredElementShellByName.value = {}
      deferredElementShellByElementId.value = {}
      elementVisibilityHooksByElementId.clear()
      panelsVisibilityRegistry.value = panelsVisibilityRegistryAfterShellCleanup(
        panelsVisibilityRegistry.value,
      )
      forgetIndexedShellElementIds(options)
      return
    }

    const pruneShellMaps = () => {
      elementShellVisibleByElementId.value = pruneRecordByElementIds(
        elementShellVisibleByElementId.value,
        elementIds,
      )
      deferredElementShellByElementId.value = pruneRecordByElementIds(
        deferredElementShellByElementId.value,
        elementIds,
      )
      deferredElementShellByName.value = pruneRecordByKeys(
        deferredElementShellByName.value,
        deferredNameKeys,
      )
      pruneVisibilityHooksByElementIds(elementVisibilityHooksByElementId, elementIds)

      panelsVisibilityRegistry.value = panelsVisibilityRegistryAfterShellCleanup(
        panelsVisibilityRegistry.value,
      )
    }

    // SAME_WINDOW form replace: drop stale shell only (slot stays; registry remounts).
    if (options?.mapsOnly) {
      pruneShellMaps()
      forgetIndexedShellElementIds(options)
      return
    }

    for (const fid of formIds) {
      delete forms.value[fid]
    }
    let removedDialogElements = false
    for (const name of Object.keys(elements.value)) {
      const entry = elements.value[name]
      const entryFormId = String(entry?.formId ?? '').trim()
      const entrySlot = String(entry?.pageDataSlot ?? '').trim()
      if ((entryFormId && formIds.has(entryFormId)) || (entrySlot && formIds.has(entrySlot))) {
        delete elements.value[name]
        removedDialogElements = true
      }
    }
    for (const key of Object.keys(elementsByKey.value)) {
      const entry = elementsByKey.value[key]
      const entryFormId = String(entry?.formId ?? '').trim()
      const entrySlot = String(entry?.pageDataSlot ?? '').trim()
      if ((entryFormId && formIds.has(entryFormId)) || (entrySlot && formIds.has(entrySlot))) {
        delete elementsByKey.value[key]
      }
    }
    for (const name of Object.keys(overlaysRegistry.value)) {
      const entry = overlaysRegistry.value[name]
      if (formIds.has(String(entry?.formId ?? '').trim())) {
        delete overlaysRegistry.value[name]
      }
    }
    if (removedDialogElements) {
      bumpElementsRegistryRevision()
    }
    en.value?._resetData()

    pruneShellMaps()
    forgetIndexedShellElementIds(options)
  })

  attachHotKeys()
  attachPageReloadGuard()
  en.value = new En({
    controls: {
      getForm,
      getPageStoreField,
      refreshPageDataSnapshot,
      collectLiveFieldOverrides: collectLiveFieldOverridesForForm,
      setValueToTempData: (value: any) => { /* TODO: Implement */ },
      verifyForm,
      runActionByName,
      runFormEvent,
      invokeBusEvent,
      reloadElement: reloadElementViaControls,
      reloadPanel: reloadPanelViaControls,
      // JSContext.reloadForm → POST /platform/reload/form + refill. STACK (actionId=-1) only for Window.doStackAction.
      reloadForm: reloadFormViaControls,
      reloadFormStack: reloadFormViaStackAction,
      resolveElementTableSwingMethod,
      refillElement: (elementName: string, fieldData: any, formId?: string) => {
        refillElementViaControls(elementName, fieldData, formId)
      },
      queuePendingOverlayRefill,
      getOverlayDuplicatedFieldNames: overlayDuplicatedFieldNamesForForm,
      getElementsByKey: () => elementsByKey.value,
      openSuggesterElement: (elementName: string, fid?: string) => {
        openSuggesterElement(elementName, fid)
      },
      empty: emptyElement,
      setVisible: setVisibleRecursive,
      setVisibleByLevel,
      setFormError,
      getOverlays: () => overlaysRegistry.value,
      addFormEventListener,
      /** Прямой вызов runAction для элемента (onChange/onSelectionChanged) из скрипта onLoad — обход Vue Proxy. */
      runElementEvent: (ctx: {
        elementId: number | string;
        eventName: string;
        data?: any;
        formId?: string;
        panelId?: string;
        name?: string
      }) => {
        const selectedData = resolveInvokeEventSelectedData(ctx.data)

        const formId = String(ctx.formId ?? '');
        const panelId = String(ctx.panelId ?? '');
        const idNum = typeof ctx.elementId === 'number' ? ctx.elementId : parseInt(String(ctx.elementId), 10);

        const registered = Number.isFinite(idNum)
          ? (Object.values(elements.value).find(
            (e: any) => Number(e?.elementId) === idNum || Number(e?.ctx?.id) === idNum,
          ) as Record<string, any> | undefined)
          : undefined

        syncEnContextForScript({
          formId,
          panelId,
          pageSlotId: registered?.pageDataSlot || getCurrentPageSlotId(),
        })

        const eventAction = formId
          ? getEventActionForElement(formId, ctx.elementId, ctx.eventName)
          : null;
        if (eventAction) {
          const promise = runAction({
            data: eventAction,
            level: 'element',
            eventName: ctx.eventName,
            formId: formId,
            panelId: panelId,
            elementId: String(ctx.elementId),
            selectedData,
            pageSlotId: registered?.pageDataSlot || getCurrentPageSlotId(),
          });
          trackPendingElementEvent(promise)
          return promise
        }

        if (ctx.eventName === 'onChange' || ctx.eventName === 'onSelectionChanged') {
          const inline =
            pickElementEventScript(registered, ctx.eventName) ??
            pickElementEventScript(registered?.ctx?.element, ctx.eventName);
          if (inline) {
            const elementData =
              registered?.ctx?.element ??
              {
                _attributes: { id: ctx.elementId },
                name: { _text: ctx.name },
                [ctx.eventName === 'onSelectionChanged' ? 'onSelectionChanged' : 'onChange']: {
                  _text: inline,
                },
              }
            const promise = runAction({
              data: elementData,
              level: 'element',
              eventName: ctx.eventName,
              formId,
              panelId,
              elementId: String(ctx.elementId),
              selectedData,
              pageSlotId: registered?.pageDataSlot || getCurrentPageSlotId(),
            })
            trackPendingElementEvent(promise)
            return promise
          }
        }

        const promise = runAction({
          level: 'element',
          eventName: ctx.eventName,
          formId: formId,
          panelId: panelId,
          elementId: String(ctx.elementId),
          data: {
            _attributes: { id: ctx.elementId },
            name: { _text: ctx.name },
          },
          selectedData,
          pageSlotId: registered?.pageDataSlot || getCurrentPageSlotId(),
        });
        trackPendingElementEvent(promise)
        return promise
      },
      trackPendingElementEvent,
    },
    elements: elements.value,
    getElements: () => elements.value,
    actions: actions.value,
    pageData: {},
  });

  en.value.registerClientFunction('reloadElement', (elementName: string) => {
    return reloadElementViaControls(elementName, {})
  });

  en.value.registerClientFunction('getFormData', (formId?: string, panelId?: string) => {
    return getForm({ formId: formId || getCurrentRegistryFormId(), panelId: panelId || getDefaultPanelId() })
  });

  en.value.registerClientFunction('setValue', (fieldName: string, value: any, formId?: string, panelId?: string) => {
    setValue({
      value: { [fieldName]: value },
      formId: formId || getCurrentRegistryFormId(),
      panelId: panelId || getDefaultPanelId(),
      elementId: fieldName
    })
  });

  en.value.registerClientFunction('setVisible', (name: string, isVisible: boolean) => {
    setVisibleRecursive(name, isVisible)
  });

  en.value.registerClientFunction('setEditable', (name: string, isEditable: boolean) => {
    setEditableRecursive(name, isEditable)
  });

  // LocalProperties: web analog of Packages.ru.esoft.wf.client.constants.LocalProperties
  if (typeof window !== 'undefined') {
    (window as any).LocalProperties = localPropertiesService.getApi();
  }
  en.value.registerClientFunction('getLocalProperties', (type: string) => localPropertiesService.get(type));

  // imports из appStore (как в createEnContext), чтобы $('initKey'), $('signFile') и др. работали в скриптах (например IN_UNIDATA)
  const registerStoreImports = () => {
    const imports = appStore?.imports || {}
    Object.entries(imports).forEach(([name, item]: [string, any]) => {
      if (item && typeof item.func === 'function') {
        en.value!.registerClientFunction(name, item.func)
      }
    })
  }
  registerStoreImports()
  // Реактивно регистрируем импорты, когда они появляются (getConfig загружает их позже монтирования)
  watch(() => appStore.imports, registerStoreImports, { deep: true })

  // TODO: скопированы глобалы сюда, но нужно будет убрать дублирование с импортами при создании контекста в action
  ;(window as any).openForCertificateSelection = () => useSignModal().openForCertificateSelection()
  ;(window as any).openForFileSelection = (files: any) =>
    (useFileSelectModal() as any).openForFileSelection(files)
  ;(window as any).createDetachedSignature = createDetachedSignature
  ;(window as any).createHash = createHash
  ;(window as any).UniData = UniData
  ;(window as any).UniDataTable = UniDataTable
  assignClientImportWindowGlobals(window)

  if (configStore.getCtxOfLastAction) {
    setTimeout(() => onAction({ getContext: configStore.getCtxOfLastAction }));
  }

  disposeOfficeEventRouter = initOfficeEventRouter({
    findElementByObjectId: findElementForOfficeRouter,
  })

  disposeFormEventRouter = initFormEventRouter({
    reloadForm: async ({ pageDataSlot, sessionId, formId }) => {
      const slot = String(pageDataSlot ?? '').trim()
      const slotPage = slot ? (appStore.pagesData as Record<string, any>)[slot] : null
      const resolvedFormId =
        String(formId ?? '').trim() ||
        (slotPage ? resolveElementRegistryFormId(slotPage, slot) : '') ||
        getCurrentRegistryFormId() ||
        slot
      try {
        // Swing: SwingMessagesOverlay → doStackAction(-1), not /reload/form
        const result = await reloadFormViaStackAction({
          pageDataSlot: slot || undefined,
          formId: resolvedFormId || undefined,
          sessionId,
        })
        if (!result) {
          console.warn('[formEventRouter] reload_form (stack) produced no result', {
            pageDataSlot: slot,
            formId: resolvedFormId,
            sessionId,
          })
        }
      } catch (error) {
        console.error('[formEventRouter] reload_form failed', error)
      }
    },
    closeForm: async ({ pageDataSlot }) => {
      const slot = String(pageDataSlot ?? '').trim()
      if (!slot) return
      const isDialog =
        appStore.dialogIds?.some((id: string) => String(id) === slot) ||
        Boolean((appStore.dialogs as Record<string, unknown> | undefined)?.[slot])
      if (isDialog) {
        await appStore.removeDialog(slot, {
          router,
          routePageId: String(route.params?.pageId ?? ''),
        })
        return
      }
      appStore.removePage(slot)
    },
  })

  if (isPlatformTestApiEnabled()) {
    installPlatformTestApi({
      getActiveSlot: getCurrentPageSlotId,
      getElements: () => elements.value,
      getForms: () => forms.value,
      getPagesData: () => appStore.pagesData as Record<string, any>,
      setValue,
      refillElement: refillElementViaControls,
    })
  }
});

onUnmounted(() => {
  if (isPlatformTestApiEnabled()) {
    uninstallPlatformTestApi()
  }
  disposeOfficeEventRouter?.()
  disposeOfficeEventRouter = null
  disposeFormEventRouter?.()
  disposeFormEventRouter = null
  detachHotKeys()
  detachPageReloadGuard()
  if (unsubscribeServerEvents) {
    unsubscribeServerEvents()
  }
  serverEventScripts.forEach((_script, formId) => {
    serverEventRegistry.clearForm(formId)
  })
  serverEventScripts.clear()
})
</script>
